import ClientPricingCategoryGroups from '../components/clientPricing/ClientPricingCategoryGroups';
import ClientPricingMarginGroups from '../components/clientPricing/ClientPricingMarginGroups';
import BulkPricingPendingSection from '../components/bulkPricing/BulkPricingPendingSection';
import { runPartialPricingBulk } from '../api/bulkPricingApi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import {
    BARCODE_PREFIX_MAX_LENGTH,
    BARCODE_MODE_OPTIONS,
    DEFAULT_BARCODE_PREFIX_MAPPINGS,
    getBarcodePrefixMappings,
    isReservedBarcodePrefix,
    saveBarcodePrefixMappings,
    sanitizeBarcodePrefixInput,
} from '../utils/barcodeSettings';
import {
    DEFAULT_PRINT_MODE,
    PRINT_MODE_DESCRIPTIONS,
    PRINT_MODE_LABELS,
    PRINT_MODE_OPTIONS,
    getPrintMode,
    savePrintMode,
} from '../utils/printSettings';
import {
    getBarcodePricingRule,
    getBranchRequestConfig,
    getCategories,
    getCategoryBarcodes,
    getPrintReceiptRedirectDefaults,
    getPosSoldOutDisplaySettings,
    getPriceRecommendationSettings,
    getPurchaseOrderNotificationSettings,
    getPurchaseOrderReasonsSettings,
    getProductFormats,
    createProductFormat,
    updateProductFormat,
    regenerateCategoryBarcodes,
    updateBarcodePricingRule,
    updateBranchRequestConfig,
    updatePosSoldOutDisplaySettings,
    updatePriceRecommendationSettings,
    updatePurchaseOrderNotificationSettings,
    updatePurchaseOrderReasonsSettings,
    getBranches,
    updateBranch,
    getTaxes,
    createTax,
    updateTax,
    getBranchTaxes,
    updateBranchTaxes,
    getClientPricingCategories,
    getClientPricingApplicabilityOptions,
    getClientPricingSettings,
    getClientPricingMarginConversionIssues,
    createClientPricingCategory,
    updateClientPricingCategory,
    deleteClientPricingCategory,
    getClientPricingDiscountCatalog,
    updateClientPricingDiscount,
    bulkUpdateClientPricingDiscounts,
    updateClientPricingSettings,
    retryClientPricingMarginConversions,
    updateCategoryAvailability,
    getProducts,
    getProductUnitEquivalences,
    updatePriceListFormatSettings,
    getRoles,
    rebuildInventoryProjection,
    sendTestWebPushNotification,
    updateVarietyInventorySettings,
    updateVarietyFormat,
    updateVarietyPriceEquivalence,
    updatePrintReceiptRedirectDefaults,
} from '../api';
import BranchUsersPage from './BranchUsersPage';
import BranchSettingsPanel from '../components/branchConfiguration/BranchSettingsPanel';
import ProductList from '../components/ProductList';
import PosSlotSettingsPanel from '../components/PosSlotSettingsPanel';
import AdvancedPricingSettingsPanel from '../components/AdvancedPricingSettingsPanel';
import SaleRoundingSettingsPanel from '../components/SaleRoundingSettingsPanel';
import ComplexPriceRulesPanel from '../components/ComplexPriceRulesPanel';
import CategoriesAdminPage from './CategoriesAdminPage';
import useCurrentUser from '../hooks/useCurrentUser';
import useMyPreferences from '../hooks/useMyPreferences';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import useProductEvents from '../hooks/useProductEvents';
import useDebouncedCallback from '../hooks/useDebouncedCallback';
import { hasAnyPermission, hasPermission, isBusinessOwnerUser } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import HorizontalPillTabs from '../components/HorizontalPillTabs';
import InfoTooltip from '../components/InfoTooltip';
import ClientPricingDiscountMatrix from '../components/clientPricing/ClientPricingDiscountMatrix';
import PendingClientPricingCosts from '../components/clientPricing/PendingClientPricingCosts';
import ClientPricingCategoryModal from '../components/ClientPricingCategoryModal';
import CompaniesSettingsPanel from '../components/CompaniesSettingsPanel';
import {
    DEFAULT_PRINT_RECEIPT_REDIRECT,
    DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS,
    DEFAULT_PURCHASE_ORDER_REVIEW,
    normalizePurchaseOrderReview,
    normalizePrintReceiptRedirectDefaults,
    normalizePrintReceiptRedirect,
} from '../utils/userPreferences';
import {
    DEFAULT_POS_SEARCH_SUGGESTION_VISIBILITY,
    POS_SOLD_OUT_DISPLAY_OPTIONS,
    POS_SOLD_OUT_MODE_NORMAL,
    normalizePosSearchSuggestionVisibility,
    normalizePosSoldOutMode,
} from '../utils/posSoldOutDisplay';
import {
    disableBrowserPushNotifications,
    enableBrowserPushNotifications,
    loadBrowserPushState,
} from '../utils/webPushNotifications';
import {
    PRODUCT_SEARCH_MODE_STRICT,
    PRODUCT_SEARCH_MODE_SUGGESTIVE,
    normalizeProductSearchMode,
} from '../utils/productSearch';
import {
    AUTO_PRICE_GLOBAL_RULE_HELP,
    AUTO_PRICE_MODE_HELP,
    AUTO_PRICE_THRESHOLD_HELP,
} from '../utils/autoPriceRuleHelpText';
import { buildProductFormatOptions } from '../utils/clientType';
import {
    CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
    CLIENT_PRICING_MARGIN_DISPLAY_RETAINED,
    CLIENT_PRICING_MODE_COST_PLUS,
    CLIENT_PRICING_MODE_DISCOUNT,
    CLIENT_PRICING_MODE_MARGIN_DISCOUNT,
    CLIENT_PRICING_RULE_FIXED,
    CLIENT_PRICING_RULE_MARGIN,
    isClientPricingMarginRule,
} from '../utils/clientPricingMatrix';
import {
    buildClientPricingCategorySearch,
    getClientPricingCategoryIdFromSearch,
    resolveClientPricingCategorySelection,
} from '../utils/clientPricingCategoryQuery';
import {
    buildSettingsBranchSearch,
    buildSettingsPath,
    getSettingsBranchIdFromSearch,
    getVisibleSettingsTopics,
    resolveSettingsBranchSelection,
    resolveSettingsDestination,
    SETTINGS_SECTION_SCOPES,
} from '../config/settingsNavigation';

const MODE_LABELS = {
    weight: 'Peso',
    amount: 'Cantidad',
    price: 'Precio',
};

const CLIENT_TYPE_LABELS = {
    detalle: 'Detalle',
    mayorista: 'Mayorista',
};

const PRICE_SYNC_TYPE_LABELS = {
    percent: 'Porcentaje',
    fixed: 'Monto fijo',
};

const DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES = ['AdminSucursal', 'Vendedor'];

const normalizeRoleNameList = (value, fallback = DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES) => {
    const source = Array.isArray(value) ? value : fallback;
    return Array.from(new Set(source.map((entry) => String(entry || '').trim()).filter(Boolean)));
};

const buildPurchaseOrderNotificationRoleOptions = (roles, selectedRoleNames) => {
    const selectedNames = normalizeRoleNameList(selectedRoleNames, DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES);
    const optionsByName = new Map();
    selectedNames.forEach((name) => {
        optionsByName.set(name, {
            id: `configured-${name}`,
            name,
            description: '',
            configuredOnly: true,
        });
    });
    (Array.isArray(roles) ? roles : []).forEach((role) => {
        const name = String(role?.name || '').trim();
        if (!name) return;
        const permissions = role.Permissions || role.permissions || [];
        const canReadPurchaseOrders = permissions.some((permission) => permission?.key === 'purchase_order.read');
        if (!canReadPurchaseOrders && !optionsByName.has(name)) return;
        optionsByName.set(name, {
            id: role.id || name,
            name,
            description: role.description || '',
            configuredOnly: !canReadPurchaseOrders,
        });
    });
    return Array.from(optionsByName.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const getPurchaseOrderTestNotificationMessage = (reason) => {
    if (reason === 'no-subscriptions') {
        return 'Este usuario no tiene navegadores suscritos. Activa este navegador primero.';
    }
    if (reason === 'web-push-not-configured') {
        return 'El servidor no tiene claves VAPID configuradas.';
    }
    return 'No se pudo enviar la notificacion de prueba.';
};

const formatCategoryBarcodeDate = (value) => {
    if (!value) return 'Sin fecha';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Sin fecha' : date.toLocaleString('es-CL');
};

const emptyPriceRecommendationSettings = () => ({
    autoUpdatePrices: true,
    rounding: {
        mode: 'hundreds_threshold',
        unit: 100,
        threshold: '20',
    },
});

const PRICE_RECOMMENDATION_ROUNDING_OPTIONS = [
    {
        unit: 100,
        mode: 'hundreds_threshold',
        label: 'Centenas',
        defaultThreshold: '20',
        maxThreshold: 99,
    },
    {
        unit: 10,
        mode: 'tens_threshold',
        label: 'Decenas',
        defaultThreshold: '2',
        maxThreshold: 9,
    },
];

const getPriceRecommendationRoundingOption = (rounding = {}) => {
    const unit = Number(rounding.unit);
    if (unit === 10) return PRICE_RECOMMENDATION_ROUNDING_OPTIONS[1];
    if (unit === 100) return PRICE_RECOMMENDATION_ROUNDING_OPTIONS[0];
    return String(rounding.mode || '').trim().toLowerCase() === 'tens_threshold'
        ? PRICE_RECOMMENDATION_ROUNDING_OPTIONS[1]
        : PRICE_RECOMMENDATION_ROUNDING_OPTIONS[0];
};

const PRODUCT_SEARCH_MODE_OPTIONS = [
    {
        value: PRODUCT_SEARCH_MODE_SUGGESTIVE,
        label: 'Busqueda sugerida',
        description: 'Muestra coincidencias exactas primero y luego sugeridos por tokens contenidos.',
    },
    {
        value: PRODUCT_SEARCH_MODE_STRICT,
        label: 'Busqueda estricta',
        description: 'Solo muestra productos que cumplen la busqueda exacta por palabras.',
    },
];

const PURCHASE_ORDER_REASON_GROUPS = [
    { id: 'reduce', label: 'Reducir cantidad' },
    { id: 'remove', label: 'Eliminar item' },
    { id: 'increase', label: 'Aumentar cantidad' },
];

const normalizeProductFormatPositions = (formats) => (
    (Array.isArray(formats) ? formats : [])
        .slice()
        .sort((a, b) => {
            const orderA = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
            const orderB = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
            if (orderA !== orderB) return orderA - orderB;
            const nameCompare = String(a?.name || '').localeCompare(String(b?.name || ''));
            if (nameCompare !== 0) return nameCompare;
            return Number(a?.id || 0) - Number(b?.id || 0);
        })
        .map((format, index) => ({
            ...format,
            sortOrder: index + 1,
        }))
);

const DIRECT_INVENTORY_UNIT_KEYS = new Set(['kg', 'kilo', 'kilogramo', 'granel', 'bin', 'unidad', 'un', 'u', 'unit']);

const toInventoryUnitKey = (value) => String(value || '').trim().toLowerCase();

const isDirectInventoryEquivalence = (baseUnit, metricUnit, { categoryIsResaleOnly = false } = {}) => {
    const baseKey = toInventoryUnitKey(baseUnit);
    const unitKey = toInventoryUnitKey(metricUnit);
    if (!baseKey || !unitKey) return false;
    if (baseKey === unitKey) return true;
    if (baseKey === 'kg' && ['kg', 'kilo', 'kilogramo', 'granel', 'bin'].includes(unitKey)) return true;
    if (baseKey === 'unidad' && ['unidad', 'un', 'u', 'unit'].includes(unitKey)) return true;
    if (baseKey === 'unidad' && categoryIsResaleOnly) return true;
    return DIRECT_INVENTORY_UNIT_KEYS.has(baseKey) && baseKey === unitKey;
};

const parsePositiveEquivalence = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const formatEquivalenceNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed)
        ? parsed.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 6 })
        : '-';
};

const normalizeEquivalencePayload = (payload) => Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
        ? payload
        : [];

const FormatEquivalenceSettingsPanel = ({
    branchId,
    canReadProducts,
    canUpdateProduct,
    confirm,
}) => {
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingKey, setSavingKey] = useState('');
    const [rebuilding, setRebuilding] = useState(false);
    const [error, setError] = useState('');
    const [savedMessage, setSavedMessage] = useState('');

    const updateVarietyInState = useCallback((varietyId, patch) => {
        setProducts((prev) => prev.map((product) => ({
            ...product,
            Varieties: (product.Varieties || []).map((variety) => (
                String(variety.id) === String(varietyId) ? { ...variety, ...patch } : variety
            )),
        })));
    }, []);

    const updatePriceInState = useCallback((varietyId, priceId, patch) => {
        setProducts((prev) => prev.map((product) => ({
            ...product,
            Varieties: (product.Varieties || []).map((variety) => {
                if (String(variety.id) !== String(varietyId)) return variety;
                return {
                    ...variety,
                    VarietyPrices: (variety.VarietyPrices || []).map((price) => (
                        String(price.id) === String(priceId) ? { ...price, ...patch } : price
                    )),
                };
            }),
        })));
    }, []);

    const loadEquivalences = useCallback(async () => {
        if (!branchId || !canReadProducts) return;
        setLoading(true);
        setError('');
        try {
            const response = await getProductUnitEquivalences(branchId);
            setProducts(normalizeEquivalencePayload(response));
            setSavedMessage('');
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudieron cargar las equivalencias.');
        } finally {
            setLoading(false);
        }
    }, [branchId, canReadProducts]);

    useEffect(() => {
        loadEquivalences();
    }, [loadEquivalences]);

    const productsWithLocalMissing = useMemo(() => products.map((product) => ({
        ...product,
        Varieties: (product.Varieties || []).map((variety) => {
            const baseUnit = variety.inventoryBaseUnit || 'kg';
            const categoryIsResaleOnly = Boolean(product.categoryIsResaleOnly);
            const prices = (variety.VarietyPrices || []).map((price) => {
                const factor = parsePositiveEquivalence(price.baseQuantityPerUnit);
                return {
                    ...price,
                    missingEquivalence: !factor && !isDirectInventoryEquivalence(baseUnit, price.metricUnit, {
                        categoryIsResaleOnly,
                    }),
                };
            });
            return {
                ...variety,
                VarietyPrices: prices,
                missingEquivalenceCount: prices.filter((price) => price.missingEquivalence).length,
            };
        }),
    })), [products]);

    const missingEquivalenceCount = useMemo(() => productsWithLocalMissing.reduce((sum, product) => (
        sum + (product.Varieties || []).reduce((varietySum, variety) => (
            varietySum + Number(variety.missingEquivalenceCount || 0)
        ), 0)
    ), 0), [productsWithLocalMissing]);

    const filteredProducts = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return productsWithLocalMissing;
        return productsWithLocalMissing
            .map((product) => {
                const varieties = (product.Varieties || []).filter((variety) => {
                    const haystack = [
                        product.productName,
                        product.categoryName,
                        variety.varietyName,
                        variety.inventoryBaseUnit,
                        ...(variety.VarietyPrices || []).flatMap((price) => [
                            price.metricUnit,
                            price.productFormatName,
                            price.productFormatCode,
                            price.clientType,
                        ]),
                    ].filter(Boolean).join(' ').toLowerCase();
                    return haystack.includes(term);
                });
                return varieties.length ? { ...product, Varieties: varieties } : null;
            })
            .filter(Boolean);
    }, [productsWithLocalMissing, search]);

    const handleSaveBaseUnit = async (variety) => {
        if (!branchId || !canUpdateProduct || !variety?.id) return;
        const baseUnit = String(variety.inventoryBaseUnit || '').trim();
        if (!baseUnit) {
            setError('La unidad base es requerida.');
            return;
        }
        const key = `variety:${variety.id}`;
        setSavingKey(key);
        setError('');
        try {
            const response = await updateVarietyInventorySettings(branchId, variety.id, {
                inventoryBaseUnit: baseUnit,
            });
            updateVarietyInState(variety.id, { inventoryBaseUnit: response?.inventoryBaseUnit || baseUnit });
            setSavedMessage('Unidad base guardada.');
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudo guardar la unidad base.');
        } finally {
            setSavingKey('');
        }
    };

    const handleSaveEquivalence = async (variety, price) => {
        if (!branchId || !canUpdateProduct || !variety?.id || !price?.id) return;
        const rawValue = price.baseQuantityPerUnit;
        const payloadValue = rawValue === '' || rawValue === null || rawValue === undefined
            ? null
            : Number(rawValue);
        if (payloadValue !== null && (!Number.isFinite(payloadValue) || payloadValue <= 0)) {
            setError('La equivalencia debe ser mayor a 0.');
            return;
        }
        const key = `price:${price.id}`;
        setSavingKey(key);
        setError('');
        try {
            const response = await updateVarietyPriceEquivalence(branchId, price.id, {
                baseQuantityPerUnit: payloadValue,
            });
            updatePriceInState(variety.id, price.id, {
                baseQuantityPerUnit: response?.baseQuantityPerUnit ?? payloadValue,
            });
            setSavedMessage('Equivalencia guardada.');
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudo guardar la equivalencia.');
        } finally {
            setSavingKey('');
        }
    };

    const handleRebuildInventory = async () => {
        if (!branchId || !canUpdateProduct || rebuilding) return;
        const approved = await confirm({
            title: 'Reconstruir inventario',
            message: 'Se recalcularan los saldos actuales desde las fuentes existentes para esta sucursal.',
            confirmLabel: 'Reconstruir',
            cancelLabel: 'Cancelar',
            tone: 'warning',
        });
        if (!approved) return;
        setRebuilding(true);
        setError('');
        try {
            await rebuildInventoryProjection(branchId);
            setSavedMessage('Inventario reconstruido.');
            await loadEquivalences();
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudo reconstruir el inventario.');
        } finally {
            setRebuilding(false);
        }
    };

    if (!branchId) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
                Selecciona una sucursal para configurar equivalencias.
            </div>
        );
    }

    if (!canReadProducts) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
                No tienes permisos para ver productos.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <datalist id="inventory-base-unit-options">
                <option value="kg" />
                <option value="unidad" />
            </datalist>
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Equivalencias de formatos</h2>
                        <p className="text-sm text-gray-500">
                            Cada variedad usa una unidad base y sus formatos convierten a esa unidad.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        {loading && <InlineLoading label="Cargando" />}
                        <button
                            type="button"
                            onClick={loadEquivalences}
                            disabled={loading || rebuilding}
                            className="h-10 rounded border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                            Actualizar
                        </button>
                        {canUpdateProduct && (
                            <button
                                type="button"
                                onClick={handleRebuildInventory}
                                disabled={loading || rebuilding}
                                className="h-10 rounded bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                            >
                                {rebuilding ? 'Reconstruyendo...' : 'Reconstruir inventario'}
                            </button>
                        )}
                    </div>
                </div>

                <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-center">
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                        placeholder="Buscar producto, variedad o formato"
                    />
                    <div className={`rounded border px-3 py-2 text-sm font-semibold ${
                        missingEquivalenceCount > 0
                            ? 'border-amber-200 bg-amber-50 text-amber-800'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    }`}>
                        {missingEquivalenceCount > 0
                            ? `${missingEquivalenceCount} equivalencias pendientes`
                            : 'Sin equivalencias pendientes'}
                    </div>
                </div>

                {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
                {savedMessage && !error && <p className="mt-3 text-sm text-green-600">{savedMessage}</p>}
            </section>

            <div className="space-y-4">
                {filteredProducts.map((product) => (
                    <section key={product.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-col gap-1 border-b border-gray-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">{product.productName}</h3>
                                <p className="text-xs text-gray-500">{product.categoryName || 'Sin categoria'}</p>
                            </div>
                            {(product.Varieties || []).some((variety) => variety.missingEquivalenceCount > 0) && (
                                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                                    Requiere equivalencias
                                </span>
                            )}
                        </div>

                        <div className="divide-y divide-gray-100">
                            {(product.Varieties || []).map((variety) => (
                                <div key={variety.id} className="py-4">
                                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">
                                                {variety.varietyName || `Variedad ${variety.id}`}
                                            </p>
                                            {variety.missingEquivalenceCount > 0 && (
                                                <p className="mt-1 text-xs font-semibold text-amber-700">
                                                    {variety.missingEquivalenceCount} formato(s) sin equivalencia.
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                                            <label className="text-xs font-semibold uppercase text-gray-600">
                                                Unidad base
                                                {canUpdateProduct ? (
                                                    <input
                                                        type="text"
                                                        value={variety.inventoryBaseUnit || ''}
                                                        onChange={(event) => updateVarietyInState(variety.id, {
                                                            inventoryBaseUnit: event.target.value,
                                                        })}
                                                        list="inventory-base-unit-options"
                                                        className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-sm normal-case sm:w-32"
                                                    />
                                                ) : (
                                                    <span className="mt-1 block text-sm font-normal normal-case text-gray-800">
                                                        {variety.inventoryBaseUnit || '-'}
                                                    </span>
                                                )}
                                            </label>
                                            {canUpdateProduct && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSaveBaseUnit(variety)}
                                                    disabled={savingKey === `variety:${variety.id}`}
                                                    className="h-9 rounded bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                                                >
                                                    {savingKey === `variety:${variety.id}` ? 'Guardando...' : 'Guardar'}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200">
                                        <div className="grid min-w-[780px] grid-cols-[minmax(0,1.3fr)_130px_150px_170px_120px] bg-gray-50 px-3 py-2 text-xs font-semibold uppercase text-gray-500">
                                            <span>Formato</span>
                                            <span>Unidad venta</span>
                                            <span>Equivalencia</span>
                                            <span>Vista previa</span>
                                            <span>Accion</span>
                                        </div>
                                        {(variety.VarietyPrices || []).map((price) => {
                                            const factor = parsePositiveEquivalence(price.baseQuantityPerUnit);
                                            const direct = isDirectInventoryEquivalence(variety.inventoryBaseUnit, price.metricUnit, {
                                                categoryIsResaleOnly: Boolean(product.categoryIsResaleOnly),
                                            });
                                            return (
                                                <div
                                                    key={price.id}
                                                    className={`grid min-w-[780px] grid-cols-[minmax(0,1.3fr)_130px_150px_170px_120px] items-center gap-2 border-t px-3 py-2 text-sm ${
                                                        price.missingEquivalence ? 'border-amber-100 bg-amber-50/60' : 'border-gray-100'
                                                    }`}
                                                >
                                                    <div className="min-w-0">
                                                        <p className="truncate font-semibold text-gray-900">
                                                            {price.productFormatName || price.productFormatCode || price.clientType || `Formato ${price.id}`}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {price.clientType || price.productFormatCode || 'Sin tipo'}
                                                        </p>
                                                    </div>
                                                    <span className="text-gray-700">{price.metricUnit || '-'}</span>
                                                    {canUpdateProduct ? (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.000001"
                                                            value={price.baseQuantityPerUnit ?? ''}
                                                            onChange={(event) => updatePriceInState(variety.id, price.id, {
                                                                baseQuantityPerUnit: event.target.value,
                                                            })}
                                                            className={`h-9 rounded border px-2 text-sm ${
                                                                price.missingEquivalence ? 'border-amber-300' : 'border-gray-300'
                                                            }`}
                                                            placeholder={direct ? '1' : 'Requerido'}
                                                        />
                                                    ) : (
                                                        <span className={price.missingEquivalence ? 'font-semibold text-amber-800' : 'text-gray-700'}>
                                                            {factor ? formatEquivalenceNumber(factor) : direct ? '1' : '-'}
                                                        </span>
                                                    )}
                                                    <span className={`text-xs ${price.missingEquivalence ? 'font-semibold text-amber-800' : 'text-gray-500'}`}>
                                                        {factor
                                                            ? `1 ${price.metricUnit || 'unidad'} = ${formatEquivalenceNumber(factor)} ${variety.inventoryBaseUnit || 'base'}`
                                                            : direct
                                                                ? `1 ${price.metricUnit || 'unidad'} = 1 ${variety.inventoryBaseUnit || 'base'}`
                                                                : 'Falta equivalencia'}
                                                    </span>
                                                    {canUpdateProduct ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleSaveEquivalence(variety, price)}
                                                            disabled={savingKey === `price:${price.id}`}
                                                            className="h-9 rounded border border-blue-200 bg-blue-50 px-3 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                                                        >
                                                            {savingKey === `price:${price.id}` ? 'Guardando...' : 'Guardar'}
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">Solo lectura</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {(variety.VarietyPrices || []).length === 0 && (
                                            <div className="border-t border-gray-100 px-3 py-4 text-sm text-gray-500">
                                                Sin formatos de precio.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
                {!loading && filteredProducts.length === 0 && (
                    <section className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                        No hay productos visibles para este filtro.
                    </section>
                )}
            </div>
        </div>
    );
};

const CLIENT_PRICING_CONVERSION_REASON_LABELS = {
    missing_cost: 'Sin costo de referencia',
    missing_price: 'Sin precio en la sucursal principal',
    missing_weight: 'Falta peso o equivalencia',
    missing_unit_weight: 'Falta peso de unidad',
    non_positive_margin: 'Margen no positivo',
    discount_exceeds_margin: 'El descuento supera el margen',
    invalid_discount: 'Descuento inválido',
};

const getClientPricingConversionReasonLabel = (issue = {}) => (
    issue.message
    || issue.reasonLabel
    || CLIENT_PRICING_CONVERSION_REASON_LABELS[issue.reason]
    || 'No se pudo convertir automáticamente'
);

const SettingsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        branchId: routeBranchId,
        topicId: routeTopicId,
        sectionId: routeSectionId,
    } = useParams();
    // Settings can edit another branch without changing the app-wide selectedBranchId.
    const [activeBranchId, setActiveBranchId] = useState(
        routeBranchId
        || getSettingsBranchIdFromSearch(location.search)
        || localStorage.getItem('selectedBranchId')
        || ''
    );
    const [activeTopic, setActiveTopic] = useState('products');
    const [activeSection, setActiveSection] = useState('catalog-categories');
    const [mappings, setMappings] = useState([]);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [barcodePricingRule, setBarcodePricingRule] = useState({
        mode: 'quantity',
        thresholds: {
            detalle: '',
            mayorista: '',
        },
    });
    const [barcodeRuleLoading, setBarcodeRuleLoading] = useState(false);
    const [barcodeRuleError, setBarcodeRuleError] = useState('');
    const [barcodeRuleSaved, setBarcodeRuleSaved] = useState(false);
    const [productFormats, setProductFormats] = useState([]);
    const [productFormatForm, setProductFormatForm] = useState({ name: '', isActive: true });
    const [productFormatsLoading, setProductFormatsLoading] = useState(false);
    const [productFormatsError, setProductFormatsError] = useState('');
    const [productFormatsSaved, setProductFormatsSaved] = useState(false);
    const productFormatOptions = useMemo(
        () => buildProductFormatOptions(productFormats.filter((format) => format?.isActive !== false)),
        [productFormats]
    );
    const [categoryBarcodes, setCategoryBarcodes] = useState([]);
    const [categoryBarcodeLoading, setCategoryBarcodeLoading] = useState(false);
    const [categoryBarcodeRegenerating, setCategoryBarcodeRegenerating] = useState(false);
    const [categoryBarcodeError, setCategoryBarcodeError] = useState('');
    const [categoryBarcodeSaved, setCategoryBarcodeSaved] = useState(false);
    const [categoryBarcodeInactiveCount, setCategoryBarcodeInactiveCount] = useState(0);
    const [branches, setBranches] = useState([]);
    const [branchLoading, setBranchLoading] = useState(false);
    const [branchListResolved, setBranchListResolved] = useState(false);
    const [branchError, setBranchError] = useState('');
    const [priceSync, setPriceSync] = useState({
        sourceBranchId: '',
        adjustmentType: '',
        adjustmentValue: '',
    });
    const [branchRequestConfig, setBranchRequestConfig] = useState({
        mainBranchId: '',
        resolvedMainBranchId: null,
        defaultPurchaseOrderCategoryIds: [],
        portalProductFormatCodes: ['mayorista'],
    });
    const [branchRequestConfigLoading, setBranchRequestConfigLoading] = useState(false);
    const [branchRequestConfigError, setBranchRequestConfigError] = useState('');
    const [branchRequestConfigSaved, setBranchRequestConfigSaved] = useState(false);
    const [purchaseOrderReasons, setPurchaseOrderReasons] = useState({
        reduce: [],
        remove: [],
        increase: [],
    });
    const [purchaseOrderReasonsLoading, setPurchaseOrderReasonsLoading] = useState(false);
    const [purchaseOrderReasonsSaving, setPurchaseOrderReasonsSaving] = useState(false);
    const [purchaseOrderReasonsError, setPurchaseOrderReasonsError] = useState('');
    const [purchaseOrderReasonsSaved, setPurchaseOrderReasonsSaved] = useState(false);
    const [purchaseOrderNotificationSettings, setPurchaseOrderNotificationSettings] = useState({
        enabled: true,
        recipientRoleNames: DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES,
        respectBranchAssignments: true,
        includeGlobalAdminUsers: true,
    });
    const [purchaseOrderNotificationRoles, setPurchaseOrderNotificationRoles] = useState([]);
    const [purchaseOrderNotificationsLoading, setPurchaseOrderNotificationsLoading] = useState(false);
    const [purchaseOrderNotificationsSaving, setPurchaseOrderNotificationsSaving] = useState(false);
    const [purchaseOrderNotificationsError, setPurchaseOrderNotificationsError] = useState('');
    const [purchaseOrderNotificationsRoleWarning, setPurchaseOrderNotificationsRoleWarning] = useState('');
    const [purchaseOrderNotificationsSaved, setPurchaseOrderNotificationsSaved] = useState(false);
    const [purchaseOrderBrowserPushState, setPurchaseOrderBrowserPushState] = useState({
        loading: false,
        saving: false,
        supported: true,
        configured: false,
        permission: 'default',
        serverSubscription: null,
        error: '',
        message: '',
        testing: false,
    });
    const [purchaseOrderReview, setPurchaseOrderReview] = useState(DEFAULT_PURCHASE_ORDER_REVIEW);
    const [purchaseOrderReviewSaving, setPurchaseOrderReviewSaving] = useState(false);
    const [purchaseOrderReviewError, setPurchaseOrderReviewError] = useState('');
    const [purchaseOrderReviewSaved, setPurchaseOrderReviewSaved] = useState(false);
    const [clientPricingCategories, setClientPricingCategories] = useState([]);
    const [clientPricingGroups, setClientPricingGroups] = useState([]);
    const [clientPricingCategoriesLoading, setClientPricingCategoriesLoading] = useState(false);
    const [clientPricingCategoriesLoaded, setClientPricingCategoriesLoaded] = useState(false);
    const [clientPricingCategoriesError, setClientPricingCategoriesError] = useState('');
    const [clientPricingCategoryDeletingId, setClientPricingCategoryDeletingId] = useState(null);
    const [clientPricingCategoryModalOpen, setClientPricingCategoryModalOpen] = useState(false);
    const [clientPricingCategorySaving, setClientPricingCategorySaving] = useState(false);
    const [clientPricingApplicabilityOptions, setClientPricingApplicabilityOptions] = useState({
        branches: [],
        categories: [],
        productFormats: [],
    });
    const [clientPricingApplicabilityLoading, setClientPricingApplicabilityLoading] = useState(false);
    const [clientPricingApplicabilityError, setClientPricingApplicabilityError] = useState('');
    const [clientPricingEditingId, setClientPricingEditingId] = useState(null);
    const [selectedClientPricingCategoryId, setSelectedClientPricingCategoryId] = useState('');
    const [clientPricingFormatRulesId, setClientPricingFormatRulesId] = useState(null);
    const [clientPricingDiscountRows, setClientPricingDiscountRows] = useState([]);
    const clientPricingCatalogRequestIdRef = useRef(0);
    const [clientPricingDiscountsLoading, setClientPricingDiscountsLoading] = useState(false);
    const [clientPricingDiscountsError, setClientPricingDiscountsError] = useState('');
    const [clientPricingDiscountsSaved, setClientPricingDiscountsSaved] = useState(false);
    const [clientPricingBulkRefresh, setClientPricingBulkRefresh] = useState(0);
    const [clientPricingSearch, setClientPricingSearch] = useState('');
    const [clientPricingBulkForm, setClientPricingBulkForm] = useState({
        discountType: 'amount',
        discountValue: '',
        benefitType: 'amount',
        benefitValue: '',
        marginPercent: '',
    });
    const [clientPricingBulkSaving, setClientPricingBulkSaving] = useState(false);
    const [clientPricingPendingCosts, setClientPricingPendingCosts] = useState([]);
    const [clientPricingPendingCostValues, setClientPricingPendingCostValues] = useState({});
    const [clientPricingPendingCostSaving, setClientPricingPendingCostSaving] = useState({});
    const [clientPricingPendingCostErrors, setClientPricingPendingCostErrors] = useState({});
    const [clientPricingPendingRule, setClientPricingPendingRule] = useState(null);
    const [clientPricingRowSaving, setClientPricingRowSaving] = useState({});
    const [clientPricingRowSaveStatus, setClientPricingRowSaveStatus] = useState({});
    const [clientPricingSettings, setClientPricingSettings] = useState({
        mode: CLIENT_PRICING_MODE_COST_PLUS,
        marginPercentDisplay: CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
    });
    const [clientPricingSettingsLoading, setClientPricingSettingsLoading] = useState(false);
    const [clientPricingSettingsSaving, setClientPricingSettingsSaving] = useState(false);
    const [clientPricingSettingsError, setClientPricingSettingsError] = useState('');
    const [clientPricingSettingsSaved, setClientPricingSettingsSaved] = useState(false);
    const [clientPricingConversionSummary, setClientPricingConversionSummary] = useState(null);
    const [clientPricingConversionIssues, setClientPricingConversionIssues] = useState([]);
    const [clientPricingConversionIssueTotal, setClientPricingConversionIssueTotal] = useState(0);
    const [clientPricingConversionIssuePage, setClientPricingConversionIssuePage] = useState(1);
    const [clientPricingConversionLoading, setClientPricingConversionLoading] = useState(false);
    const [clientPricingConversionRetrying, setClientPricingConversionRetrying] = useState(false);
    const [clientPricingConversionError, setClientPricingConversionError] = useState('');
    const clientPricingConversionRequestIdRef = useRef(0);
    const [portalCategoryOptions, setPortalCategoryOptions] = useState([]);
    const [portalCategoryLoading, setPortalCategoryLoading] = useState(false);
    const [portalCategoryError, setPortalCategoryError] = useState('');
    const [priceSyncError, setPriceSyncError] = useState('');
    const [priceSyncSaved, setPriceSyncSaved] = useState(false);
    const [priceSyncLoading, setPriceSyncLoading] = useState(false);
    const [printMode, setPrintMode] = useState(DEFAULT_PRINT_MODE);
    const [printModeSaved, setPrintModeSaved] = useState(false);
    const [printReceiptRedirect, setPrintReceiptRedirect] = useState(DEFAULT_PRINT_RECEIPT_REDIRECT);
    const [printRedirectDefaults, setPrintRedirectDefaults] = useState(DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS);
    const [printRedirectDefaultsLoading, setPrintRedirectDefaultsLoading] = useState(false);
    const [printRedirectDefaultsSaving, setPrintRedirectDefaultsSaving] = useState(false);
    const [printRedirectDefaultsError, setPrintRedirectDefaultsError] = useState('');
    const [printRedirectDefaultsSaved, setPrintRedirectDefaultsSaved] = useState(false);
    const [printSettingsSaving, setPrintSettingsSaving] = useState(false);
    const [printSettingsError, setPrintSettingsError] = useState('');
    const [taxes, setTaxes] = useState([]);
    const [taxesLoading, setTaxesLoading] = useState(false);
    const [taxesError, setTaxesError] = useState('');
    const [taxForm, setTaxForm] = useState({
        name: '',
        rate: '',
        applyToAllProducts: true,
        isActive: true,
    });
    const [taxSaving, setTaxSaving] = useState(false);
    const [taxEdits, setTaxEdits] = useState({});
    const [branchTaxMap, setBranchTaxMap] = useState({});
    const [branchTaxLoading, setBranchTaxLoading] = useState(false);
    const [branchTaxError, setBranchTaxError] = useState('');
    const [branchTaxSaving, setBranchTaxSaving] = useState({});
    const [branchTaxDirty, setBranchTaxDirty] = useState({});
    const [priceRecommendationSettings, setPriceRecommendationSettings] = useState(() => emptyPriceRecommendationSettings());
    const [priceRecommendationLoading, setPriceRecommendationLoading] = useState(false);
    const [priceRecommendationSaving, setPriceRecommendationSaving] = useState(false);
    const [priceRecommendationError, setPriceRecommendationError] = useState('');
    const [priceRecommendationSaved, setPriceRecommendationSaved] = useState(false);
    const [priceRecommendationLoadedBranch, setPriceRecommendationLoadedBranch] = useState(null);
    const priceRecommendationRequestRef = useRef(0);
    const priceRecommendationBranchRef = useRef(activeBranchId);
    priceRecommendationBranchRef.current = activeBranchId;
    const [productSearchModeDraft, setProductSearchModeDraft] = useState(PRODUCT_SEARCH_MODE_SUGGESTIVE);
    const [productSearchSaving, setProductSearchSaving] = useState(false);
    const [productSearchError, setProductSearchError] = useState('');
    const [productSearchSaved, setProductSearchSaved] = useState(false);
    const [posSoldOutMode, setPosSoldOutMode] = useState(POS_SOLD_OUT_MODE_NORMAL);
    const [posSearchSuggestions, setPosSearchSuggestions] = useState(
        DEFAULT_POS_SEARCH_SUGGESTION_VISIBILITY
    );
    const [posSoldOutLoading, setPosSoldOutLoading] = useState(false);
    const [posSoldOutSaving, setPosSoldOutSaving] = useState(false);
    const [posSoldOutError, setPosSoldOutError] = useState('');
    const [posSoldOutSaved, setPosSoldOutSaved] = useState(false);
    const { user } = useCurrentUser();
    const { confirm } = useConfirmDialog();
    const {
        preferences: myPreferences,
        loading: preferencesLoading,
        error: preferencesError,
        savePreferences: saveMyPreferences,
    } = useMyPreferences();
    const {
        searchMode: productSearchMode,
        loading: productSearchSettingsLoading,
        error: productSearchSettingsLoadError,
        saveProductSearchSettings,
    } = useProductSearchSettings();

    const canReadBranches = hasPermission(user, 'branch.read');
    const canUpdateBranch = hasPermission(user, 'branch.update');
    const canReadTaxes = hasPermission(user, 'tax.read');
    const isBusinessOwner = isBusinessOwnerUser(user);
    const canManageTaxes = !isBusinessOwner && hasPermission(user, 'tax.manage');
    const canReadUsers = hasPermission(user, 'user.read');
    const canReadProducts = hasPermission(user, 'product.read');
    const canReadPrices = hasPermission(user, 'price.read');
    const canUpdatePrices = hasPermission(user, 'price.update');
    const canCreateProduct = hasPermission(user, 'product.create');
    const canUpdateProduct = hasPermission(user, 'product.update');
    const canDeleteProduct = hasPermission(user, 'product.delete');
    const canReadCategories = hasAnyPermission(user, ['category.read', 'category.manage']);
    const canUpdateCategories = hasAnyPermission(user, ['category.update', 'category.manage']);
    const canManagePurchaseOrderReasons = !isBusinessOwner && hasPermission(user, 'purchase_order.settings');
    const canReadPurchaseOrderSettings = canManagePurchaseOrderReasons || hasAnyPermission(user, [
        'purchase_order.settings.read',
        'settings.read',
    ]) || isBusinessOwner;
    const canManageClientPricing = !isBusinessOwner && hasPermission(user, 'client_pricing.manage');
    const canReadClientPricing = canManageClientPricing || hasAnyPermission(user, [
        'client_pricing.read',
        'settings.read',
    ]) || isBusinessOwner;
    const canManageGlobalSettings = !isBusinessOwner && hasAnyPermission(user, [
        'settings.manage',
        'branch.access.any',
    ]);
    const canReadGlobalSettings = canManageGlobalSettings
        || hasPermission(user, 'settings.read')
        || isBusinessOwner;
    const canReviewPurchaseOrders = hasAnyPermission(user, [
        'purchase_order.read',
        'purchase_order.update',
        'purchase_order.settings',
    ]);
    const canAccessRegisters = hasAnyPermission(user, [
        'cash_register.read',
        'cash_register.manage',
        'cash_register.assign_users',
    ]);
    const canConfigureRegisters = !isBusinessOwner && hasAnyPermission(user, [
        'cash_register.manage',
        'cash_register.assign_users',
    ]);
    const canUsePersonalPrintSettings = Boolean(user);

    const visibleTopics = useMemo(() => getVisibleSettingsTopics(user), [user]);

    const activeTopicConfig = useMemo(
        () => visibleTopics.find((topic) => topic.id === activeTopic) || visibleTopics[0] || null,
        [activeTopic, visibleTopics]
    );

    const activeSectionConfig = useMemo(
        () =>
            activeTopicConfig?.sections.find((section) => section.id === activeSection)
            || activeTopicConfig?.sections[0]
            || null,
        [activeSection, activeTopicConfig]
    );

    const isBranchScopedSection = Boolean(activeSectionConfig?.branchScoped);
    const activeBranches = useMemo(
        () => branches.filter((branch) => !branch.isDeleted),
        [branches]
    );

    const activeBranch = useMemo(
        () => activeBranches.find((branch) => String(branch.id) === String(activeBranchId)),
        [activeBranches, activeBranchId]
    );

    const clientPricingReferenceBranchId = useMemo(() => {
        if (activeBranchId) return String(activeBranchId);
        if (branchRequestConfig.resolvedMainBranchId) return String(branchRequestConfig.resolvedMainBranchId);
        return activeBranches[0]?.id ? String(activeBranches[0].id) : '';
    }, [activeBranchId, activeBranches, branchRequestConfig.resolvedMainBranchId]);

    const branchLabel = activeBranch?.branchName || 'Sucursal seleccionada';
    const effectiveMainBranchId = branchRequestConfig.resolvedMainBranchId ?? activeBranches[0]?.id ?? null;
    useEffect(() => {
        setMappings(getBarcodePrefixMappings());
    }, []);

    useEffect(() => {
        let mounted = true;
        const loadBarcodeRule = async () => {
            if (!canReadGlobalSettings) {
                setBarcodeRuleError('');
                setBarcodeRuleLoading(false);
                return;
            }
            setBarcodeRuleLoading(true);
            setBarcodeRuleError('');
            try {
                const data = await getBarcodePricingRule();
                if (!mounted) return;
                const thresholds = data?.thresholds || {};
                setBarcodePricingRule({
                    mode: data?.mode === 'subtotal' ? 'subtotal' : 'quantity',
                    thresholds: Object.fromEntries(
                        Object.entries(thresholds).map(([key, value]) => [key, value ?? ''])
                    ),
                });
                setBarcodeRuleSaved(false);
            } catch (err) {
                if (!mounted) return;
                setBarcodeRuleError(err?.response?.data?.message || 'No se pudo cargar la regla global.');
            } finally {
                if (mounted) setBarcodeRuleLoading(false);
            }
        };
        loadBarcodeRule();
        return () => {
            mounted = false;
        };
    }, [canReadGlobalSettings]);

    const loadProductFormats = useCallback(async () => {
        if (!canReadGlobalSettings) return;
        setProductFormatsLoading(true);
        setProductFormatsError('');
        try {
            const data = await getProductFormats();
            setProductFormats(normalizeProductFormatPositions(data?.formats));
            setProductFormatsSaved(false);
        } catch (err) {
            setProductFormatsError(err?.response?.data?.message || 'No se pudieron cargar los formatos.');
        } finally {
            setProductFormatsLoading(false);
        }
    }, [canReadGlobalSettings]);

    useEffect(() => {
        if (!canReadGlobalSettings) return;
        loadProductFormats();
    }, [canReadGlobalSettings, loadProductFormats]);

    const applyCategoryBarcodeResponse = (data) => {
        setCategoryBarcodes(Array.isArray(data?.categories) ? data.categories : []);
        setCategoryBarcodeInactiveCount(Number(data?.inactiveCount || 0));
    };

    const fetchCategoryBarcodes = async () => {
        setCategoryBarcodeLoading(true);
        setCategoryBarcodeError('');
        try {
            const data = await getCategoryBarcodes();
            applyCategoryBarcodeResponse(data);
            setCategoryBarcodeSaved(false);
        } catch (err) {
            setCategoryBarcodes([]);
            setCategoryBarcodeError(
                err?.response?.data?.message || 'No se pudieron cargar los barcodes de categorias.'
            );
        } finally {
            setCategoryBarcodeLoading(false);
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'catalog-category-barcodes') return;
        fetchCategoryBarcodes();
    }, [activeSectionConfig?.id]);

    const loadClientPricingCategories = useCallback(async () => {
        if (!canReadClientPricing) {
            setClientPricingCategories([]);
            setClientPricingCategoriesLoaded(true);
            setClientPricingCategoriesError('No tienes permisos para administrar descuentos de clientes.');
            return;
        }
        setClientPricingCategoriesLoading(true);
        setClientPricingCategoriesLoaded(false);
        setClientPricingCategoriesError('');
        try {
            const data = await getClientPricingCategories();
            const categories = Array.isArray(data?.categories) ? data.categories : [];
            setClientPricingCategories(categories);
            setClientPricingGroups(Array.isArray(data?.groups) ? data.groups : []);
        } catch (err) {
            setClientPricingCategories([]);
            setClientPricingCategoriesError(
                err?.response?.data?.message || 'No se pudieron cargar las categorias de descuento.'
            );
        } finally {
            setClientPricingCategoriesLoading(false);
            setClientPricingCategoriesLoaded(true);
        }
    }, [canReadClientPricing]);

    const loadClientPricingApplicabilityOptions = useCallback(async () => {
        if (!canReadClientPricing) {
            setClientPricingApplicabilityOptions({ branches: [], categories: [], productFormats: [] });
            setClientPricingApplicabilityError('No tienes permisos para ver las opciones de aplicabilidad.');
            return;
        }
        setClientPricingApplicabilityLoading(true);
        setClientPricingApplicabilityError('');
        try {
            const data = await getClientPricingApplicabilityOptions();
            setClientPricingApplicabilityOptions({
                branches: Array.isArray(data?.branches) ? data.branches : [],
                categories: Array.isArray(data?.categories) ? data.categories : [],
                productFormats: Array.isArray(data?.productFormats) ? data.productFormats : [],
            });
        } catch (err) {
            setClientPricingApplicabilityOptions({ branches: [], categories: [], productFormats: [] });
            setClientPricingApplicabilityError(
                err?.response?.data?.message || 'No se pudieron cargar las opciones de aplicabilidad.'
            );
        } finally {
            setClientPricingApplicabilityLoading(false);
        }
    }, [canReadClientPricing]);

    const selectClientPricingCategory = useCallback((categoryId, { replace = false } = {}) => {
        const nextId = String(categoryId || '').trim();
        setSelectedClientPricingCategoryId(nextId);
        const nextSearch = buildClientPricingCategorySearch(location.search, nextId);
        if (nextSearch === location.search) return;
        navigate(`${location.pathname}${nextSearch}`, { replace });
    }, [location.pathname, location.search, navigate]);

    const loadClientPricingSettings = useCallback(async () => {
        if (!canReadClientPricing) return;
        setClientPricingSettingsLoading(true);
        setClientPricingSettingsError('');
        try {
            const data = await getClientPricingSettings();
            setClientPricingSettings({
                mode: [
                    CLIENT_PRICING_MODE_COST_PLUS,
                    CLIENT_PRICING_MODE_DISCOUNT,
                    CLIENT_PRICING_MODE_MARGIN_DISCOUNT,
                ].includes(data?.mode) ? data.mode : CLIENT_PRICING_MODE_COST_PLUS,
                marginPercentDisplay: data?.marginPercentDisplay === CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
                    ? CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
                    : CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
            });
            setClientPricingSettingsSaved(false);
        } catch (err) {
            setClientPricingSettingsError(
                err?.response?.data?.message || 'No se pudo cargar la configuracion de precios por cliente.'
            );
        } finally {
            setClientPricingSettingsLoading(false);
        }
    }, [canReadClientPricing]);

    const loadClientPricingConversionIssues = useCallback(async (page = 1) => {
        const requestId = ++clientPricingConversionRequestIdRef.current;
        if (!canReadClientPricing) return;
        setClientPricingConversionLoading(true);
        setClientPricingConversionError('');
        try {
            const data = await getClientPricingMarginConversionIssues({ page, pageSize: 100 });
            if (requestId !== clientPricingConversionRequestIdRef.current) return;
            setClientPricingConversionIssues(Array.isArray(data?.issues) ? data.issues : []);
            setClientPricingConversionIssueTotal(Number(data?.total || 0));
            setClientPricingConversionIssuePage(Number(data?.page || page));
            if (data?.summary) {
                setClientPricingConversionSummary((current) => ({
                    ...(current || {}),
                    ...data.summary,
                }));
            }
        } catch (err) {
            if (requestId !== clientPricingConversionRequestIdRef.current) return;
            setClientPricingConversionError(
                err?.response?.data?.message || 'No se pudieron cargar los descuentos pendientes de conversión.'
            );
        } finally {
            if (requestId === clientPricingConversionRequestIdRef.current) {
                setClientPricingConversionLoading(false);
            }
        }
    }, [canReadClientPricing]);

    const loadClientPricingDiscountCatalog = useCallback(async () => {
        const requestId = ++clientPricingCatalogRequestIdRef.current;
        if (!canReadClientPricing || !selectedClientPricingCategoryId || clientPricingSettingsLoading
            || (clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT
                && String(clientPricingFormatRulesId) !== String(selectedClientPricingCategoryId))) {
            setClientPricingDiscountRows([]);
            setClientPricingDiscountsLoading(false);
            setClientPricingDiscountsError('');
            return;
        }
        setClientPricingDiscountsLoading(true);
        setClientPricingDiscountsError('');
        try {
            const data = await getClientPricingDiscountCatalog(selectedClientPricingCategoryId, {
                branchId: clientPricingReferenceBranchId,
                search: clientPricingSearch.trim() || undefined,
                searchMode: productSearchMode,
            });
            if (requestId !== clientPricingCatalogRequestIdRef.current) return;
            setClientPricingDiscountRows(Array.isArray(data?.rows) ? data.rows : []);
            setClientPricingRowSaveStatus({});
            setClientPricingDiscountsSaved(false);
        } catch (err) {
            if (requestId !== clientPricingCatalogRequestIdRef.current) return;
            setClientPricingDiscountRows([]);
            setClientPricingDiscountsError(
                err?.response?.data?.message || 'No se pudo cargar la matriz de descuentos.'
            );
        } finally {
            if (requestId === clientPricingCatalogRequestIdRef.current) {
                setClientPricingDiscountsLoading(false);
            }
        }
    }, [
        canReadClientPricing,
        selectedClientPricingCategoryId,
        clientPricingReferenceBranchId,
        clientPricingSearch,
        productSearchMode,
        clientPricingSettingsLoading,
        clientPricingSettings.mode,
        clientPricingFormatRulesId,
    ]);

    const { schedule: scheduleClientPricingCatalogRefresh } = useDebouncedCallback(() => {
        if (activeSectionConfig?.id !== 'client-pricing-categories') return;
        if (!selectedClientPricingCategoryId) return;
        loadClientPricingDiscountCatalog();
    }, 250);

    const handleClientPricingCatalogEvent = useCallback((event) => {
        if (event?.type !== 'products.invalidated') return;
        scheduleClientPricingCatalogRefresh();
    }, [scheduleClientPricingCatalogRefresh]);

    useProductEvents(
        activeSectionConfig?.id === 'client-pricing-categories'
            ? clientPricingReferenceBranchId
            : null,
        handleClientPricingCatalogEvent
    );

    useEffect(() => {
        if (activeSectionConfig?.id !== 'client-pricing-categories') return;
        loadClientPricingCategories();
    }, [activeSectionConfig?.id, loadClientPricingCategories]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'client-pricing-categories') return;
        loadClientPricingApplicabilityOptions();
    }, [activeSectionConfig?.id, loadClientPricingApplicabilityOptions]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'client-pricing-categories') return;
        if (!clientPricingCategoriesLoaded || clientPricingCategoriesError) return;
        const requestedId = getClientPricingCategoryIdFromSearch(location.search);
        const resolvedId = resolveClientPricingCategorySelection({
            categories: clientPricingCategories,
            requestedId,
            currentId: selectedClientPricingCategoryId,
        });
        if (resolvedId !== selectedClientPricingCategoryId) {
            setSelectedClientPricingCategoryId(resolvedId);
        }
        const canonicalSearch = buildClientPricingCategorySearch(location.search, resolvedId);
        if (canonicalSearch !== location.search) {
            navigate(`${location.pathname}${canonicalSearch}`, { replace: true });
        }
    }, [
        activeSectionConfig?.id,
        clientPricingCategories,
        clientPricingCategoriesError,
        clientPricingCategoriesLoaded,
        location.pathname,
        location.search,
        navigate,
        selectedClientPricingCategoryId,
    ]);

    useEffect(() => {
        if (!['client-pricing-categories', 'client-pricing-configuration'].includes(activeSectionConfig?.id)) return;
        loadClientPricingSettings();
    }, [activeSectionConfig?.id, loadClientPricingSettings]);

    useEffect(() => {
        if (!['client-pricing-categories', 'client-pricing-configuration'].includes(activeSectionConfig?.id)) return;
        if (clientPricingSettings.mode !== CLIENT_PRICING_MODE_MARGIN_DISCOUNT) {
            clientPricingConversionRequestIdRef.current += 1;
            setClientPricingConversionLoading(false);
            return;
        }
        loadClientPricingConversionIssues();
    }, [
        activeSectionConfig?.id,
        clientPricingSettings.mode,
        loadClientPricingConversionIssues,
    ]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'client-pricing-categories') return undefined;
        const timer = window.setTimeout(() => {
            loadClientPricingDiscountCatalog();
        }, 250);
        return () => {
            window.clearTimeout(timer);
            clientPricingCatalogRequestIdRef.current += 1;
        };
    }, [activeSectionConfig?.id, loadClientPricingDiscountCatalog]);

    useEffect(() => {
        setClientPricingBulkSaving(false);
        setClientPricingPendingCosts([]);
        setClientPricingPendingCostValues({});
        setClientPricingPendingCostSaving({});
        setClientPricingPendingCostErrors({});
        setClientPricingPendingRule(null);
    }, [selectedClientPricingCategoryId, clientPricingReferenceBranchId]);

    useEffect(() => {
        if (!canReadGlobalSettings) {
            setBranchRequestConfigLoading(false);
            setBranchRequestConfigError('');
            return undefined;
        }
        let mounted = true;
        const loadBranchRequestConfig = async () => {
            setBranchRequestConfigLoading(true);
            setBranchRequestConfigError('');
            try {
                const config = await getBranchRequestConfig();
                if (!mounted) return;
                setBranchRequestConfig({
                    mainBranchId: config?.mainBranchId ? String(config.mainBranchId) : '',
                    resolvedMainBranchId: config?.resolvedMainBranchId ?? null,
                    defaultPurchaseOrderCategoryIds: Array.isArray(config?.defaultPurchaseOrderCategoryIds)
                        ? config.defaultPurchaseOrderCategoryIds.map((id) => String(id))
                        : [],
                    portalProductFormatCodes: Array.isArray(config?.portalProductFormatCodes) && config.portalProductFormatCodes.length
                        ? config.portalProductFormatCodes.map((code) => String(code))
                        : ['mayorista'],
                });
                setBranchRequestConfigSaved(false);
            } catch (err) {
                if (!mounted) return;
                setBranchRequestConfigError(
                    err?.response?.data?.message || 'No se pudo cargar la configuracion de solicitudes.'
                );
            } finally {
                if (mounted) setBranchRequestConfigLoading(false);
            }
        };
        loadBranchRequestConfig();
        return () => {
            mounted = false;
        };
    }, [canReadGlobalSettings]);

    useEffect(() => {
        if (![
            'branch-price-sync',
            'branch-stock-requests',
            'purchase-order-visible-formats',
            'purchase-order-default-categories',
        ].includes(activeSectionConfig?.id)) return;
        let mounted = true;
        const loadPortalCategories = async () => {
            if (!canReadCategories) {
                setPortalCategoryOptions([]);
                setPortalCategoryError('No tienes permisos para ver categorias.');
                return;
            }
            setPortalCategoryLoading(true);
            setPortalCategoryError('');
            try {
                const data = await getCategories();
                if (!mounted) return;
                const categories = Array.isArray(data) ? data : normalizePaginatedResponse(data).data;
                setPortalCategoryOptions(
                    categories
                        .filter((category) => category?.isActive !== false)
                        .map((category) => ({
                            id: String(category.id),
                            name: category.name || `Categoria ${category.id}`,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name))
                );
            } catch (err) {
                if (!mounted) return;
                setPortalCategoryOptions([]);
                setPortalCategoryError(
                    err?.response?.data?.message || 'No se pudieron cargar las categorias.'
                );
            } finally {
                if (mounted) setPortalCategoryLoading(false);
            }
        };
        loadPortalCategories();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canReadCategories]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'purchase-order-reasons') return;
        let mounted = true;
        const loadPurchaseOrderReasons = async () => {
            if (!canReadPurchaseOrderSettings) {
                setPurchaseOrderReasonsError('No tienes permisos para ver estos motivos.');
                return;
            }
            setPurchaseOrderReasonsLoading(true);
            setPurchaseOrderReasonsError('');
            try {
                const data = await getPurchaseOrderReasonsSettings();
                if (!mounted) return;
                setPurchaseOrderReasons({
                    reduce: Array.isArray(data?.reduce) ? data.reduce : [],
                    remove: Array.isArray(data?.remove) ? data.remove : [],
                    increase: Array.isArray(data?.increase) ? data.increase : [],
                });
                setPurchaseOrderReasonsSaved(false);
            } catch (err) {
                if (!mounted) return;
                setPurchaseOrderReasonsError(
                    err?.response?.data?.message || 'No se pudieron cargar los motivos.'
                );
            } finally {
                if (mounted) setPurchaseOrderReasonsLoading(false);
            }
        };
        loadPurchaseOrderReasons();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canReadPurchaseOrderSettings]);

    useEffect(() => {
        if (!['purchase-order-notifications', 'notifications'].includes(activeSectionConfig?.id)) return;
        let mounted = true;
        const loadPurchaseOrderNotificationSettings = async () => {
            if (!canReadPurchaseOrderSettings) {
                setPurchaseOrderNotificationsError('No tienes permisos para ver esta configuracion.');
                return;
            }
            setPurchaseOrderNotificationsLoading(true);
            setPurchaseOrderNotificationsError('');
            setPurchaseOrderNotificationsRoleWarning('');
            try {
                const settings = await getPurchaseOrderNotificationSettings();
                if (!mounted) return;
                const normalizedSettings = {
                    enabled: settings?.enabled !== false,
                    recipientRoleNames: normalizeRoleNameList(settings?.recipientRoleNames),
                    respectBranchAssignments: settings?.respectBranchAssignments !== false,
                    includeGlobalAdminUsers: settings?.includeGlobalAdminUsers !== false,
                };
                setPurchaseOrderNotificationSettings(normalizedSettings);
                let roles = [];
                let roleWarning = '';
                if (canReadUsers) {
                    try {
                        const rolesResponse = await getRoles({ pageSize: 0 });
                        if (!mounted) return;
                        roles = Array.isArray(rolesResponse)
                            ? rolesResponse
                            : normalizePaginatedResponse(rolesResponse).data;
                    } catch (roleError) {
                        if (!mounted) return;
                        roleWarning = roleError?.response?.data?.message
                            || 'No se pudo cargar el catalogo completo de roles. Se muestran los roles ya configurados.';
                    }
                } else {
                    roleWarning = 'No tienes permiso para cargar el catalogo completo de roles. Se muestran los roles ya configurados.';
                }
                const roleOptions = buildPurchaseOrderNotificationRoleOptions(
                    roles,
                    normalizedSettings.recipientRoleNames
                );
                setPurchaseOrderNotificationRoles(roleOptions);
                if (!roleOptions.some((role) => !role.configuredOnly) && !roleWarning) {
                    roleWarning = 'Se muestran los roles configurados. Revisa que los permisos base esten sincronizados en el servidor.';
                }
                setPurchaseOrderNotificationsRoleWarning(roleWarning);
                setPurchaseOrderNotificationsSaved(false);
            } catch (err) {
                if (!mounted) return;
                setPurchaseOrderNotificationsError(
                    err?.response?.data?.message || 'No se pudo cargar la configuracion de notificaciones.'
                );
            } finally {
                if (mounted) setPurchaseOrderNotificationsLoading(false);
            }
        };
        loadPurchaseOrderNotificationSettings();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canReadPurchaseOrderSettings, canReadUsers]);

    useEffect(() => {
        setPrintMode(getPrintMode());
    }, []);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'registers-print-defaults') return undefined;
        if (!canReadGlobalSettings) {
            setPrintRedirectDefaultsLoading(false);
            setPrintRedirectDefaultsError('');
            return undefined;
        }
        let mounted = true;
        const loadPrintRedirectDefaults = async () => {
            setPrintRedirectDefaultsLoading(true);
            setPrintRedirectDefaultsError('');
            try {
                const settings = await getPrintReceiptRedirectDefaults();
                if (!mounted) return;
                setPrintRedirectDefaults(normalizePrintReceiptRedirectDefaults(settings));
                setPrintRedirectDefaultsSaved(false);
            } catch (err) {
                if (!mounted) return;
                setPrintRedirectDefaultsError(
                    err?.response?.data?.message || 'No se pudo cargar el tiempo por defecto.'
                );
            } finally {
                if (mounted) setPrintRedirectDefaultsLoading(false);
            }
        };
        loadPrintRedirectDefaults();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canReadGlobalSettings]);

    useEffect(() => {
        setPrintReceiptRedirect(normalizePrintReceiptRedirect(myPreferences?.printReceiptRedirect));
    }, [myPreferences?.printReceiptRedirect]);

    useEffect(() => {
        setPurchaseOrderReview(normalizePurchaseOrderReview(myPreferences?.purchaseOrderReview));
    }, [myPreferences?.purchaseOrderReview]);

    useEffect(() => {
        fetchTaxes();
    }, [canReadTaxes]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'taxes-manage') return;
        if (!activeBranches.length) return;
        fetchBranchTaxes();
    }, [activeSectionConfig?.id, activeBranches, taxes, canReadTaxes]);

    useEffect(() => {
        if (!taxes.length) {
            setTaxEdits({});
            return;
        }
        const nextEdits = {};
        taxes.forEach((tax) => {
            nextEdits[tax.id] = {
                name: tax.name || '',
                rate: tax.rate ?? '',
                applyToAllProducts: !!tax.applyToAllProducts,
                isActive: tax.isActive !== false,
            };
        });
        setTaxEdits(nextEdits);
    }, [taxes]);



    useEffect(() => {
        const requestedBranchId = routeBranchId || getSettingsBranchIdFromSearch(location.search);
        if (requestedBranchId) setActiveBranchId(requestedBranchId);
    }, [location.search, routeBranchId]);





    useEffect(() => {
        const destination = resolveSettingsDestination({
            visibleTopics,
            topicId: routeTopicId,
            sectionId: routeSectionId,
            search: location.search,
        });
        if (!destination) return;

        if (destination.topic.id !== activeTopic) {
            setActiveTopic(destination.topic.id);
        }
        if (destination.section.id !== activeSection) {
            setActiveSection(destination.section.id);
        }
        if (`${location.pathname}${location.search}` !== destination.path) {
            navigate(destination.path, { replace: true });
        }
    }, [
        activeSection,
        activeTopic,
        location.pathname,
        location.search,
        navigate,
        routeSectionId,
        routeTopicId,
        visibleTopics,
    ]);

    useEffect(() => {
        if (!activeTopicConfig?.sections?.length) return;
        if (activeTopicConfig.sections.some((section) => section.id === activeSection)) return;
        setActiveSection(activeTopicConfig.sections[0].id);
    }, [activeSection, activeTopicConfig]);

    const goToSettingsSection = (sectionId, topicId = null) => {
        const topicConfig = topicId
            ? visibleTopics.find((topic) => topic.id === topicId)
            : visibleTopics.find((topic) => topic.sections.some((section) => section.id === sectionId));
        if (!topicConfig?.sections.some((section) => section.id === sectionId)) return;
        setActiveTopic(topicConfig.id);
        setActiveSection(sectionId);
        navigate(buildSettingsPath(topicConfig.id, sectionId, location.search), { replace: true });
    };

    const fetchBranches = async () => {
        setBranchListResolved(false);
        if (!canReadBranches) {
            setBranches([]);
            setBranchError('No tienes permisos para ver sucursales.');
            setBranchLoading(false);
            setBranchListResolved(true);
            return;
        }
        setBranchLoading(true);
        setBranchError('');
        try {
            const data = await getBranches({ pageSize: 0 });
            const normalized = normalizePaginatedResponse(data);
            const list = normalized.data || [];
            setBranches(list);
            setPriceSyncError('');
            setPriceSyncSaved(false);
        } catch (err) {
            if (err.response?.status === 403) {
                setBranchError('No tienes permisos para ver sucursales.');
                setPriceSyncError('No tienes permisos para ver la configuracion de precios.');
            } else {
                setBranchError('No se pudieron cargar las sucursales.');
                setPriceSyncError('No se pudo cargar la configuracion de precios.');
            }
        } finally {
            setBranchLoading(false);
            setBranchListResolved(true);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, [canReadBranches]);

    useEffect(() => {
        const nextId = resolveSettingsBranchSelection({
            currentBranchId: activeBranchId,
            branches: activeBranches,
            listResolved: branchListResolved && !branchLoading,
            canReadBranches,
            hasError: Boolean(branchError),
        });
        if (nextId !== String(activeBranchId || '')) setActiveBranchId(nextId);
        const searchParams = new URLSearchParams(location.search);
        const requestedBranchId = getSettingsBranchIdFromSearch(location.search);
        if (
            branchListResolved
            && !branchLoading
            && canReadBranches
            && !branchError
            && searchParams.has('branchId')
            && (
                !requestedBranchId
                || requestedBranchId !== nextId
            )
        ) {
            const nextSearch = buildSettingsBranchSearch(location.search, nextId);
            navigate(`${location.pathname}${nextSearch}`, { replace: true });
        }
    }, [
        activeBranches,
        activeBranchId,
        branchError,
        branchListResolved,
        branchLoading,
        canReadBranches,
        location.pathname,
        location.search,
        navigate,
    ]);

    useEffect(() => {
        if (!activeBranchId || !activeBranches.length) return;
        const current = activeBranches.find((branch) => String(branch.id) === String(activeBranchId));
        if (!current) return;
        setPriceSync({
            sourceBranchId: current.priceSyncSourceBranchId
                ? String(current.priceSyncSourceBranchId)
                : '',
            adjustmentType: current.priceSyncAdjustmentType || '',
            adjustmentValue:
                current.priceSyncAdjustmentValue !== null
                && current.priceSyncAdjustmentValue !== undefined
                    ? String(current.priceSyncAdjustmentValue)
                    : '',
        });
        setPriceSyncSaved(false);
    }, [activeBranchId, activeBranches]);

    const sanitizeAdjustmentInput = (value) => value.replace(/[^\d-]/g, '');

    const handlePriceSyncSourceChange = (value) => {
        setPriceSync((prev) => ({
            sourceBranchId: value,
            adjustmentType: value ? (prev.adjustmentType || 'percent') : '',
            adjustmentValue: value ? prev.adjustmentValue : '',
        }));
        setPriceSyncSaved(false);
        setPriceSyncError('');
    };

    const handlePriceSyncTypeChange = (value) => {
        setPriceSync((prev) => ({
            ...prev,
            adjustmentType: value,
        }));
        setPriceSyncSaved(false);
    };

    const handlePriceSyncValueChange = (value) => {
        setPriceSync((prev) => ({
            ...prev,
            adjustmentValue: sanitizeAdjustmentInput(value),
        }));
        setPriceSyncSaved(false);
    };

    const parseAdjustmentValue = (value) => {
        if (value === '' || value === null || value === undefined) return 0;
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return null;
        return Math.round(parsed);
    };

    const handleSavePriceSync = async () => {
        if (!activeBranchId) {
            setPriceSyncError('Sucursal no definida.');
            return;
        }
        if (!canUpdateBranch) {
            setPriceSyncError('No tienes permisos para actualizar esta configuracion.');
            return;
        }
        setPriceSyncLoading(true);
        try {
            const payload = {};
            if (!priceSync.sourceBranchId) {
                payload.priceSyncSourceBranchId = null;
                payload.priceSyncAdjustmentType = null;
                payload.priceSyncAdjustmentValue = null;
            } else {
                if (!PRICE_SYNC_TYPE_LABELS[priceSync.adjustmentType]) {
                    setPriceSyncError('Selecciona un tipo de ajuste valido.');
                    setPriceSyncLoading(false);
                    return;
                }
                const parsedValue = parseAdjustmentValue(priceSync.adjustmentValue);
                if (parsedValue === null) {
                    setPriceSyncError('El ajuste debe ser un numero valido.');
                    setPriceSyncLoading(false);
                    return;
                }
                payload.priceSyncSourceBranchId = Number(priceSync.sourceBranchId);
                payload.priceSyncAdjustmentType = priceSync.adjustmentType;
                payload.priceSyncAdjustmentValue = parsedValue;
            }
            await updateBranch(activeBranchId, payload);
            setBranches((prev) =>
                prev.map((branch) =>
                    String(branch.id) === String(activeBranchId)
                        ? {
                            ...branch,
                            priceSyncSourceBranchId: payload.priceSyncSourceBranchId,
                            priceSyncAdjustmentType: payload.priceSyncAdjustmentType,
                            priceSyncAdjustmentValue: payload.priceSyncAdjustmentValue,
                        }
                        : branch
                )
            );
            setPriceSyncError('');
            setPriceSyncSaved(true);
        } catch (err) {
            setPriceSyncSaved(false);
            if (err.response?.status === 403) {
                setPriceSyncError('No tienes permisos para actualizar esta configuracion.');
            } else {
                setPriceSyncError(
                    err.response?.data?.message || 'No se pudo guardar la configuracion de precios.'
                );
            }
        } finally {
            setPriceSyncLoading(false);
        }
    };

    const handleBranchRequestMainChange = (value) => {
        setBranchRequestConfig((prev) => ({
            ...prev,
            mainBranchId: value,
        }));
        setBranchRequestConfigSaved(false);
        setBranchRequestConfigError('');
    };

    const handleDefaultPurchaseOrderCategoryToggle = (categoryId) => {
        const normalizedId = String(categoryId);
        setBranchRequestConfig((prev) => {
            const current = new Set((prev.defaultPurchaseOrderCategoryIds || []).map(String));
            if (current.has(normalizedId)) {
                current.delete(normalizedId);
            } else {
                current.add(normalizedId);
            }
            return {
                ...prev,
                defaultPurchaseOrderCategoryIds: Array.from(current),
            };
        });
        setBranchRequestConfigSaved(false);
        setBranchRequestConfigError('');
    };

    const handlePortalProductFormatToggle = (code) => {
        const normalizedCode = String(code || '').trim().toLowerCase();
        if (!normalizedCode) return;
        setBranchRequestConfig((prev) => {
            const current = new Set((prev.portalProductFormatCodes || []).map((entry) => String(entry).trim().toLowerCase()));
            if (current.has(normalizedCode)) {
                current.delete(normalizedCode);
            } else {
                current.add(normalizedCode);
            }
            return {
                ...prev,
                portalProductFormatCodes: Array.from(current),
            };
        });
        setBranchRequestConfigSaved(false);
        setBranchRequestConfigError('');
    };

    const handleSaveBranchRequestConfig = async () => {
        if (!canManageGlobalSettings) {
            setBranchRequestConfigError('No tienes permisos para actualizar esta configuracion.');
            return;
        }
        setBranchRequestConfigLoading(true);
        setBranchRequestConfigError('');
        try {
            const payload = {
                mainBranchId: branchRequestConfig.mainBranchId
                    ? Number(branchRequestConfig.mainBranchId)
                    : null,
                defaultPurchaseOrderCategoryIds: (branchRequestConfig.defaultPurchaseOrderCategoryIds || [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isInteger(id)),
                portalProductFormatCodes: (branchRequestConfig.portalProductFormatCodes || [])
                    .map((code) => String(code || '').trim().toLowerCase())
                    .filter(Boolean),
            };
            const saved = await updateBranchRequestConfig(payload);
            setBranchRequestConfig({
                mainBranchId: saved?.mainBranchId ? String(saved.mainBranchId) : '',
                resolvedMainBranchId: saved?.resolvedMainBranchId ?? null,
                defaultPurchaseOrderCategoryIds: Array.isArray(saved?.defaultPurchaseOrderCategoryIds)
                    ? saved.defaultPurchaseOrderCategoryIds.map((id) => String(id))
                    : [],
                portalProductFormatCodes: Array.isArray(saved?.portalProductFormatCodes) && saved.portalProductFormatCodes.length
                    ? saved.portalProductFormatCodes.map((code) => String(code))
                    : ['mayorista'],
            });
            setBranchRequestConfigSaved(true);
        } catch (err) {
            setBranchRequestConfigSaved(false);
            setBranchRequestConfigError(
                err?.response?.data?.message || 'No se pudo guardar la configuracion de solicitudes.'
            );
        } finally {
            setBranchRequestConfigLoading(false);
        }
    };

    const updatePurchaseOrderReason = (group, index, patch) => {
        setPurchaseOrderReasons((prev) => ({
            ...prev,
            [group]: (prev[group] || []).map((entry, entryIndex) =>
                entryIndex === index ? { ...entry, ...patch } : entry
            ),
        }));
        setPurchaseOrderReasonsSaved(false);
        setPurchaseOrderReasonsError('');
    };

    const addPurchaseOrderReason = (group) => {
        setPurchaseOrderReasons((prev) => ({
            ...prev,
            [group]: [
                ...(prev[group] || []),
                {
                    id: '',
                    label: '',
                    active: true,
                },
            ],
        }));
        setPurchaseOrderReasonsSaved(false);
        setPurchaseOrderReasonsError('');
    };

    const removePurchaseOrderReason = (group, index) => {
        setPurchaseOrderReasons((prev) => ({
            ...prev,
            [group]: (prev[group] || []).filter((_, entryIndex) => entryIndex !== index),
        }));
        setPurchaseOrderReasonsSaved(false);
        setPurchaseOrderReasonsError('');
    };

    const handleSavePurchaseOrderReasons = async () => {
        if (!canManagePurchaseOrderReasons) return;
        setPurchaseOrderReasonsSaving(true);
        setPurchaseOrderReasonsError('');
        try {
            const response = await updatePurchaseOrderReasonsSettings(purchaseOrderReasons);
            const saved = response?.reasons || response;
            setPurchaseOrderReasons({
                reduce: Array.isArray(saved?.reduce) ? saved.reduce : [],
                remove: Array.isArray(saved?.remove) ? saved.remove : [],
                increase: Array.isArray(saved?.increase) ? saved.increase : [],
            });
            setPurchaseOrderReasonsSaved(true);
        } catch (err) {
            setPurchaseOrderReasonsSaved(false);
            setPurchaseOrderReasonsError(
                err?.response?.data?.message || 'No se pudieron guardar los motivos.'
            );
        } finally {
            setPurchaseOrderReasonsSaving(false);
        }
    };

    const updatePurchaseOrderNotificationSetting = (patch) => {
        setPurchaseOrderNotificationSettings((prev) => ({
            ...prev,
            ...patch,
        }));
        setPurchaseOrderNotificationsSaved(false);
        setPurchaseOrderNotificationsError('');
    };

    const togglePurchaseOrderNotificationRole = (roleName) => {
        const normalizedRole = String(roleName || '').trim();
        if (!normalizedRole) return;
        setPurchaseOrderNotificationSettings((prev) => {
            const current = new Set(prev.recipientRoleNames || []);
            if (current.has(normalizedRole)) current.delete(normalizedRole);
            else current.add(normalizedRole);
            return {
                ...prev,
                recipientRoleNames: Array.from(current),
            };
        });
        setPurchaseOrderNotificationsSaved(false);
        setPurchaseOrderNotificationsError('');
    };

    const handleSavePurchaseOrderNotifications = async () => {
        if (!canManagePurchaseOrderReasons) return;
        setPurchaseOrderNotificationsSaving(true);
        setPurchaseOrderNotificationsError('');
        try {
            const response = await updatePurchaseOrderNotificationSettings(purchaseOrderNotificationSettings);
            const settings = response?.settings || response;
            setPurchaseOrderNotificationSettings({
                enabled: settings?.enabled !== false,
                recipientRoleNames: Array.isArray(settings?.recipientRoleNames)
                    ? settings.recipientRoleNames
                    : [],
                respectBranchAssignments: settings?.respectBranchAssignments !== false,
                includeGlobalAdminUsers: settings?.includeGlobalAdminUsers !== false,
            });
            setPurchaseOrderNotificationsSaved(true);
        } catch (err) {
            setPurchaseOrderNotificationsSaved(false);
            setPurchaseOrderNotificationsError(
                err?.response?.data?.message || 'No se pudo guardar la configuracion de notificaciones.'
            );
        } finally {
            setPurchaseOrderNotificationsSaving(false);
        }
    };

    const refreshPurchaseOrderBrowserPushState = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            loading: true,
            error: '',
            message: '',
        }));
        try {
            const state = await loadBrowserPushState();
            setPurchaseOrderBrowserPushState({
                loading: false,
                saving: false,
                testing: false,
                supported: state.supported,
                configured: state.configured,
                permission: state.permission,
                serverSubscription: state.serverSubscription,
                error: '',
                message: '',
            });
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                loading: false,
                saving: false,
                testing: false,
                error: err?.message || 'No se pudo cargar el estado de este navegador.',
                message: '',
            }));
        }
    };

    const handleEnablePurchaseOrderBrowserPush = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            saving: true,
            testing: false,
            error: '',
            message: '',
        }));
        try {
            await enableBrowserPushNotifications();
            await refreshPurchaseOrderBrowserPushState();
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                message: 'Este navegador quedo suscrito a notificaciones.',
            }));
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                saving: false,
                error: err?.message || 'No se pudieron activar las notificaciones en este navegador.',
                message: '',
            }));
        }
    };

    const handleDisablePurchaseOrderBrowserPush = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            saving: true,
            testing: false,
            error: '',
            message: '',
        }));
        try {
            await disableBrowserPushNotifications();
            await refreshPurchaseOrderBrowserPushState();
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                message: 'Este navegador dejo de recibir notificaciones.',
            }));
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                saving: false,
                error: err?.message || 'No se pudieron desactivar las notificaciones en este navegador.',
                message: '',
            }));
        }
    };

    const handleSendPurchaseOrderTestNotification = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            testing: true,
            error: '',
            message: '',
        }));
        try {
            const response = await sendTestWebPushNotification();
            const result = response?.result || {};
            await refreshPurchaseOrderBrowserPushState();
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                message: result.sent > 0
                    ? 'Notificacion de prueba enviada.'
                    : getPurchaseOrderTestNotificationMessage(result.reason),
            }));
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                testing: false,
                error: err?.response?.data?.message || err?.message || 'No se pudo enviar la notificacion de prueba.',
                message: '',
            }));
        }
    };

    useEffect(() => {
        if (!['purchase-order-notifications', 'notifications'].includes(activeSectionConfig?.id)) return;
        if (!canReadPurchaseOrderSettings) return;
        refreshPurchaseOrderBrowserPushState();
    }, [activeSectionConfig?.id, canReadPurchaseOrderSettings]);

    const handlePrefixChange = (index, value) => {
        setMappings((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                prefix: sanitizeBarcodePrefixInput(value),
            };
            return next;
        });
        setSaved(false);
    };

    const handleModeChange = (index, value) => {
        setMappings((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                mode: value,
            };
            return next;
        });
        setSaved(false);
    };

    const handleAddMapping = () => {
        setMappings((prev) => [...prev, { prefix: '', mode: 'weight' }]);
        setSaved(false);
    };

    const handleRemoveMapping = (index) => {
        setMappings((prev) => prev.filter((_, i) => i !== index));
        setSaved(false);
    };

    const validateMappings = (nextMappings) => {
        const normalized = nextMappings.map((item) => ({
            prefix: sanitizeBarcodePrefixInput(item?.prefix),
            mode: BARCODE_MODE_OPTIONS.includes(item?.mode) ? item.mode : 'weight',
        }));

        const prefixes = normalized.map((item) => item.prefix).filter(Boolean);
        if (prefixes.length === 0) {
            return { valid: false, message: 'Agrega al menos un prefijo.' };
        }

        const uniquePrefixes = new Set(prefixes);
        if (uniquePrefixes.size !== prefixes.length) {
            return { valid: false, message: 'Los prefijos deben ser unicos.' };
        }

        if (normalized.some((item) => !item.prefix)) {
            return { valid: false, message: 'Completa todos los prefijos.' };
        }

        if (normalized.some((item) => isReservedBarcodePrefix(item.prefix))) {
            return { valid: false, message: 'El prefijo 9 esta reservado para totales por categoria.' };
        }

        return { valid: true, normalized };
    };

    const handleSave = () => {
        const result = validateMappings(mappings);
        if (!result.valid) {
            setError(result.message);
            setSaved(false);
            return;
        }
        const savedMappings = saveBarcodePrefixMappings(result.normalized);
        setMappings(savedMappings);
        setError('');
        setSaved(true);
    };

    const handleReset = () => {
        const savedMappings = saveBarcodePrefixMappings(DEFAULT_BARCODE_PREFIX_MAPPINGS);
        setMappings(savedMappings);
        setError('');
        setSaved(false);
    };

    const handleBarcodeRuleModeChange = (mode) => {
        setBarcodePricingRule((prev) => ({
            ...prev,
            mode: mode === 'subtotal' ? 'subtotal' : 'quantity',
        }));
        setBarcodeRuleSaved(false);
    };

    const handleBarcodeRuleThresholdChange = (key, value) => {
        const sanitized = String(value ?? '').replace(/[^\d]/g, '');
        setBarcodePricingRule((prev) => ({
            ...prev,
            thresholds: {
                ...prev.thresholds,
                [key]: sanitized,
            },
        }));
        setBarcodeRuleSaved(false);
    };

    const handleSaveBarcodeRule = async () => {
        if (!canManageGlobalSettings) {
            setBarcodeRuleError('No tienes permisos para guardar esta regla.');
            return;
        }
        const payload = {
            mode: barcodePricingRule.mode === 'subtotal' ? 'subtotal' : 'quantity',
            thresholds: Object.fromEntries(productFormatOptions.map((option) => [
                option.value,
                barcodePricingRule.thresholds[option.value] === ''
                    ? null
                    : Number(barcodePricingRule.thresholds[option.value]),
            ])),
        };
        setBarcodeRuleLoading(true);
        setBarcodeRuleError('');
        try {
            const response = await updateBarcodePricingRule(payload);
            const savedRule = response?.rule || payload;
                setBarcodePricingRule({
                    mode: savedRule.mode === 'subtotal' ? 'subtotal' : 'quantity',
                    thresholds: Object.fromEntries(
                        Object.entries(savedRule?.thresholds || {}).map(([key, value]) => [key, value ?? ''])
                    ),
                });
            setBarcodeRuleSaved(true);
        } catch (err) {
            setBarcodeRuleError(err?.response?.data?.message || 'No se pudo guardar la regla global.');
            setBarcodeRuleSaved(false);
        } finally {
            setBarcodeRuleLoading(false);
        }
    };

    const handleCreateProductFormat = async (event) => {
        event.preventDefault();
        if (!canManageGlobalSettings) {
            setProductFormatsError('No tienes permisos para guardar formatos.');
            return;
        }
        const name = productFormatForm.name.trim();
        if (!name) {
            setProductFormatsError('El nombre del formato es obligatorio.');
            return;
        }
        setProductFormatsLoading(true);
        setProductFormatsError('');
        try {
            const nextSortOrder = productFormats.reduce((max, format) => (
                Math.max(max, Number(format?.sortOrder) || 0)
            ), 0) + 1;
            await createProductFormat({
                name,
                isActive: Boolean(productFormatForm.isActive),
                sortOrder: nextSortOrder,
            });
            setProductFormatForm({ name: '', isActive: true });
            await loadProductFormats();
            setProductFormatsSaved(true);
        } catch (err) {
            setProductFormatsError(err?.response?.data?.message || 'No se pudo crear el formato.');
        } finally {
            setProductFormatsLoading(false);
        }
    };

    const handleUpdateProductFormat = async (format, patch) => {
        if (!format?.id || !canManageGlobalSettings) return;
        setProductFormatsLoading(true);
        setProductFormatsError('');
        try {
            await updateProductFormat(format.id, patch);
            await loadProductFormats();
            setProductFormatsSaved(true);
        } catch (err) {
            setProductFormatsError(err?.response?.data?.message || 'No se pudo actualizar el formato.');
        } finally {
            setProductFormatsLoading(false);
        }
    };

    const handleMoveProductFormat = async (format, direction) => {
        if (!format?.id || !canManageGlobalSettings || productFormatsLoading) return;
        const currentFormats = normalizeProductFormatPositions(productFormats);
        const currentIndex = currentFormats.findIndex((entry) => entry.id === format.id);
        const nextIndex = currentIndex + direction;
        if (currentIndex < 0 || nextIndex < 0 || nextIndex >= currentFormats.length) return;

        const reordered = currentFormats.slice();
        [reordered[currentIndex], reordered[nextIndex]] = [reordered[nextIndex], reordered[currentIndex]];
        const positioned = reordered.map((entry, index) => ({ ...entry, sortOrder: index + 1 }));
        const previousOrders = new Map(currentFormats.map((entry) => [entry.id, entry.sortOrder]));
        const changedFormats = positioned.filter((entry) => previousOrders.get(entry.id) !== entry.sortOrder);

        setProductFormats(positioned);
        setProductFormatsLoading(true);
        setProductFormatsError('');
        try {
            await Promise.all(changedFormats.map((entry) => (
                updateProductFormat(entry.id, { sortOrder: entry.sortOrder })
            )));
            await loadProductFormats();
            setProductFormatsSaved(true);
        } catch (err) {
            setProductFormats(currentFormats);
            setProductFormatsError(err?.response?.data?.message || 'No se pudo actualizar el orden de los formatos.');
        } finally {
            setProductFormatsLoading(false);
        }
    };

    const handleRegenerateCategoryBarcodes = async () => {
        if (!canManageGlobalSettings) {
            setCategoryBarcodeError('No tienes permisos para generar barcodes de categorias.');
            return;
        }
        setCategoryBarcodeRegenerating(true);
        setCategoryBarcodeError('');
        setCategoryBarcodeSaved(false);
        try {
            const data = await regenerateCategoryBarcodes();
            applyCategoryBarcodeResponse(data);
            setCategoryBarcodeSaved(true);
        } catch (err) {
            setCategoryBarcodeError(
                err?.response?.data?.error
                || err?.response?.data?.message
                || 'No se pudieron generar los barcodes de categorias.'
            );
        } finally {
            setCategoryBarcodeRegenerating(false);
        }
    };

    const handlePrintModeChange = (value) => {
        setPrintMode(value);
        setPrintModeSaved(false);
    };

    const handlePrintRedirectEnabledChange = (enabled) => {
        setPrintReceiptRedirect((prev) => ({
            ...prev,
            enabled,
        }));
        setPrintModeSaved(false);
    };

    const handlePrintRedirectDelayChange = (value) => {
        const sanitized = String(value ?? '').replace(/[^\d]/g, '');
        setPrintReceiptRedirect((prev) => ({
            ...prev,
            delaySeconds: sanitized,
        }));
        setPrintModeSaved(false);
    };

    const handlePrintRedirectDefaultDelayChange = (value) => {
        const sanitized = String(value ?? '').replace(/[^\d]/g, '');
        setPrintRedirectDefaults((prev) => ({
            ...prev,
            delaySeconds: sanitized,
        }));
        setPrintRedirectDefaultsSaved(false);
        setPrintRedirectDefaultsError('');
    };

    const savePrintRedirectDefaults = async (settings) => {
        const normalizedDefaults = normalizePrintReceiptRedirectDefaults(settings);
        setPrintRedirectDefaultsSaving(true);
        setPrintRedirectDefaultsError('');
        try {
            const response = await updatePrintReceiptRedirectDefaults(normalizedDefaults);
            const savedDefaults = normalizePrintReceiptRedirectDefaults(response?.settings || response);
            setPrintRedirectDefaults(savedDefaults);
            setPrintRedirectDefaultsSaved(true);
            if (!myPreferences?.hasCustomPrintReceiptRedirect) {
                setPrintReceiptRedirect((prev) => normalizePrintReceiptRedirect({
                    ...prev,
                    delaySeconds: savedDefaults.delaySeconds,
                }));
            }
        } catch (error) {
            setPrintRedirectDefaultsSaved(false);
            setPrintRedirectDefaultsError(
                error?.response?.data?.message || 'No se pudo guardar el tiempo por defecto.'
            );
        } finally {
            setPrintRedirectDefaultsSaving(false);
        }
    };

    const handleSavePrintRedirectDefaults = () => {
        savePrintRedirectDefaults(printRedirectDefaults);
    };

    const handleResetPrintRedirectDefaults = () => {
        savePrintRedirectDefaults(DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS);
    };

    const handlePurchaseOrderReviewChange = (field, value) => {
        setPurchaseOrderReview((prev) => ({
            ...prev,
            [field]: value,
        }));
        setPurchaseOrderReviewSaved(false);
    };

    const handleSavePurchaseOrderReview = async () => {
        const normalizedReview = normalizePurchaseOrderReview(purchaseOrderReview);
        setPurchaseOrderReviewSaving(true);
        setPurchaseOrderReviewError('');
        try {
            const savedPreferences = await saveMyPreferences({
                purchaseOrderReview: normalizedReview,
            });
            setPurchaseOrderReview(
                normalizePurchaseOrderReview(savedPreferences?.purchaseOrderReview || normalizedReview)
            );
            setPurchaseOrderReviewSaved(true);
        } catch (error) {
            setPurchaseOrderReviewSaved(false);
            setPurchaseOrderReviewError(error?.response?.data?.message || 'No se pudo guardar la configuracion.');
        } finally {
            setPurchaseOrderReviewSaving(false);
        }
    };

    const handleResetPurchaseOrderReview = async () => {
        setPurchaseOrderReviewSaving(true);
        setPurchaseOrderReviewError('');
        try {
            const savedPreferences = await saveMyPreferences({
                purchaseOrderReview: DEFAULT_PURCHASE_ORDER_REVIEW,
            });
            setPurchaseOrderReview(
                normalizePurchaseOrderReview(savedPreferences?.purchaseOrderReview || DEFAULT_PURCHASE_ORDER_REVIEW)
            );
            setPurchaseOrderReviewSaved(false);
        } catch (error) {
            setPurchaseOrderReviewError(error?.response?.data?.message || 'No se pudo restablecer la configuracion.');
        } finally {
            setPurchaseOrderReviewSaving(false);
        }
    };

    const handleSavePrintMode = async () => {
        const normalizedRedirect = normalizePrintReceiptRedirect(printReceiptRedirect);
        setPrintSettingsSaving(true);
        setPrintSettingsError('');
        try {
            const savedMode = savePrintMode(printMode);
            const savedPreferences = await saveMyPreferences({
                printReceiptRedirect: normalizedRedirect,
            });
            setPrintMode(savedMode);
            setPrintReceiptRedirect(
                normalizePrintReceiptRedirect(savedPreferences?.printReceiptRedirect || normalizedRedirect)
            );
            setPrintModeSaved(true);
        } catch (error) {
            setPrintModeSaved(false);
            setPrintSettingsError(error?.response?.data?.message || 'No se pudo guardar la configuracion.');
        } finally {
            setPrintSettingsSaving(false);
        }
    };

    const handleResetPrintMode = async () => {
        setPrintSettingsSaving(true);
        setPrintSettingsError('');
        try {
            const savedMode = savePrintMode(DEFAULT_PRINT_MODE);
            const savedPreferences = await saveMyPreferences({
                printReceiptRedirect: null,
            });
            setPrintMode(savedMode);
            setPrintReceiptRedirect(
                normalizePrintReceiptRedirect(savedPreferences?.printReceiptRedirect || DEFAULT_PRINT_RECEIPT_REDIRECT)
            );
            setPrintModeSaved(false);
        } catch (error) {
            setPrintSettingsError(error?.response?.data?.message || 'No se pudo restablecer la configuracion.');
        } finally {
            setPrintSettingsSaving(false);
        }
    };

    const fetchTaxes = async () => {
        if (!canReadTaxes) {
            setTaxes([]);
            setTaxesError('No tienes permisos para ver impuestos.');
            return;
        }
        setTaxesLoading(true);
        setTaxesError('');
        try {
            const data = await getTaxes();
            setTaxes(Array.isArray(data) ? data : []);
        } catch (err) {
            setTaxes([]);
            setTaxesError(err.response?.data?.message || 'No se pudieron cargar los impuestos.');
        } finally {
            setTaxesLoading(false);
        }
    };

    const fetchBranchTaxes = async () => {
        if (!canReadTaxes || !activeBranches.length) return;
        setBranchTaxLoading(true);
        setBranchTaxError('');
        try {
            const results = await Promise.all(
                activeBranches.map(async (branch) => ({
                    branchId: branch.id,
                    taxes: await getBranchTaxes(branch.id),
                }))
            );
            const nextMap = {};
            results.forEach((result) => {
                const branchMap = {};
                (result.taxes || []).forEach((tax) => {
                    branchMap[tax.id] = !!tax.branchActive;
                });
                nextMap[result.branchId] = branchMap;
            });
            setBranchTaxMap(nextMap);
        } catch (err) {
            setBranchTaxError(err.response?.data?.message || 'No se pudieron cargar los impuestos por sucursal.');
        } finally {
            setBranchTaxLoading(false);
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'catalog-product-search') return;
        setProductSearchModeDraft(normalizeProductSearchMode(productSearchMode));
        setProductSearchError('');
        setProductSearchSaved(false);
    }, [activeSectionConfig?.id, productSearchMode]);

    const handleProductSearchModeChange = (mode) => {
        setProductSearchModeDraft(normalizeProductSearchMode(mode));
        setProductSearchError('');
        setProductSearchSaved(false);
    };

    const handleSaveProductSearchSettings = async () => {
        if (!canManageGlobalSettings) {
            setProductSearchError('No tienes permisos para guardar esta configuracion.');
            return;
        }
        setProductSearchSaving(true);
        setProductSearchError('');
        try {
            const saved = await saveProductSearchSettings({
                mode: normalizeProductSearchMode(productSearchModeDraft),
            });
            setProductSearchModeDraft(normalizeProductSearchMode(saved?.mode));
            setProductSearchSaved(true);
        } catch (err) {
            setProductSearchSaved(false);
            setProductSearchError(err.response?.data?.message || 'No se pudo guardar la configuracion de busqueda.');
        } finally {
            setProductSearchSaving(false);
        }
    };

    const fetchPosSoldOutDisplaySettings = async () => {
        setPosSoldOutLoading(true);
        setPosSoldOutError('');
        try {
            const settings = await getPosSoldOutDisplaySettings();
            setPosSoldOutMode(normalizePosSoldOutMode(settings?.soldOutMode));
            setPosSearchSuggestions(normalizePosSearchSuggestionVisibility(settings?.searchSuggestions));
            setPosSoldOutSaved(false);
        } catch (err) {
            setPosSoldOutMode(POS_SOLD_OUT_MODE_NORMAL);
            setPosSearchSuggestions(DEFAULT_POS_SEARCH_SUGGESTION_VISIBILITY);
            setPosSoldOutError(err.response?.data?.message || 'No se pudo cargar la configuracion POS.');
        } finally {
            setPosSoldOutLoading(false);
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'catalog-pos-sales') return;
        fetchPosSoldOutDisplaySettings();
    }, [activeSectionConfig?.id]);

    const handlePosSoldOutModeChange = (mode) => {
        setPosSoldOutMode(normalizePosSoldOutMode(mode));
        setPosSoldOutSaved(false);
        setPosSoldOutError('');
    };

    const handlePosSearchSuggestionChange = (device, enabled) => {
        setPosSearchSuggestions((previous) => ({
            ...normalizePosSearchSuggestionVisibility(previous),
            [device]: Boolean(enabled),
        }));
        setPosSoldOutSaved(false);
        setPosSoldOutError('');
    };

    const handleSavePosSoldOutDisplaySettings = async () => {
        if (!canManageGlobalSettings) {
            setPosSoldOutError('No tienes permisos para guardar esta configuracion POS.');
            return;
        }
        setPosSoldOutSaving(true);
        setPosSoldOutError('');
        try {
            const savedSettings = await updatePosSoldOutDisplaySettings({
                soldOutMode: normalizePosSoldOutMode(posSoldOutMode),
                searchSuggestions: normalizePosSearchSuggestionVisibility(posSearchSuggestions),
            });
            setPosSoldOutMode(normalizePosSoldOutMode(savedSettings?.soldOutMode));
            setPosSearchSuggestions(normalizePosSearchSuggestionVisibility(savedSettings?.searchSuggestions));
            setPosSoldOutSaved(true);
        } catch (err) {
            setPosSoldOutSaved(false);
            setPosSoldOutError(err.response?.data?.message || 'No se pudo guardar la configuracion POS.');
        } finally {
            setPosSoldOutSaving(false);
        }
    };

    const normalizePriceRecommendationSettingsForForm = (settings) => {
        const option = getPriceRecommendationRoundingOption(settings?.rounding);
        return {
            autoUpdatePrices: settings?.autoUpdatePrices !== false,
            rounding: {
                mode: option.mode,
                unit: option.unit,
                threshold: settings?.rounding?.threshold !== undefined && settings?.rounding?.threshold !== null
                    ? String(settings.rounding.threshold)
                    : option.defaultThreshold,
            },
        };
    };

    const fetchPriceRecommendationSettings = async () => {
        const requestId = ++priceRecommendationRequestRef.current;
        const isCurrent = () => requestId === priceRecommendationRequestRef.current
            && activeBranchId === priceRecommendationBranchRef.current;
        setPriceRecommendationLoadedBranch(null);
        setPriceRecommendationSettings(emptyPriceRecommendationSettings());
        setPriceRecommendationSaved(false);
        setPriceRecommendationSaving(false);
        if (!activeBranchId || !canReadPrices) {
            return;
        }
        setPriceRecommendationLoading(true);
        setPriceRecommendationError('');
        try {
            const settings = await getPriceRecommendationSettings(activeBranchId);
            if (!isCurrent()) return;
            setPriceRecommendationSettings(normalizePriceRecommendationSettingsForForm(settings));
            setPriceRecommendationLoadedBranch(activeBranchId);
            setPriceRecommendationSaved(false);
        } catch (err) {
            if (!isCurrent()) return;
            setPriceRecommendationError(err.response?.data?.message || 'No se pudieron cargar las recomendaciones.');
        } finally {
            if (isCurrent()) setPriceRecommendationLoading(false);
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'catalog-price-recommendations') return;
        fetchPriceRecommendationSettings();
        return () => { priceRecommendationRequestRef.current += 1; };
    }, [activeSectionConfig?.id, activeBranchId, canReadPrices]);

    const updatePriceRecommendationRounding = (field, value) => {
        setPriceRecommendationSettings((prev) => ({
            ...prev,
            rounding: {
                ...prev.rounding,
                [field]: value,
            },
        }));
        setPriceRecommendationSaved(false);
        setPriceRecommendationError('');
    };

    const updatePriceRecommendationRoundingUnit = (unitValue) => {
        const option = getPriceRecommendationRoundingOption({ unit: unitValue });
        setPriceRecommendationSettings((prev) => {
            const currentOption = getPriceRecommendationRoundingOption(prev.rounding);
            return {
                ...prev,
                rounding: {
                    ...prev.rounding,
                    mode: option.mode,
                    unit: option.unit,
                    threshold: currentOption.unit === option.unit
                        ? prev.rounding?.threshold ?? option.defaultThreshold
                        : option.defaultThreshold,
                },
            };
        });
        setPriceRecommendationSaved(false);
        setPriceRecommendationError('');
    };

    const updatePriceRecommendationMode = (autoUpdatePrices) => {
        setPriceRecommendationSettings((prev) => ({
            ...prev,
            autoUpdatePrices,
        }));
        setPriceRecommendationSaved(false);
        setPriceRecommendationError('');
    };

    const buildPriceRecommendationPayload = () => {
        const option = getPriceRecommendationRoundingOption(priceRecommendationSettings.rounding);
        return {
            autoUpdatePrices: priceRecommendationSettings.autoUpdatePrices !== false,
            rounding: {
                mode: option.mode,
                unit: option.unit,
                threshold: Number(priceRecommendationSettings.rounding?.threshold || option.defaultThreshold),
            },
        };
    };

    const validatePriceRecommendationPayload = (payload) => {
        const option = getPriceRecommendationRoundingOption(payload.rounding);
        const threshold = Number(payload.rounding.threshold);
        if (!Number.isFinite(threshold) || threshold < 0 || threshold > option.maxThreshold) {
            return `El umbral de redondeo debe estar entre 0 y ${option.maxThreshold}.`;
        }
        return '';
    };

    const handleSavePriceRecommendationSettings = async () => {
        if (!activeBranchId || !canUpdatePrices || priceRecommendationLoadedBranch !== activeBranchId) return;
        const requestId = priceRecommendationRequestRef.current;
        const isCurrent = () => requestId === priceRecommendationRequestRef.current
            && activeBranchId === priceRecommendationBranchRef.current;
        const payload = buildPriceRecommendationPayload();
        const validationError = validatePriceRecommendationPayload(payload);
        if (validationError) {
            setPriceRecommendationError(validationError);
            setPriceRecommendationSaved(false);
            return;
        }
        setPriceRecommendationSaving(true);
        setPriceRecommendationError('');
        try {
            const savedSettings = await updatePriceRecommendationSettings(activeBranchId, payload);
            if (!isCurrent()) return;
            setPriceRecommendationSettings(normalizePriceRecommendationSettingsForForm(savedSettings));
            setPriceRecommendationSaved(true);
        } catch (err) {
            if (!isCurrent()) return;
            setPriceRecommendationSaved(false);
            setPriceRecommendationError(err.response?.data?.message || 'No se pudieron guardar las recomendaciones.');
        } finally {
            if (isCurrent()) setPriceRecommendationSaving(false);
        }
    };

    const closeClientPricingCategoryModal = () => {
        if (clientPricingCategorySaving) return;
        setClientPricingCategoryModalOpen(false);
        setClientPricingEditingId(null);
        setClientPricingCategoriesError('');
    };

    const handleOpenClientPricingCategoryCreate = () => {
        if (!canManageClientPricing) return;
        setClientPricingEditingId(null);
        setClientPricingCategoriesError('');
        setClientPricingCategoryModalOpen(true);
    };

    const handleSubmitClientPricingCategory = async (form) => {
        if (!canManageClientPricing) return;
        const name = String(form?.name || '').trim();
        if (!name) {
            setClientPricingCategoriesError('Ingresa un nombre para la categoria.');
            return;
        }
        setClientPricingCategorySaving(true);
        setClientPricingCategoriesError('');
        try {
            const payload = {
                name,
                groupId: form?.groupId ?? null,
                ...(form?.marginReductionPercent !== undefined ? { marginReductionPercent: form.marginReductionPercent } : {}),
                description: form?.description,
                isActive: form?.isActive === true,
                applicableBranchIds: form?.applicableBranchIds === null
                    ? null
                    : Array.isArray(form?.applicableBranchIds)
                    ? form.applicableBranchIds
                    : [],
                applicableCategoryIds: form?.applicableCategoryIds === null
                    ? null
                    : Array.isArray(form?.applicableCategoryIds)
                    ? form.applicableCategoryIds
                    : [],
                applicableProductFormatCodes: form?.applicableProductFormatCodes === null
                    ? null
                    : Array.isArray(form?.applicableProductFormatCodes)
                    ? form.applicableProductFormatCodes
                    : [],
            };
            const response = clientPricingEditingId
                ? await updateClientPricingCategory(clientPricingEditingId, payload)
                : await createClientPricingCategory(payload);
            const savedCategoryId = response?.category?.id || clientPricingEditingId;
            const shouldReloadSelectedCatalog = clientPricingEditingId
                && String(clientPricingEditingId) === String(selectedClientPricingCategoryId);
            await loadClientPricingCategories();
            if (shouldReloadSelectedCatalog) await loadClientPricingDiscountCatalog();
            setClientPricingCategoryModalOpen(false);
            setClientPricingEditingId(null);
            if (savedCategoryId) selectClientPricingCategory(String(savedCategoryId));
        } catch (err) {
            setClientPricingCategoriesError(
                err?.response?.data?.message || 'No se pudo guardar la categoria.'
            );
        } finally {
            setClientPricingCategorySaving(false);
        }
    };

    const handleEditClientPricingCategory = (category) => {
        setClientPricingEditingId(category?.id || null);
        setClientPricingCategoriesError('');
        setClientPricingCategoryModalOpen(true);
    };

    const handleDeleteClientPricingCategory = async (category) => {
        const categoryId = category?.id;
        if (!canManageClientPricing || !categoryId) return;
        const confirmed = await confirm({
            title: 'Eliminar categoria',
            message: `Se eliminara "${category.name || 'esta categoria'}", sus descuentos y se quitara de los clientes que la usen.`,
            confirmLabel: 'Eliminar',
            cancelLabel: 'Cancelar',
            tone: 'danger',
        });
        if (!confirmed) return;

        setClientPricingCategoryDeletingId(categoryId);
        setClientPricingCategoriesError('');
        try {
            await deleteClientPricingCategory(categoryId);
            if (String(clientPricingEditingId || '') === String(categoryId)) {
                setClientPricingCategoryModalOpen(false);
                setClientPricingEditingId(null);
            }
            if (String(selectedClientPricingCategoryId || '') === String(categoryId)) {
                selectClientPricingCategory('', { replace: true });
                setClientPricingDiscountRows([]);
            }
            await loadClientPricingCategories();
            setClientPricingDiscountsSaved(false);
        } catch (err) {
            setClientPricingCategoriesError(
                err?.response?.data?.message || 'No se pudo eliminar la categoria.'
            );
        } finally {
            setClientPricingCategoryDeletingId(null);
        }
    };

    const handleSaveClientPricingSettings = async () => {
        if (!canManageClientPricing) return;
        setClientPricingSettingsSaving(true);
        setClientPricingSettingsError('');
        setClientPricingSettingsSaved(false);
        try {
            const data = await updateClientPricingSettings(clientPricingSettings);
            const savedSettings = data?.settings || data;
            setClientPricingSettings({
                mode: [
                    CLIENT_PRICING_MODE_COST_PLUS,
                    CLIENT_PRICING_MODE_DISCOUNT,
                    CLIENT_PRICING_MODE_MARGIN_DISCOUNT,
                ].includes(savedSettings?.mode) ? savedSettings.mode : CLIENT_PRICING_MODE_COST_PLUS,
                marginPercentDisplay: savedSettings?.marginPercentDisplay === CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
                    ? CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
                    : CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
            });
            if (data?.conversionSummary) setClientPricingConversionSummary(data.conversionSummary);
            setClientPricingSettingsSaved(true);
            if (savedSettings?.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT) {
                await Promise.all([
                    loadClientPricingConversionIssues(),
                    selectedClientPricingCategoryId
                        ? loadClientPricingDiscountCatalog()
                        : Promise.resolve(),
                ]);
            } else {
                setClientPricingConversionSummary(null);
                setClientPricingConversionIssues([]);
                setClientPricingConversionIssueTotal(0);
                setClientPricingConversionIssuePage(1);
                setClientPricingConversionError('');
            }
        } catch (err) {
            setClientPricingSettingsError(
                err?.response?.data?.message || 'No se pudo guardar la configuracion de precios por cliente.'
            );
        } finally {
            setClientPricingSettingsSaving(false);
        }
    };

    const handleRetryClientPricingConversions = async () => {
        if (!canManageClientPricing || clientPricingConversionRetrying) return;
        setClientPricingConversionRetrying(true);
        setClientPricingConversionError('');
        try {
            const data = await retryClientPricingMarginConversions();
            if (data?.conversionSummary || data?.summary) {
                setClientPricingConversionSummary(data.conversionSummary || data.summary);
            }
            await Promise.all([
                loadClientPricingConversionIssues(),
                selectedClientPricingCategoryId ? loadClientPricingDiscountCatalog() : Promise.resolve(),
            ]);
        } catch (err) {
            setClientPricingConversionError(
                err?.response?.data?.message || 'No se pudo reintentar la conversión.'
            );
        } finally {
            setClientPricingConversionRetrying(false);
        }
    };

    const saveClientPricingRow = useCallback(async (row) => {
        if (!canManageClientPricing || !selectedClientPricingCategoryId || !row?.varietyPriceId) return;
        const varietyPriceId = row.varietyPriceId;
        const marginRule = isClientPricingMarginRule(row);
        const payload = marginRule ? {
            branchId: clientPricingReferenceBranchId,
            ruleType: CLIENT_PRICING_RULE_MARGIN,
            marginReductionPercent: row.marginReductionPercent ?? '',
        } : {
            branchId: clientPricingReferenceBranchId,
            ruleType: CLIENT_PRICING_RULE_FIXED,
            discountType: row.discountType || 'amount',
            discountValue: row.discountValue || 0,
            ...(row.replaceExistingRule === true ? { replaceExistingRule: true } : {}),
        };
        setClientPricingRowSaving((prev) => ({ ...prev, [varietyPriceId]: true }));
        setClientPricingRowSaveStatus((prev) => ({ ...prev, [varietyPriceId]: 'saving' }));
        setClientPricingDiscountsError('');
        try {
            const result = await updateClientPricingDiscount(selectedClientPricingCategoryId, varietyPriceId, payload);
            setClientPricingDiscountRows((rows) => rows.map((currentRow) => {
                if (Number(currentRow.varietyPriceId) !== Number(varietyPriceId)) return currentRow;
                const stillSameDraft = marginRule
                    ? isClientPricingMarginRule(currentRow)
                        && String(currentRow.marginReductionPercent ?? '') === String(payload.marginReductionPercent ?? '')
                    : !isClientPricingMarginRule(currentRow)
                        && String(currentRow.discountType || 'amount') === String(payload.discountType)
                        && String(currentRow.discountValue || 0) === String(payload.discountValue || 0);
                const savedRow = result?.row || result?.discount || {};
                const wasCleared = Object.prototype.hasOwnProperty.call(result || {}, 'discount')
                    && result.discount === null;
                return stillSameDraft ? {
                    ...currentRow,
                    ...(wasCleared ? {
                        ruleType: CLIENT_PRICING_RULE_FIXED,
                        marginReductionPercent: null,
                        discountType: 'amount',
                        discountValue: 0,
                        conversionIssue: null,
                    } : savedRow),
                    isDirty: false,
                    replaceExistingRule: undefined,
                } : currentRow;
            }));
            setClientPricingRowSaveStatus((prev) => ({ ...prev, [varietyPriceId]: 'saved' }));
            setClientPricingDiscountsSaved(true);
        } catch (err) {
            setClientPricingDiscountsSaved(false);
            setClientPricingRowSaveStatus((prev) => ({ ...prev, [varietyPriceId]: 'error' }));
            setClientPricingDiscountsError(
                err?.response?.data?.message || 'No se pudo guardar el descuento.'
            );
        } finally {
            setClientPricingRowSaving((prev) => ({ ...prev, [varietyPriceId]: false }));
        }
    }, [
        canManageClientPricing,
        clientPricingReferenceBranchId,
        selectedClientPricingCategoryId,
    ]);

    const handleClientPricingRowCommit = useCallback(async (varietyPriceId, patch = {}, nextRow = {}) => {
        const id = Number(varietyPriceId);
        if (!Number.isInteger(id)) return;
        const currentRow = clientPricingDiscountRows.find((row) => Number(row.varietyPriceId) === id) || {};
        const committedRow = {
            ...currentRow,
            ...nextRow,
            ...patch,
            varietyPriceId: id,
            isDirty: false,
            ...(patch?.ruleType === CLIENT_PRICING_RULE_FIXED ? { marginReductionPercent: null } : {}),
        };
        setClientPricingDiscountRows((rows) => rows.map((row) => (
            Number(row.varietyPriceId) === id ? committedRow : row
        )));
        setClientPricingDiscountsSaved(false);
        await saveClientPricingRow(committedRow);
    }, [clientPricingDiscountRows, saveClientPricingRow]);

    const handleClientPricingPendingCostChange = useCallback((varietyPriceId, field, value) => {
        const id = Number(varietyPriceId);
        if (!Number.isInteger(id)) return;
        setClientPricingPendingCostValues((previous) => ({
            ...previous,
            [id]: {
                ...(previous[id] || {}),
                [field]: value,
            },
        }));
        setClientPricingPendingCostErrors((previous) => ({ ...previous, [id]: '' }));
    }, []);

    const handleSaveClientPricingUnitWeight = useCallback(async (row, unitWeightKg) => {
        const varietyPriceId = Number(row?.varietyPriceId);
        if (!Number.isInteger(varietyPriceId) || !clientPricingReferenceBranchId) {
            throw new Error('No se pudo identificar el formato o la sucursal.');
        }
        if (!canUpdateProduct) {
            throw new Error('No tienes permiso para editar el peso del formato.');
        }
        const parsedUnitWeightKg = Number(unitWeightKg);
        if (!Number.isFinite(parsedUnitWeightKg) || parsedUnitWeightKg <= 0) {
            throw new Error('Ingresa un peso mayor a 0.');
        }
        await updateVarietyFormat(clientPricingReferenceBranchId, varietyPriceId, {
            unitWeightKg: parsedUnitWeightKg,
        });
        await loadClientPricingDiscountCatalog();
    }, [canUpdateProduct, clientPricingReferenceBranchId, loadClientPricingDiscountCatalog]);

    const handleSaveClientPricingPendingCost = useCallback(async (row) => {
        const varietyPriceId = Number(row?.varietyPriceId);
        const draft = clientPricingPendingCostValues[varietyPriceId] || {};
        if (!Number.isInteger(varietyPriceId)) return;
        if (!canManageClientPricing) {
            setClientPricingPendingCostErrors((previous) => ({
                ...previous,
                [varietyPriceId]: 'No tienes permiso para administrar precios por cliente.',
            }));
            return;
        }
        const ruleType = draft.ruleType || row?.ruleType || 'amount';
        const ruleValue = Number(draft.ruleValue ?? row?.ruleValue);
        if (!['amount', 'percent'].includes(ruleType) || !Number.isFinite(ruleValue) || ruleValue <= 0) {
            setClientPricingPendingCostErrors((previous) => ({
                ...previous,
                [varietyPriceId]: 'Ingresa un valor de regla mayor a 0.',
            }));
            return;
        }
        if (!selectedClientPricingCategoryId || !clientPricingReferenceBranchId) {
            setClientPricingPendingCostErrors((previous) => ({
                ...previous,
                [varietyPriceId]: 'La regla masiva ya no esta disponible. Vuelve a aplicarla.',
            }));
            return;
        }

        setClientPricingPendingCostSaving((previous) => ({ ...previous, [varietyPriceId]: true }));
        setClientPricingPendingCostErrors((previous) => ({ ...previous, [varietyPriceId]: '' }));
        const savedReferenceData = [];
        try {
            const canEditWeight = row?.reason === 'missing_weight'
                || row?.referenceCost?.calculationMethod === 'unit_weight_kg';
            const canEditCost = row?.reason === 'missing_cost'
                || row?.referenceCost?.calculationMethod === 'estimated'
                || row?.referenceCost?.referenceCostSource === 'estimated';
            const hasWeightDraft = canEditWeight && String(draft.weight ?? '').trim() !== '';
            const hasCostDraft = canEditCost && String(draft.cost ?? '').trim() !== '';
            let unitWeightKg = null;
            let estimatedCost = null;
            if (row?.reason === 'missing_weight' && !canUpdateProduct) {
                throw new Error('No tienes permiso para editar el peso del formato.');
            }
            if (row?.reason === 'missing_cost' && !canUpdatePrices) {
                throw new Error('No tienes permiso para editar el costo del precio.');
            }
            if (hasWeightDraft && canUpdateProduct) {
                unitWeightKg = Number(draft.weight);
                if (!Number.isFinite(unitWeightKg) || unitWeightKg <= 0) {
                    throw new Error('Ingresa un peso mayor a 0.');
                }
            }
            if (hasCostDraft && canUpdatePrices) {
                const rawEstimatedCost = draft.cost;
                estimatedCost = Number(rawEstimatedCost);
                if (!Number.isInteger(estimatedCost) || estimatedCost < 0) {
                    throw new Error('Ingresa un costo entero mayor o igual a 0.');
                }
            }
            if (row?.reason === 'missing_weight' && !hasWeightDraft) {
                throw new Error('Ingresa un peso mayor a 0.');
            }
            if (row?.reason === 'missing_cost' && !hasCostDraft) {
                throw new Error('Ingresa un costo entero mayor o igual a 0.');
            }
            if (hasWeightDraft && canUpdateProduct) {
                await updateVarietyFormat(clientPricingReferenceBranchId, varietyPriceId, { unitWeightKg });
                savedReferenceData.push('weight');
            }
            if (hasCostDraft && canUpdatePrices) {
                await updatePriceListFormatSettings(clientPricingReferenceBranchId, varietyPriceId, {
                    shared: { manualBaseCost: estimatedCost },
                    branch: {},
                });
                savedReferenceData.push('cost');
            }
            const action = row?.action || clientPricingPendingRule?.action || 'set_benefit';
            const result = await bulkUpdateClientPricingDiscounts(selectedClientPricingCategoryId, {
                action,
                branchId: clientPricingReferenceBranchId,
                ...(action === 'set_benefit'
                    ? { benefitType: ruleType, benefitValue: ruleValue }
                    : { discountType: ruleType, discountValue: ruleValue }),
                varietyPriceIds: [varietyPriceId],
            });
            const nextPending = Array.isArray(result?.pendingRows)
                ? result.pendingRows[0] || null
                : Array.isArray(result?.pendingCosts)
                    ? result.pendingCosts[0] || null
                    : null;
            if (nextPending) {
                setClientPricingPendingCosts((previous) => previous.map((pendingRow) => (
                    Number(pendingRow.varietyPriceId) === varietyPriceId ? nextPending : pendingRow
                )));
                setClientPricingPendingCostValues((previous) => ({
                    ...previous,
                    [varietyPriceId]: {
                        ...draft,
                        ruleType,
                        ruleValue: String(ruleValue),
                    },
                }));
                await loadClientPricingDiscountCatalog();
                return;
            }
            setClientPricingPendingCosts((previous) => previous.filter(
                (pendingRow) => Number(pendingRow.varietyPriceId) !== varietyPriceId
            ));
            setClientPricingPendingCostValues((previous) => {
                const next = { ...previous };
                delete next[varietyPriceId];
                return next;
            });
            setClientPricingDiscountsSaved(true);
            await loadClientPricingDiscountCatalog();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'No se pudo corregir y aplicar el cambio.';
            const savedMessage = savedReferenceData.length === 2
                ? 'Peso y costo guardados.'
                : savedReferenceData[0] === 'weight'
                    ? 'Peso guardado.'
                    : savedReferenceData[0] === 'cost'
                        ? 'Costo guardado.'
                        : '';
            setClientPricingPendingCostErrors((previous) => ({
                ...previous,
                [varietyPriceId]: savedMessage ? `${savedMessage} ${message}` : message,
            }));
        } finally {
            setClientPricingPendingCostSaving((previous) => ({ ...previous, [varietyPriceId]: false }));
        }
    }, [
        clientPricingPendingCostValues,
        clientPricingPendingRule,
        canManageClientPricing,
        canUpdatePrices,
        canUpdateProduct,
        clientPricingReferenceBranchId,
        loadClientPricingDiscountCatalog,
        selectedClientPricingCategoryId,
    ]);


    const clientPricingBulkScopeRef = useRef(null);
    const clientPricingBulkScopeKey = `${selectedClientPricingCategoryId}:${clientPricingReferenceBranchId}`;
    if (clientPricingBulkScopeRef.current?.key !== clientPricingBulkScopeKey) clientPricingBulkScopeRef.current = { key: clientPricingBulkScopeKey };

    const handleBulkClientPricingDiscounts = async ({
        action = 'apply',
        selectedRows = [],
        selectedIds = [],
        discountType,
        discountValue,
        benefitType,
        benefitValue,
        ruleType,
        marginReductionPercent,
        replaceExistingRules,
    } = {}) => {
        if (!canManageClientPricing || !selectedClientPricingCategoryId) return;
        const ownerScope = clientPricingBulkScopeRef.current;
        const isCurrentBulk = () => clientPricingBulkScopeRef.current === ownerScope;
        const targetIds = selectedIds.length
            ? selectedIds
            : selectedRows.map((row) => Number(row?.varietyPriceId)).filter((id) => Number.isInteger(id));
        if (!targetIds.length) {
            setClientPricingDiscountsError('Selecciona al menos un precio.');
            return;
        }
        const costPlusAction = action === 'set_benefit';
        const marginAction = action === 'set_margin_discount' || ruleType === CLIENT_PRICING_RULE_MARGIN;
        const actionValue = marginAction
            ? marginReductionPercent
            : costPlusAction
                ? benefitValue ?? clientPricingBulkForm.benefitValue
                : discountValue ?? clientPricingBulkForm.discountValue;
        if (action !== 'clear' && (String(actionValue).trim() === '' || !Number.isFinite(Number(actionValue)))) {
            setClientPricingDiscountsError(
                marginAction
                    ? 'Ingresa un porcentaje de margen válido.'
                    : costPlusAction ? 'Ingresa un margen valido.' : 'Ingresa un descuento valido.'
            );
            return;
        }
        const confirmed = action === 'clear'
            ? await confirm({
                title: clientPricingSettings.mode === CLIENT_PRICING_MODE_COST_PLUS
                    ? 'Restablecer precios'
                    : 'Limpiar descuentos',
                message: clientPricingSettings.mode === CLIENT_PRICING_MODE_COST_PLUS
                    ? 'Los precios seleccionados volveran a su precio base.'
                    : 'Se limpiaran los descuentos de los precios seleccionados.',
                confirmLabel: 'Limpiar',
                cancelLabel: 'Cancelar',
                tone: 'danger',
            })
            : true;
        if (!confirmed) return;
        setClientPricingBulkSaving(true);
        setClientPricingDiscountsError('');
        try {
            const resolvedBenefitType = benefitType ?? clientPricingBulkForm.benefitType;
            const resolvedBenefitValue = benefitValue ?? clientPricingBulkForm.benefitValue;
            const resolvedDiscountType = discountType ?? clientPricingBulkForm.discountType;
            const resolvedDiscountValue = discountValue ?? clientPricingBulkForm.discountValue;
            const result = await runPartialPricingBulk(`client-discounts:${selectedClientPricingCategoryId}:${clientPricingReferenceBranchId}`, {
                action,
                branchId: clientPricingReferenceBranchId,
                search: clientPricingSearch.trim(),
                searchMode: productSearchMode,
                discountType: resolvedDiscountType,
                discountValue: resolvedDiscountValue,
                ruleType: marginAction ? CLIENT_PRICING_RULE_MARGIN : CLIENT_PRICING_RULE_FIXED,
                marginReductionPercent: marginAction ? marginReductionPercent : undefined,
                replaceExistingRules: replaceExistingRules === true,
                benefitType: resolvedBenefitType,
                benefitValue: resolvedBenefitValue,
                varietyPriceIds: targetIds,
            }, (payload) => bulkUpdateClientPricingDiscounts(selectedClientPricingCategoryId, payload));
            if (!isCurrentBulk()) return;
            setClientPricingBulkRefresh((current) => current + 1);
            const pendingCosts = costPlusAction && Array.isArray(result?.pendingRows)
                ? result.pendingRows
                : costPlusAction && Array.isArray(result?.pendingCosts)
                    ? result.pendingCosts
                    : [];
            setClientPricingPendingCosts(pendingCosts);
            setClientPricingPendingCostValues(Object.fromEntries(
                pendingCosts.map((row) => [Number(row.varietyPriceId), {
                    cost: row?.referenceCost?.referenceCostSource === 'estimated'
                        || row?.referenceCost?.calculationMethod === 'estimated'
                        ? String(row?.referenceCost?.referenceCost ?? '')
                        : '',
                    weight: row?.referenceCost?.unitWeightKg ?? '',
                    ruleType: row?.ruleType || (costPlusAction ? resolvedBenefitType : resolvedDiscountType) || 'amount',
                    ruleValue: String(row?.ruleValue ?? (costPlusAction ? resolvedBenefitValue : resolvedDiscountValue) ?? ''),
                }])
            ));
            setClientPricingPendingCostSaving({});
            setClientPricingPendingCostErrors({});
            setClientPricingPendingRule(costPlusAction && pendingCosts.length
                ? {
                    action,
                    ruleType: resolvedBenefitType || 'amount',
                    ruleValue: resolvedBenefitValue,
                }
                : pendingCosts.length
                    ? {
                        action,
                        ruleType: resolvedDiscountType || 'amount',
                        ruleValue: resolvedDiscountValue,
                    }
                    : null);
            await loadClientPricingDiscountCatalog();
            setClientPricingDiscountsSaved(true);
        } catch (err) {
            if (!isCurrentBulk()) return;
            setClientPricingDiscountsSaved(false);
            setClientPricingDiscountsError(
                err?.response?.data?.message || 'No se pudo aplicar la accion masiva.'
            );
        } finally {
            if (isCurrentBulk()) setClientPricingBulkSaving(false);
        }
    };

    const handleCreateTax = async () => {
        if (!canManageTaxes) return;
        const name = String(taxForm.name || '').trim();
        const rate = Number(taxForm.rate);
        if (!name || !Number.isFinite(rate)) {
            setTaxesError('Completa el nombre y una tasa valida.');
            return;
        }
        setTaxSaving(true);
        setTaxesError('');
        try {
            await createTax({
                name,
                rate,
                applyToAllProducts: !!taxForm.applyToAllProducts,
                isActive: !!taxForm.isActive,
                isIncludedInPrice: true,
            });
            setTaxForm({ name: '', rate: '', applyToAllProducts: true, isActive: true });
            await fetchTaxes();
            await fetchBranchTaxes();
        } catch (err) {
            setTaxesError(err.response?.data?.message || 'No se pudo crear el impuesto.');
        } finally {
            setTaxSaving(false);
        }
    };

    const handleTaxEditChange = (taxId, field, value) => {
        setTaxEdits((prev) => ({
            ...prev,
            [taxId]: {
                ...prev[taxId],
                [field]: value,
            },
        }));
    };

    const handleSaveTax = async (taxId) => {
        if (!canManageTaxes) return;
        const draft = taxEdits[taxId];
        if (!draft) return;
        const name = String(draft.name || '').trim();
        const rate = Number(draft.rate);
        if (!name || !Number.isFinite(rate)) {
            setTaxesError('Completa el nombre y una tasa valida.');
            return;
        }
        setTaxSaving(true);
        setTaxesError('');
        try {
            await updateTax(taxId, {
                name,
                rate,
                applyToAllProducts: !!draft.applyToAllProducts,
                isActive: !!draft.isActive,
                isIncludedInPrice: true,
            });
            await fetchTaxes();
            await fetchBranchTaxes();
        } catch (err) {
            setTaxesError(err.response?.data?.message || 'No se pudo actualizar el impuesto.');
        } finally {
            setTaxSaving(false);
        }
    };

    const handleBranchTaxToggle = (branchId, taxId) => {
        setBranchTaxMap((prev) => ({
            ...prev,
            [branchId]: {
                ...(prev[branchId] || {}),
                [taxId]: !(prev[branchId] || {})[taxId],
            },
        }));
        setBranchTaxDirty((prev) => ({ ...prev, [branchId]: true }));
    };

    const handleSaveBranchTaxes = async (branchId) => {
        if (!canManageTaxes) return;
        const branchMap = branchTaxMap[branchId] || {};
        const activeTaxIds = taxes.filter((tax) => branchMap[tax.id]).map((tax) => tax.id);
        setBranchTaxSaving((prev) => ({ ...prev, [branchId]: true }));
        try {
            const result = await updateBranchTaxes(branchId, { activeTaxIds });
            const updated = Array.isArray(result?.taxes) ? result.taxes : [];
            const nextBranchMap = {};
            updated.forEach((tax) => {
                nextBranchMap[tax.id] = !!tax.branchActive;
            });
            setBranchTaxMap((prev) => ({ ...prev, [branchId]: nextBranchMap }));
            setBranchTaxDirty((prev) => ({ ...prev, [branchId]: false }));
        } catch (err) {
            setBranchTaxError(err.response?.data?.message || 'No se pudieron guardar los impuestos.');
        } finally {
            setBranchTaxSaving((prev) => ({ ...prev, [branchId]: false }));
        }
    };

    const handleBranchSelect = (branchId) => {
        const nextId = String(branchId || '');
        if (!nextId || nextId === String(activeBranchId)) return;
        setActiveBranchId(nextId);
        if (activeTopicConfig && activeSectionConfig) {
            const nextSearch = buildSettingsBranchSearch(location.search, nextId);
            navigate(
                buildSettingsPath(activeTopicConfig.id, activeSectionConfig.id, nextSearch),
                { replace: true }
            );
        }
    };

    const sourceBranchOptions = activeBranches.filter(
        (branch) => String(branch.id) !== String(activeBranchId)
    );
    const isSyncEnabled = Boolean(priceSync.sourceBranchId);
    const canViewPriceSync = canReadGlobalSettings;
    const canEditPriceSync = canManageGlobalSettings && canUpdateBranch;

    const renderBranchTabs = () => {
        if (!isBranchScopedSection) return null;
        if (branchLoading) {
            return (
                <div className="text-sm text-gray-500">
                    <InlineLoading label="Cargando sucursales" />
                </div>
            );
        }
        if (!activeBranchId && !activeBranches.length) {
            return (
                <div className="text-sm text-gray-500">
                    Selecciona una sucursal para ver esta configuracion.
                </div>
            );
        }
        if (!canReadBranches || !activeBranches.length) {
            return (
                <div className="text-xs text-gray-500">
                    Sucursal actual: <span className="font-medium text-gray-700">{branchLabel}</span>
                </div>
            );
        }
        return (
            <HorizontalPillTabs
                items={activeBranches.map((branch) => ({
                    id: branch.id,
                    label: branch.branchName,
                }))}
                activeId={activeBranchId}
                onSelect={handleBranchSelect}
            />
        );
    };

    const renderSectionScope = () => {
        if (!activeSectionConfig) return null;
        const scope = SETTINGS_SECTION_SCOPES[activeSectionConfig.id];
        if (!scope) return null;
        return (
            <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900">{activeSectionConfig.label}</h2>
                    {activeSectionConfig.description && (
                        <p className="mt-1 text-xs text-gray-500">{activeSectionConfig.description}</p>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${scope.className}`}>
                        Afecta: {scope.label}
                    </span>
                    {activeSectionConfig.branchScoped && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
                            Sucursal: {branchLabel}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderUsersAccess = () => {
        if (!canReadUsers) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver usuarios.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-xs text-gray-500">
                        Gestiona roles y accesos desde esta vista.
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {canReadBranches && (
                            <button
                                type="button"
                                onClick={() => goToSettingsSection('branch-directory', 'branches')}
                                className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                            >
                                Ir a sucursales
                            </button>
                        )}
                        {canReadCategories && (
                            <button
                                type="button"
                                onClick={() => goToSettingsSection('catalog-categories', 'products')}
                                className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                            >
                                Ir a categorias
                            </button>
                        )}
                    </div>
                </div>
                <BranchUsersPage embedded globalView />
            </div>
        );
    };

    const renderBranchDirectory = () => <BranchSettingsPanel onChanged={fetchBranches} />;

    const renderPurchaseOrderReasons = () => {
        if (!canReadPurchaseOrderSettings) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para administrar motivos de ordenes.
                </div>
            );
        }

        return (
            <div className="border rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-md font-semibold">Motivos de ajuste de ordenes</h2>
                        <p className="text-xs text-gray-500">
                            Se usan al reducir, eliminar o aumentar items en ordenes de compra de clientes.
                        </p>
                    </div>
                    {canManagePurchaseOrderReasons && (
                        <button
                            type="button"
                            onClick={handleSavePurchaseOrderReasons}
                            disabled={purchaseOrderReasonsSaving || purchaseOrderReasonsLoading}
                            className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800 disabled:opacity-60"
                        >
                            {purchaseOrderReasonsSaving ? 'Guardando...' : 'Guardar'}
                        </button>
                    )}
                </div>

                {purchaseOrderReasonsLoading ? (
                    <InlineLoading label="Cargando motivos" />
                ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                        {PURCHASE_ORDER_REASON_GROUPS.map((group) => (
                            <div key={group.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <div className="mb-3 flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-semibold text-gray-800">{group.label}</h3>
                                    {canManagePurchaseOrderReasons && (
                                        <button
                                            type="button"
                                            onClick={() => addPurchaseOrderReason(group.id)}
                                            className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                                        >
                                            Agregar
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {(purchaseOrderReasons[group.id] || []).map((reason, index) => (
                                        <div key={`${group.id}-${index}`} className="rounded border border-gray-200 bg-white p-2">
                                            <div className="grid grid-cols-1 gap-2">
                                                <input
                                                    value={reason.label || ''}
                                                    onChange={(event) => updatePurchaseOrderReason(group.id, index, { label: event.target.value })}
                                                    placeholder="Motivo"
                                                    disabled={!canManagePurchaseOrderReasons}
                                                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                                                />
                                                <input
                                                    value={reason.id || ''}
                                                    onChange={(event) => updatePurchaseOrderReason(group.id, index, { id: event.target.value })}
                                                    placeholder="id"
                                                    disabled={!canManagePurchaseOrderReasons}
                                                    className="rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-600"
                                                />
                                                <div className="flex items-center justify-between gap-2">
                                                    <label className="flex items-center gap-2 text-xs text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={reason.active !== false}
                                                            onChange={(event) => updatePurchaseOrderReason(group.id, index, { active: event.target.checked })}
                                                            disabled={!canManagePurchaseOrderReasons}
                                                        />
                                                        Activo
                                                    </label>
                                                    {canManagePurchaseOrderReasons && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removePurchaseOrderReason(group.id, index)}
                                                            className="rounded bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100"
                                                        >
                                                            Quitar
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {!(purchaseOrderReasons[group.id] || []).length && (
                                        <p className="text-xs text-gray-500">Sin motivos configurados.</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {purchaseOrderReasonsError && (
                    <p className="text-sm text-red-600 mt-3">{purchaseOrderReasonsError}</p>
                )}
                {purchaseOrderReasonsSaved && !purchaseOrderReasonsError && (
                    <p className="text-sm text-green-600 mt-3">Motivos guardados.</p>
                )}
            </div>
        );
    };

    const renderPurchaseOrderNotifications = () => {
        if (!canReadPurchaseOrderSettings) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para administrar notificaciones de ordenes.
                </div>
            );
        }
        const selectedRoleNames = new Set(purchaseOrderNotificationSettings.recipientRoleNames || []);

        return (
            <div className="border rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-md font-semibold">Notificaciones push de ordenes</h2>
                        <p className="text-xs text-gray-500">
                            Define quien recibe avisos del navegador cuando un cliente crea una orden de compra.
                        </p>
                    </div>
                    {canManagePurchaseOrderReasons && (
                        <button
                            type="button"
                            onClick={handleSavePurchaseOrderNotifications}
                            disabled={purchaseOrderNotificationsSaving || purchaseOrderNotificationsLoading}
                            className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800 disabled:opacity-60"
                        >
                            {purchaseOrderNotificationsSaving ? 'Guardando...' : 'Guardar'}
                        </button>
                    )}
                </div>

                {purchaseOrderNotificationsLoading ? (
                    <InlineLoading label="Cargando notificaciones" />
                ) : (
                    <div className="space-y-4">
                        <label className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={purchaseOrderNotificationSettings.enabled !== false}
                                onChange={(event) => updatePurchaseOrderNotificationSetting({ enabled: event.target.checked })}
                                disabled={!canManagePurchaseOrderReasons}
                            />
                            Activar notificaciones de nuevas ordenes
                        </label>

                        <div className="rounded-lg border border-gray-200 bg-white p-3">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Este navegador</h3>
                                    <p className="text-xs text-gray-500">
                                        Activa el permiso del navegador y registra este dispositivo para recibir avisos.
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                        <span className={`rounded-full px-2 py-1 ${
                                            purchaseOrderBrowserPushState.supported
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {purchaseOrderBrowserPushState.supported ? 'Compatible' : 'No compatible'}
                                        </span>
                                        <span className={`rounded-full px-2 py-1 ${
                                            purchaseOrderBrowserPushState.configured
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-amber-100 text-amber-700'
                                        }`}>
                                            {purchaseOrderBrowserPushState.configured ? 'Servidor configurado' : 'Servidor sin claves push'}
                                        </span>
                                        <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
                                            Permiso: {purchaseOrderBrowserPushState.permission}
                                        </span>
                                        <span className={`rounded-full px-2 py-1 ${
                                            purchaseOrderBrowserPushState.serverSubscription
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                            {purchaseOrderBrowserPushState.serverSubscription ? 'Suscrito' : 'No suscrito'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={refreshPurchaseOrderBrowserPushState}
                                        disabled={
                                            purchaseOrderBrowserPushState.loading
                                            || purchaseOrderBrowserPushState.saving
                                            || purchaseOrderBrowserPushState.testing
                                        }
                                        className="rounded border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-60"
                                    >
                                        {purchaseOrderBrowserPushState.loading ? 'Cargando...' : 'Actualizar'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSendPurchaseOrderTestNotification}
                                        disabled={
                                            !purchaseOrderBrowserPushState.serverSubscription
                                            || purchaseOrderBrowserPushState.loading
                                            || purchaseOrderBrowserPushState.saving
                                            || purchaseOrderBrowserPushState.testing
                                        }
                                        className="rounded bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
                                    >
                                        {purchaseOrderBrowserPushState.testing ? 'Enviando...' : 'Enviar prueba'}
                                    </button>
                                    {purchaseOrderBrowserPushState.serverSubscription ? (
                                        <button
                                            type="button"
                                            onClick={handleDisablePurchaseOrderBrowserPush}
                                            disabled={
                                                purchaseOrderBrowserPushState.loading
                                                || purchaseOrderBrowserPushState.saving
                                                || purchaseOrderBrowserPushState.testing
                                            }
                                            className="rounded bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-60"
                                        >
                                            {purchaseOrderBrowserPushState.saving ? 'Guardando...' : 'Desactivar este navegador'}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleEnablePurchaseOrderBrowserPush}
                                            disabled={
                                                !purchaseOrderBrowserPushState.supported
                                                || !purchaseOrderBrowserPushState.configured
                                                || purchaseOrderBrowserPushState.loading
                                                || purchaseOrderBrowserPushState.saving
                                                || purchaseOrderBrowserPushState.testing
                                            }
                                            className="rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                                        >
                                            {purchaseOrderBrowserPushState.saving ? 'Guardando...' : 'Activar en este navegador'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {purchaseOrderBrowserPushState.error && (
                                <p className="mt-3 text-sm text-red-600">{purchaseOrderBrowserPushState.error}</p>
                            )}
                            {purchaseOrderBrowserPushState.message && !purchaseOrderBrowserPushState.error && (
                                <p className="mt-3 text-sm text-green-600">{purchaseOrderBrowserPushState.message}</p>
                            )}
                            {!purchaseOrderBrowserPushState.loading
                                && purchaseOrderBrowserPushState.supported
                                && !purchaseOrderBrowserPushState.configured && (
                                <p className="mt-3 text-xs text-amber-700">
                                    Falta configurar las claves VAPID en el backend antes de pedir el permiso del navegador.
                                </p>
                            )}
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <div className="mb-3 flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Roles destinatarios</h3>
                                    <p className="text-xs text-gray-500">
                                        Solo se muestran roles con permiso para leer ordenes de compra.
                                    </p>
                                </div>
                                <span className="text-xs text-gray-500">{selectedRoleNames.size} seleccionados</span>
                            </div>
                            {purchaseOrderNotificationRoles.length ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {purchaseOrderNotificationRoles.map((role) => (
                                        <label
                                            key={role.name}
                                            className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedRoleNames.has(role.name)}
                                                onChange={() => togglePurchaseOrderNotificationRole(role.name)}
                                                disabled={!canManagePurchaseOrderReasons}
                                            />
                                            <span>{role.name}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                                    No hay roles destinatarios configurados.
                                </p>
                            )}
                            {purchaseOrderNotificationsRoleWarning && (
                                <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                                    {purchaseOrderNotificationsRoleWarning}
                                </p>
                            )}
                        </div>

                        <label className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={purchaseOrderNotificationSettings.respectBranchAssignments !== false}
                                onChange={(event) => updatePurchaseOrderNotificationSetting({ respectBranchAssignments: event.target.checked })}
                                disabled={!canManagePurchaseOrderReasons}
                            />
                            Respetar asignaciones de sucursal
                        </label>

                        <label className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={purchaseOrderNotificationSettings.includeGlobalAdminUsers !== false}
                                onChange={(event) => updatePurchaseOrderNotificationSetting({ includeGlobalAdminUsers: event.target.checked })}
                                disabled={!canManagePurchaseOrderReasons}
                            />
                            Incluir usuarios administradores globales
                        </label>
                    </div>
                )}

                {purchaseOrderNotificationsError && (
                    <p className="text-sm text-red-600 mt-3">{purchaseOrderNotificationsError}</p>
                )}
                {purchaseOrderNotificationsSaved && !purchaseOrderNotificationsError && (
                    <p className="text-sm text-green-600 mt-3">Configuracion de notificaciones guardada.</p>
                )}
            </div>
        );
    };

    const renderPriceSync = ({
        showPriceSync = true,
        showRequestMain = true,
        showPortalFormats = true,
        showDefaultCategories = true,
    } = {}) => {
        if (!canViewPriceSync) {
            return (
                <div className="border rounded-lg p-4">
                    <h2 className="text-md font-semibold">Casa matriz de precios</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        No tienes permisos para ver la configuracion de precios.
                    </p>
                </div>
            );
        }

        const selectedDefaultPurchaseOrderCategoryIds = new Set(
            (branchRequestConfig.defaultPurchaseOrderCategoryIds || []).map(String)
        );
        const selectedPortalProductFormatCodes = new Set(
            (branchRequestConfig.portalProductFormatCodes || []).map((code) => String(code).trim().toLowerCase())
        );
        const requestPanelTitle = showRequestMain
            ? 'Configuracion de solicitudes de stock'
            : showPortalFormats
                ? 'Formatos visibles para ordenes'
                : 'Categorias por defecto de ordenes';
        const requestPanelDescription = showRequestMain
            ? 'Define la sucursal que recibe solicitudes y gestiona envios salientes.'
            : showPortalFormats
                ? 'Define que formatos de producto puede ver y ordenar el cliente.'
                : 'Define las categorias iniciales al habilitar el portal de ordenes de compra.';

        return (
            <div className="space-y-4">
                {showPriceSync && (
                <div className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-md font-semibold">Casa matriz de precios</h2>
                            <p className="text-xs text-gray-500">
                                Replica cambios de precios desde otra sucursal con ajuste.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {canReadBranches && (
                                <button
                                    type="button"
                                    onClick={() => goToSettingsSection('branch-directory', 'branches')}
                                    className="rounded border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    Ir a sucursales
                                </button>
                            )}
                            {canEditPriceSync && (
                                <button
                                    onClick={handleSavePriceSync}
                                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                                    disabled={priceSyncLoading}
                                >
                                    Guardar configuracion
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Casa matriz</span>
                            <select
                                value={priceSync.sourceBranchId}
                                onChange={(e) => handlePriceSyncSourceChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!canEditPriceSync}
                            >
                                <option value="">Sin sincronizacion</option>
                                {sourceBranchOptions.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branchName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Tipo ajuste</span>
                            <select
                                value={priceSync.adjustmentType}
                                onChange={(e) => handlePriceSyncTypeChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!isSyncEnabled || !canEditPriceSync}
                            >
                                <option value="">Selecciona</option>
                                {Object.keys(PRICE_SYNC_TYPE_LABELS).map((key) => (
                                    <option key={key} value={key}>
                                        {PRICE_SYNC_TYPE_LABELS[key]}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Ajuste</span>
                            <input
                                type="text"
                                value={priceSync.adjustmentValue}
                                onChange={(e) => handlePriceSyncValueChange(e.target.value)}
                                placeholder="0"
                                className="w-40 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!isSyncEnabled || !canEditPriceSync}
                            />
                            <span className="text-xs text-gray-500">Usa negativo para descuento.</span>
                        </div>
                        <p className="text-xs text-gray-500">
                            El ajuste se aplica a cada cambio de precio. La copia inicial requiere backfill.
                        </p>
                    </div>
                    {!canEditPriceSync && (
                        <p className="text-xs text-gray-500 mt-2">
                            Solo lectura: necesitas permisos para editar.
                        </p>
                    )}
                    {priceSyncError && (
                        <p className="text-sm text-red-600 mt-3">{priceSyncError}</p>
                    )}
                    {priceSyncSaved && (
                        <p className="text-sm text-green-600 mt-3">Configuracion guardada.</p>
                    )}
                </div>
                )}

                {(showRequestMain || showPortalFormats || showDefaultCategories) && (
                <div className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-md font-semibold">{requestPanelTitle}</h2>
                            <p className="text-xs text-gray-500">
                                {requestPanelDescription}
                            </p>
                        </div>
                        {canManageGlobalSettings && (
                            <button
                                type="button"
                                onClick={handleSaveBranchRequestConfig}
                                className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800 disabled:opacity-60"
                                disabled={branchRequestConfigLoading}
                            >
                                Guardar
                            </button>
                        )}
                    </div>
                    <div className="space-y-3">
                        {showRequestMain && (
                            <>
                                <div className="flex items-center gap-3">
                                    <span className="w-28 text-sm font-medium text-gray-700">Principal</span>
                                    <select
                                        value={branchRequestConfig.mainBranchId}
                                        onChange={(e) => handleBranchRequestMainChange(e.target.value)}
                                        className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                        disabled={!canManageGlobalSettings || branchRequestConfigLoading}
                                    >
                                        <option value="">Automatico (sucursal mas antigua)</option>
                                        {activeBranches.map((branch) => (
                                            <option key={branch.id} value={branch.id}>
                                                {branch.branchName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <p className="text-xs text-gray-500">
                                    Principal efectiva: {
                                        activeBranches.find((branch) => String(branch.id) === String(branchRequestConfig.resolvedMainBranchId))?.branchName
                                        || 'No definida'
                                    }
                                </p>
                            </>
                        )}
                        {showPortalFormats && (
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Formatos visibles en portal</h3>
                                    <p className="text-xs text-gray-500">
                                        Define que formatos de producto puede ver y ordenar el cliente.
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs text-gray-500">
                                        {selectedPortalProductFormatCodes.size} seleccionados
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => goToSettingsSection('catalog-product-formats', 'products')}
                                        className="rounded border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                    >
                                        Ir a formatos de producto
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {productFormatOptions.map((option) => (
                                    <label
                                        key={option.value}
                                        className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedPortalProductFormatCodes.has(option.value)}
                                            onChange={() => handlePortalProductFormatToggle(option.value)}
                                            disabled={!canManageGlobalSettings || branchRequestConfigLoading}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {!selectedPortalProductFormatCodes.size && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 mt-3">
                                    Selecciona al menos un formato para que el portal muestre productos.
                                </p>
                            )}
                        </div>
                        )}
                        {showDefaultCategories && (
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Categorias por defecto para ordenes</h3>
                                    <p className="text-xs text-gray-500">
                                        Se aplican al habilitar el portal de ordenes de compra de un cliente.
                                    </p>
                                </div>
                                <span className="text-xs text-gray-500">
                                    {selectedDefaultPurchaseOrderCategoryIds.size} seleccionadas
                                </span>
                            </div>
                            {portalCategoryLoading ? (
                                <InlineLoading label="Cargando categorias" />
                            ) : portalCategoryError ? (
                                <p className="text-xs text-red-600">{portalCategoryError}</p>
                            ) : portalCategoryOptions.length ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {portalCategoryOptions.map((category) => (
                                        <label
                                            key={category.id}
                                            className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedDefaultPurchaseOrderCategoryIds.has(String(category.id))}
                                                onChange={() => handleDefaultPurchaseOrderCategoryToggle(category.id)}
                                                disabled={!canManageGlobalSettings || branchRequestConfigLoading}
                                            />
                                            <span>{category.name}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-500">
                                    No hay categorias activas disponibles.
                                </p>
                            )}
                            {!selectedDefaultPurchaseOrderCategoryIds.size && !portalCategoryLoading && !portalCategoryError && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 mt-3">
                                    Sin categorias seleccionadas: los clientes habilitados veran vacio el catalogo de ordenes hasta configurar sus categorias.
                                </p>
                            )}
                        </div>
                        )}
                    </div>
                    {branchRequestConfigError && (
                        <p className="text-sm text-red-600 mt-3">{branchRequestConfigError}</p>
                    )}
                    {branchRequestConfigSaved && (
                        <p className="text-sm text-green-600 mt-3">Configuracion de solicitudes guardada.</p>
                    )}
                </div>
                )}
            </div>
        );
    };

    const renderPosFavs = () => (
        <PosSlotSettingsPanel
            branchId={activeBranchId}
            branchLabel={branchLabel}
            preferences={myPreferences}
            preferencesLoading={preferencesLoading}
            preferencesError={preferencesError}
            savePreferences={saveMyPreferences}
            canAccessRegisters={canAccessRegisters}
            canReadProducts={canReadProducts}
            canReadCategories={canReadCategories}
        />
    );

    const renderCatalogProducts = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar productos.
                </div>
            );
        }
        if (!canReadProducts) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver productos.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Gestiona la identidad de productos, variedades y formatos. Los costos, márgenes y reglas de precio se configuran en Precios.
                </div>
                <ProductList
                    branchId={activeBranchId}
                    embedded
                    canReadProducts={canReadProducts}
                    canReadPrices={canReadPrices}
                    canCreateProduct={canCreateProduct}
                    canUpdateProduct={canUpdateProduct}
                    canDeleteProduct={canDeleteProduct}
                    canManageClientPricing={canManageClientPricing}
                />
            </div>
        );
    };

    const renderCatalogCategories = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar categorias.
                </div>
            );
        }
        if (!canReadCategories) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver categorias.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-xs text-gray-500">
                        Configura categorias, subcategorias y disponibilidad por sucursal.
                    </div>
                    {canReadUsers && (
                        <button
                            type="button"
                            onClick={() => goToSettingsSection('users-access', 'users-access')}
                            className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                        >
                            Ir a usuarios
                        </button>
                    )}
                </div>
                <CategoriesAdminPage
                    branchId={activeBranchId}
                    embedded
                    canUpdatePrices={canUpdatePrices}
                />
            </div>
        );
    };

    const renderCatalogProductSearch = () => (
        <div className="space-y-4">
            <div className="border rounded-lg p-4 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <h2 className="text-md font-semibold">Busqueda de productos</h2>
                        <p className="text-xs text-gray-500">
                            Define si los buscadores muestran solo coincidencias exactas o tambien sugeridos por tokens contenidos.
                        </p>
                    </div>
                    {productSearchSettingsLoading && <InlineLoading label="Cargando busqueda" />}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {PRODUCT_SEARCH_MODE_OPTIONS.map((option) => {
                        const isActive = productSearchModeDraft === option.value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                className={`text-left border rounded-lg p-3 transition disabled:opacity-60 ${
                                    isActive
                                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200'
                                }`}
                                onClick={() => handleProductSearchModeChange(option.value)}
                                disabled={!canManageGlobalSettings || productSearchSettingsLoading || productSearchSaving}
                            >
                                <span className="block text-sm font-semibold">{option.label}</span>
                                <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                            </button>
                        );
                    })}
                </div>

                {(productSearchError || productSearchSettingsLoadError) && (
                    <p className="text-sm text-red-600">
                        {productSearchError || productSearchSettingsLoadError}
                    </p>
                )}
                {productSearchSaved && !productSearchError && (
                    <p className="text-sm text-green-600">Configuracion de busqueda guardada.</p>
                )}

                {canManageGlobalSettings && (
                    <button
                        type="button"
                        className="px-4 py-2 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-60"
                        onClick={handleSaveProductSearchSettings}
                        disabled={productSearchSaving || productSearchSettingsLoading}
                    >
                        {productSearchSaving ? 'Guardando...' : 'Guardar busqueda'}
                    </button>
                )}
            </div>
        </div>
    );

    const renderCatalogPosSales = () => {
        const normalizedSearchSuggestions = normalizePosSearchSuggestionVisibility(posSearchSuggestions);

        return (
            <div className="space-y-4">
                <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <h2 className="text-md font-semibold">Venta POS</h2>
                            <p className="text-xs text-gray-500">
                                Define como se muestran los productos agotados y las sugerencias del buscador de caja.
                            </p>
                        </div>
                        {posSoldOutLoading && <InlineLoading label="Cargando configuracion POS" />}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {POS_SOLD_OUT_DISPLAY_OPTIONS.map((option) => {
                            const isActive = posSoldOutMode === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`text-left border rounded-lg p-3 transition disabled:opacity-60 ${
                                        isActive
                                            ? 'border-blue-500 bg-blue-50 text-blue-800'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200'
                                    }`}
                                    onClick={() => handlePosSoldOutModeChange(option.value)}
                                    disabled={!canManageGlobalSettings || posSoldOutLoading || posSoldOutSaving}
                                >
                                    <span className="block text-sm font-semibold">{option.label}</span>
                                    <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="border-t border-gray-100 pt-4 space-y-3">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-800">Sugerencias de busqueda</h3>
                            <p className="text-xs text-gray-500">
                                Controla si el buscador muestra sugerencias automaticas por tipo de dispositivo.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                                {
                                    key: 'desktop',
                                    label: 'Mostrar en escritorio',
                                    description: 'Activa sugerencias en pantallas medianas y grandes.',
                                },
                                {
                                    key: 'mobile',
                                    label: 'Mostrar en movil',
                                    description: 'Activa sugerencias en telefonos. Por defecto quedan ocultas.',
                                },
                            ].map((option) => (
                                <label
                                    key={option.key}
                                    className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-sm"
                                >
                                    <input
                                        type="checkbox"
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        checked={Boolean(normalizedSearchSuggestions[option.key])}
                                        onChange={(event) => handlePosSearchSuggestionChange(option.key, event.target.checked)}
                                        disabled={!canManageGlobalSettings || posSoldOutLoading || posSoldOutSaving}
                                    />
                                    <span>
                                        <span className="block font-semibold text-gray-800">{option.label}</span>
                                        <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {posSoldOutError && (
                        <p className="text-sm text-red-600">{posSoldOutError}</p>
                    )}
                    {posSoldOutSaved && !posSoldOutError && (
                        <p className="text-sm text-green-600">Configuracion POS guardada.</p>
                    )}

                    {canManageGlobalSettings && (
                        <button
                            type="button"
                            className="px-4 py-2 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-60"
                            onClick={handleSavePosSoldOutDisplaySettings}
                            disabled={posSoldOutSaving || posSoldOutLoading}
                        >
                            {posSoldOutSaving ? 'Guardando...' : 'Guardar configuracion POS'}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderCatalogPriceRecommendations = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para configurar recomendaciones.
                </div>
            );
        }
        if (!canReadPrices) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver recomendaciones de precios.
                </div>
            );
        }

        const roundingOption = getPriceRecommendationRoundingOption(priceRecommendationSettings.rounding);
        const currentRoundingThreshold = Number(priceRecommendationSettings.rounding?.threshold);
        const roundingThresholdForHelp = Number.isFinite(currentRoundingThreshold)
            ? Math.min(roundingOption.maxThreshold, Math.max(0, Math.round(currentRoundingThreshold)))
            : Number(roundingOption.defaultThreshold);
        const roundingUpperStart = Math.min(roundingOption.maxThreshold, roundingThresholdForHelp + 1);
        const roundingTargetLabel = roundingOption.unit === 10 ? 'decena' : 'centena';
        const roundingHelpText = roundingThresholdForHelp >= roundingOption.maxThreshold
            ? `Con ${roundingThresholdForHelp}: terminaciones 0-${roundingOption.maxThreshold} bajan; no sube ninguna terminacion a la siguiente ${roundingTargetLabel}.`
            : `Con ${roundingThresholdForHelp}: terminaciones 0-${roundingThresholdForHelp} bajan; ${roundingUpperStart}-${roundingOption.maxThreshold} suben a la siguiente ${roundingTargetLabel}.`;

        return (
            <div className="space-y-4">
                <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <h2 className="text-md font-semibold">Recomendaciones de precios</h2>
                            <p className="text-xs text-gray-500">
                                La regla de precio se configura por formato en la lista de precios. Aqui se define si la ejecucion actualiza precios o solo deja recomendaciones.
                            </p>
                        </div>
                        {priceRecommendationLoading && <InlineLoading label="Cargando recomendaciones" />}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                            {
                                value: true,
                                label: 'Actualizar precios automaticamente',
                                description: 'La ejecucion guarda los precios calculados y registra los cambios aplicados.',
                            },
                            {
                                value: false,
                                label: 'Solo recomendar precios',
                                description: 'La ejecucion calcula y registra recomendaciones sin cambiar la lista de precios.',
                            },
                        ].map((option) => {
                            const isSelected = (priceRecommendationSettings.autoUpdatePrices !== false) === option.value;
                            return (
                                <button
                                    key={String(option.value)}
                                    type="button"
                                    className={`text-left border rounded-lg p-3 transition disabled:opacity-60 ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50 text-blue-800'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200'
                                    }`}
                                    onClick={() => updatePriceRecommendationMode(option.value)}
                                    disabled={!canUpdatePrices || priceRecommendationLoading || priceRecommendationSaving}
                                >
                                    <span className="block text-sm font-semibold">{option.label}</span>
                                    <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <label className="text-xs text-gray-700">
                            Redondear a
                            <select
                                className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                value={roundingOption.unit}
                                onChange={(e) => updatePriceRecommendationRoundingUnit(e.target.value)}
                                disabled={!canUpdatePrices || priceRecommendationLoading || priceRecommendationSaving}
                            >
                                {PRICE_RECOMMENDATION_ROUNDING_OPTIONS.map((option) => (
                                    <option key={option.unit} value={option.unit}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-xs text-gray-700">
                            Umbral redondeo {roundingOption.label.toLowerCase()}
                            <input
                                type="number"
                                min="0"
                                max={roundingOption.maxThreshold}
                                className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                value={priceRecommendationSettings.rounding?.threshold ?? roundingOption.defaultThreshold}
                                onChange={(e) => updatePriceRecommendationRounding('threshold', e.target.value)}
                                disabled={!canUpdatePrices || priceRecommendationLoading || priceRecommendationSaving}
                            />
                        </label>
                        <div className="text-xs text-gray-500 flex items-end">
                            {roundingHelpText}
                            {' '}Los precios fijos no se redondean.
                        </div>
                    </div>

                    {priceRecommendationError && (
                        <p className="text-sm text-red-600">{priceRecommendationError}</p>
                    )}
                    {priceRecommendationSaved && !priceRecommendationError && (
                        <p className="text-sm text-green-600">Recomendaciones guardadas.</p>
                    )}
                    {canUpdatePrices ? (
                        <button
                            type="button"
                            className="px-4 py-2 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-60"
                            onClick={handleSavePriceRecommendationSettings}
                            disabled={priceRecommendationSaving || priceRecommendationLoading || priceRecommendationLoadedBranch !== activeBranchId}
                        >
                            {priceRecommendationSaving ? 'Guardando...' : 'Guardar recomendaciones'}
                        </button>
                    ) : (
                        <p className="text-xs text-gray-500">Solo lectura: necesitas permiso para actualizar precios.</p>
                    )}
                </div>
            </div>
        );
    };

    const renderProductBarcodeSettings = () => (
        <div className="border rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                    <h2 className="text-md font-semibold">Codigo de barra productos</h2>
                    <p className="text-xs text-gray-500">
                        Prefijos de 1 digito para codigos de 13 digitos. El prefijo 9 esta reservado para categorias.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleAddMapping}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                >
                    + Prefijo
                </button>
            </div>

            <div className="space-y-3">
                {mappings.map((mapping, index) => (
                    <div
                        key={`${mapping.prefix}-${index}`}
                        className="flex items-center gap-3"
                    >
                        <input
                            type="text"
                            value={mapping.prefix}
                            placeholder="Prefijo"
                            inputMode="numeric"
                            maxLength={BARCODE_PREFIX_MAX_LENGTH}
                            pattern="[0-9]*"
                            onChange={(e) => handlePrefixChange(index, e.target.value)}
                            className="w-20 p-2 border border-gray-300 rounded"
                        />
                        <select
                            value={mapping.mode}
                            onChange={(e) => handleModeChange(index, e.target.value)}
                            className="flex-1 p-2 border border-gray-300 rounded"
                        >
                            {BARCODE_MODE_OPTIONS.map((mode) => (
                                <option key={mode} value={mode}>
                                    {MODE_LABELS[mode] || mode}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => handleRemoveMapping(index)}
                            className="text-red-600 text-sm hover:text-red-700"
                        >
                            Quitar
                        </button>
                    </div>
                ))}
            </div>

            {error && (
                <p className="text-sm text-red-600 mt-3">{error}</p>
            )}
            {saved && (
                <p className="text-sm text-green-600 mt-3">Configuracion guardada.</p>
            )}

            <div className="flex items-center gap-3 mt-4">
                <button
                    type="button"
                    onClick={handleSave}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                >
                    Guardar
                </button>
                <button
                    type="button"
                    onClick={handleReset}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
                >
                    Restablecer
                </button>
            </div>
        </div>
    );

    const renderAutoPriceRule = () => (
        <div className="border rounded-lg p-4 space-y-3">
            <div>
                <div className="flex items-center gap-1.5">
                    <h2 className="text-md font-semibold">Selección automática de formato en ventas</h2>
                    <InfoTooltip message={AUTO_PRICE_GLOBAL_RULE_HELP} label="Cómo funciona la selección automática de formato" />
                </div>
                <p className="text-xs text-gray-500">
                    Define cuándo una venta cambia automáticamente entre formatos según la cantidad acumulada por variedad o el subtotal del documento. Esta regla es independiente de las reglas de precio por sucursal.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs text-gray-700">
                    <span className="inline-flex items-center gap-1">
                        Modo
                        <InfoTooltip message={AUTO_PRICE_MODE_HELP} label="Como se calcula el modo" />
                    </span>
                    <select
                        className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                        value={barcodePricingRule.mode}
                        onChange={(e) => handleBarcodeRuleModeChange(e.target.value)}
                        disabled={!canManageGlobalSettings || barcodeRuleLoading}
                    >
                        <option value="quantity">Cantidad por variedad</option>
                        <option value="subtotal">Subtotal documento</option>
                    </select>
                </label>
                <div className="text-xs text-gray-500 flex items-end">
                    Si mas de un tipo califica, se aplica el precio calificado mas barato.
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {productFormatOptions.map(({ value: type, label }) => (
                    <label key={type} className="text-xs text-gray-700">
                        <span className="inline-flex items-center gap-1">
                            Umbral {label}
                            <InfoTooltip message={AUTO_PRICE_THRESHOLD_HELP} label={`Como funciona el umbral ${label}`} />
                        </span>
                        <input
                            type="text"
                            inputMode="numeric"
                            className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                            value={barcodePricingRule.thresholds[type] ?? ''}
                            onChange={(e) => handleBarcodeRuleThresholdChange(type, e.target.value)}
                            placeholder="Sin configurar"
                            disabled={!canManageGlobalSettings || barcodeRuleLoading}
                        />
                    </label>
                ))}
            </div>
            {barcodeRuleError && (
                <p className="text-xs text-red-600">{barcodeRuleError}</p>
            )}
            {barcodeRuleSaved && !barcodeRuleError && (
                <p className="text-xs text-green-600">Regla global guardada.</p>
            )}
            {canManageGlobalSettings && <div>
                <button
                    type="button"
                    onClick={handleSaveBarcodeRule}
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                    disabled={barcodeRuleLoading}
                >
                    {barcodeRuleLoading ? 'Guardando...' : 'Guardar regla'}
                </button>
            </div>}
        </div>
    );

    const renderCategoryBarcodeSettings = () => (
        <div className="border rounded-lg p-4 space-y-3">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                    <h2 className="text-md font-semibold">Codigo de barras categorias</h2>
                    <p className="text-xs text-gray-500">
                        Catalogo persistente del identificador de 6 digitos por categoria. El voucher agrega los 6 digitos del total.
                    </p>
                </div>
                {canManageGlobalSettings && (
                    <button
                        type="button"
                        onClick={handleRegenerateCategoryBarcodes}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                        disabled={categoryBarcodeLoading || categoryBarcodeRegenerating}
                    >
                        {categoryBarcodeRegenerating ? 'Generando...' : 'Generar categorias'}
                    </button>
                )}
            </div>

            {categoryBarcodeError && (
                <p className="text-xs text-red-600">{categoryBarcodeError}</p>
            )}
            {categoryBarcodeSaved && !categoryBarcodeError && (
                <p className="text-xs text-green-600">Barcodes de categorias generados.</p>
            )}
            {categoryBarcodeInactiveCount > 0 && (
                <p className="text-xs text-gray-500">
                    {categoryBarcodeInactiveCount} categorias inactivas se conservan en el catalogo.
                </p>
            )}

            {categoryBarcodeLoading ? (
                <InlineLoading label="Cargando categorias" />
            ) : categoryBarcodes.length ? (
                <div className="max-w-full overflow-x-auto border border-gray-200 rounded bg-white">
                    <table className="min-w-[640px] w-full text-sm">
                        <thead className="bg-gray-100 text-xs text-gray-600 uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Categoria</th>
                                <th className="px-3 py-2 text-left">ID</th>
                                <th className="px-3 py-2 text-left">Identificador</th>
                                <th className="px-3 py-2 text-left">Actualizado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {categoryBarcodes.map((entry) => (
                                <tr key={entry.id || entry.categoryId}>
                                    <td className="px-3 py-2 text-gray-800">
                                        {entry.categoryName || `Categoria ${entry.categoryId}`}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">
                                        {entry.categoryId}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-gray-900">
                                        {entry.barcode || (
                                            <span className="font-sans text-xs text-red-600">
                                                categoria excede limite
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">
                                        {formatCategoryBarcodeDate(entry.lastGeneratedAt)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="border border-dashed border-gray-300 rounded p-4 text-center text-xs text-gray-500 bg-white">
                    No hay barcodes de categorias generados.
                </div>
            )}
        </div>
    );

    const renderBarcodes = () => (
        <div className="space-y-4">
            {renderProductBarcodeSettings()}
            {renderAutoPriceRule()}
            {renderCategoryBarcodeSettings()}
        </div>
    );

    const renderPrintRedirectDefaults = () => {
        if (!canReadGlobalSettings) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para editar la configuracion global de impresion.
                </div>
            );
        }

        return (
            <div className="border rounded-lg p-4 space-y-4">
                <div>
                    <h2 className="text-md font-semibold">Redireccion por defecto</h2>
                    <p className="text-xs text-gray-500">
                        Define el tiempo usado por usuarios sin una redireccion personalizada.
                    </p>
                </div>
                {printRedirectDefaultsLoading ? (
                    <InlineLoading label="Cargando configuracion" />
                ) : (
                    <div className="max-w-xs">
                        <label htmlFor="print-redirect-default-delay" className="block text-xs font-semibold text-gray-600 mb-1">
                            Tiempo de espera (segundos)
                        </label>
                        <input
                            id="print-redirect-default-delay"
                            type="number"
                            min="1"
                            max="300"
                            value={printRedirectDefaults.delaySeconds ?? ''}
                            onChange={(e) => handlePrintRedirectDefaultDelayChange(e.target.value)}
                            disabled={!canManageGlobalSettings || printRedirectDefaultsSaving}
                            className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        />
                    </div>
                )}
                {canManageGlobalSettings && <div className="flex items-center gap-3">
                    <button
                        onClick={handleSavePrintRedirectDefaults}
                        disabled={printRedirectDefaultsLoading || printRedirectDefaultsSaving}
                        className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                    >
                        {printRedirectDefaultsSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button
                        onClick={handleResetPrintRedirectDefaults}
                        disabled={printRedirectDefaultsLoading || printRedirectDefaultsSaving}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300 disabled:opacity-60"
                    >
                        Restablecer
                    </button>
                </div>}
                {printRedirectDefaultsError && (
                    <p className="text-sm text-red-600">{printRedirectDefaultsError}</p>
                )}
                {printRedirectDefaultsSaved && (
                    <p className="text-sm text-green-600">Configuracion guardada.</p>
                )}
                <p className="text-xs text-gray-500">
                    Cada usuario puede reemplazar este tiempo desde sus preferencias personales de impresion.
                </p>
            </div>
        );
    };

    const renderPrintSettings = () => (
        <div className="border rounded-lg p-4 space-y-4">
            <div>
                <h2 className="text-md font-semibold">Impresion de comprobantes</h2>
                <p className="text-xs text-gray-500">
                    Define como imprimir desde este navegador.
                </p>
            </div>
            <div className="space-y-3">
                {PRINT_MODE_OPTIONS.map((mode) => (
                    <label
                        key={mode}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                            printMode === mode
                                ? 'border-blue-200 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <input
                            type="radio"
                            name="print-mode"
                            value={mode}
                            checked={printMode === mode}
                            onChange={(e) => handlePrintModeChange(e.target.value)}
                            className="mt-1"
                        />
                        <div>
                            <div className="text-sm font-medium">
                                {PRINT_MODE_LABELS[mode] || mode}
                            </div>
                            {PRINT_MODE_DESCRIPTIONS[mode] && (
                                <div className="text-xs text-gray-500">
                                    {PRINT_MODE_DESCRIPTIONS[mode]}
                                </div>
                            )}
                        </div>
                    </label>
                ))}
            </div>
            <div className="border-t pt-4 space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Redireccion despues de imprimir</h3>
                    <p className="text-xs text-gray-500">
                        Aplica solo al boton Imprimir Recibo en la pagina de comprobante.
                    </p>
                </div>
                <label className="flex items-center gap-3 text-sm">
                    <input
                        type="checkbox"
                        checked={Boolean(printReceiptRedirect.enabled)}
                        onChange={(e) => handlePrintRedirectEnabledChange(e.target.checked)}
                        disabled={preferencesLoading || printSettingsSaving}
                    />
                    <span>Redirigir automaticamente a nueva venta</span>
                </label>
                <div className="max-w-xs">
                    <label htmlFor="print-redirect-delay" className="block text-xs font-semibold text-gray-600 mb-1">
                        Tiempo de espera (segundos)
                    </label>
                    <input
                        id="print-redirect-delay"
                        type="number"
                        min="1"
                        max="300"
                        value={printReceiptRedirect.delaySeconds ?? ''}
                        onChange={(e) => handlePrintRedirectDelayChange(e.target.value)}
                        disabled={!printReceiptRedirect.enabled || preferencesLoading || printSettingsSaving}
                        className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    />
                </div>
                {!preferencesLoading && (
                    <p className="text-xs text-gray-500">
                        {myPreferences?.hasCustomPrintReceiptRedirect
                            ? 'Este usuario tiene una redireccion personalizada.'
                            : `Usando el tiempo por defecto global: ${printReceiptRedirect.delaySeconds || DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS.delaySeconds} segundos.`}
                    </p>
                )}
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={handleSavePrintMode}
                    disabled={preferencesLoading || printSettingsSaving}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                >
                    {printSettingsSaving ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                    onClick={handleResetPrintMode}
                    disabled={preferencesLoading || printSettingsSaving}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300 disabled:opacity-60"
                >
                    Restablecer
                </button>
            </div>
            {(preferencesError || printSettingsError) && (
                <p className="text-sm text-red-600">{printSettingsError || preferencesError}</p>
            )}
            {printModeSaved && (
                <p className="text-sm text-green-600">Configuracion guardada.</p>
            )}
            <p className="text-xs text-gray-500">
                El modo de impresion se guarda en este navegador. La redireccion se guarda para tu usuario; restablecer vuelve al predeterminado global.
            </p>
        </div>
    );

    const renderPurchaseOrderReviewSettings = () => (
        <div className="border rounded-lg p-4 space-y-4">
            <div>
                <h2 className="text-md font-semibold">Revision de ordenes de compra</h2>
                <p className="text-xs text-gray-500">
                    Define como se guardan y confirman los items cuando revisas ordenes.
                </p>
            </div>
            <div className="space-y-3">
                <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm">
                    <input
                        type="checkbox"
                        checked={Boolean(purchaseOrderReview.autoSaveItemChanges)}
                        onChange={(event) => handlePurchaseOrderReviewChange('autoSaveItemChanges', event.target.checked)}
                        disabled={preferencesLoading || purchaseOrderReviewSaving}
                        className="mt-1"
                    />
                    <span>
                        <span className="block font-medium text-gray-900">Guardar cambios automaticamente</span>
                        <span className="block text-xs text-gray-500">
                            Cantidades y motivos se guardan al editar. Si lo desactivas, deberas guardar cada item.
                        </span>
                    </span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm">
                    <input
                        type="checkbox"
                        checked={Boolean(purchaseOrderReview.requireItemConfirmation)}
                        onChange={(event) => handlePurchaseOrderReviewChange('requireItemConfirmation', event.target.checked)}
                        disabled={preferencesLoading || purchaseOrderReviewSaving}
                        className="mt-1"
                    />
                    <span>
                        <span className="block font-medium text-gray-900">Confirmar items procesados por separado</span>
                        <span className="block text-xs text-gray-500">
                            Los cambios guardados quedan pendientes hasta confirmar que el item fue procesado.
                        </span>
                    </span>
                </label>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={handleSavePurchaseOrderReview}
                    disabled={preferencesLoading || purchaseOrderReviewSaving}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                >
                    {purchaseOrderReviewSaving ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                    onClick={handleResetPurchaseOrderReview}
                    disabled={preferencesLoading || purchaseOrderReviewSaving}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300 disabled:opacity-60"
                >
                    Restablecer
                </button>
            </div>
            {(preferencesError || purchaseOrderReviewError) && (
                <p className="text-sm text-red-600">{purchaseOrderReviewError || preferencesError}</p>
            )}
            {purchaseOrderReviewSaved && (
                <p className="text-sm text-green-600">Configuracion guardada.</p>
            )}
            <p className="text-xs text-gray-500">
                Esta configuracion se guarda para tu usuario y aplica en cualquier navegador.
            </p>
        </div>
    );

    const renderTaxSettings = () => (
        <div className="space-y-6">
            <div className="border rounded-lg p-4 space-y-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                        <h2 className="text-md font-semibold">Impuestos y activacion por sucursal</h2>
                        <p className="text-xs text-gray-500">Administra el IVA, otros impuestos y su activacion por sucursal.</p>
                    </div>
                    {canReadBranches && (
                        <button
                            type="button"
                            onClick={() => goToSettingsSection('branch-directory', 'branches')}
                            className="rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            Ir a sucursales
                        </button>
                    )}
                </div>
                {!canReadTaxes && (
                    <p className="text-sm text-gray-500">No tienes permisos para ver impuestos.</p>
                )}
                {canReadTaxes && taxesError && (
                    <p className="text-sm text-red-600">{taxesError}</p>
                )}
                {canReadTaxes && taxesLoading && (
                    <InlineLoading label="Cargando impuestos" />
                )}

                {canReadTaxes && !taxesLoading && (
                    <>
                        {canManageTaxes && (
                            <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                                <div className="text-sm font-semibold text-gray-700">Crear impuesto</div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                        <input
                                            type="text"
                                            value={taxForm.name}
                                            onChange={(e) => setTaxForm((prev) => ({ ...prev, name: e.target.value }))}
                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                            disabled={taxSaving}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 uppercase">Tasa (%)</label>
                                        <input
                                            type="number"
                                            value={taxForm.rate}
                                            onChange={(e) => setTaxForm((prev) => ({ ...prev, rate: e.target.value }))}
                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                            disabled={taxSaving}
                                        />
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={taxForm.applyToAllProducts}
                                                onChange={(e) => setTaxForm((prev) => ({ ...prev, applyToAllProducts: e.target.checked }))}
                                                disabled={taxSaving}
                                            />
                                            Aplicar a todos
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={taxForm.isActive}
                                                onChange={(e) => setTaxForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                                                disabled={taxSaving}
                                            />
                                            Activo
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                        onClick={handleCreateTax}
                                        disabled={taxSaving}
                                    >
                                        {taxSaving ? 'Guardando...' : 'Agregar impuesto'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                            <div className="text-sm font-semibold text-gray-700">Impuestos registrados</div>
                            {taxes.length === 0 && (
                                <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                            )}
                            {taxes.map((tax) => {
                                const draft = taxEdits[tax.id] || {};
                                return (
                                    <div key={tax.id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                            <div className="md:col-span-2">
                                                <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                                <input
                                                    type="text"
                                                    value={draft.name ?? tax.name}
                                                    onChange={(e) => handleTaxEditChange(tax.id, 'name', e.target.value)}
                                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                    disabled={!canManageTaxes || taxSaving}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-gray-600 uppercase">Tasa (%)</label>
                                                <input
                                                    type="number"
                                                    value={draft.rate ?? tax.rate}
                                                    onChange={(e) => handleTaxEditChange(tax.id, 'rate', e.target.value)}
                                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                    disabled={!canManageTaxes || taxSaving}
                                                />
                                            </div>
                                            <div className="flex flex-wrap gap-3">
                                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.applyToAllProducts ?? tax.applyToAllProducts}
                                                        onChange={(e) => handleTaxEditChange(tax.id, 'applyToAllProducts', e.target.checked)}
                                                        disabled={!canManageTaxes || taxSaving}
                                                    />
                                                    Aplicar a todos
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.isActive ?? tax.isActive}
                                                        onChange={(e) => handleTaxEditChange(tax.id, 'isActive', e.target.checked)}
                                                        disabled={!canManageTaxes || taxSaving}
                                                    />
                                                    Activo
                                                </label>
                                            </div>
                                        </div>
                                        {canManageTaxes && (
                                            <div>
                                                <button
                                                    type="button"
                                                    className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                                                    onClick={() => handleSaveTax(tax.id)}
                                                    disabled={taxSaving}
                                                >
                                                    Guardar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            <div className="border rounded-lg p-4 space-y-4">
                <div>
                    <h2 className="text-md font-semibold">Activacion por sucursal</h2>
                    <p className="text-xs text-gray-500">
                        Define que impuestos aplican en cada sucursal.
                    </p>
                </div>
                {!canReadTaxes && (
                    <p className="text-sm text-gray-500">No tienes permisos para ver impuestos por sucursal.</p>
                )}
                {canReadTaxes && branchTaxError && (
                    <p className="text-sm text-red-600">{branchTaxError}</p>
                )}
                {canReadTaxes && branchTaxLoading && (
                    <InlineLoading label="Cargando impuestos por sucursal" />
                )}
                {canReadTaxes && !branchTaxLoading && taxes.length === 0 && (
                    <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                )}
                {canReadTaxes && !branchTaxLoading && taxes.length > 0 && (
                    <div className="max-w-full overflow-x-auto">
                        <table className="min-w-[720px] w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Sucursal</th>
                                    {taxes.map((tax) => (
                                        <th
                                            key={tax.id}
                                            className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase"
                                        >
                                            {tax.name}
                                        </th>
                                    ))}
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {activeBranches.map((branch) => {
                                    const branchMap = branchTaxMap[branch.id] || {};
                                    const isSaving = !!branchTaxSaving[branch.id];
                                    const isDirty = !!branchTaxDirty[branch.id];
                                    return (
                                        <tr key={branch.id}>
                                            <td className="px-3 py-2 font-medium text-gray-800">
                                                {branch.branchName || `Sucursal ${branch.id}`}
                                            </td>
                                            {taxes.map((tax) => (
                                                <td key={tax.id} className="px-3 py-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!branchMap[tax.id]}
                                                        onChange={() => handleBranchTaxToggle(branch.id, tax.id)}
                                                        disabled={!canManageTaxes || tax.isActive === false || isSaving}
                                                    />
                                                </td>
                                            ))}
                                            <td className="px-3 py-2 text-right">
                                                {canManageTaxes && (
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-60"
                                                        onClick={() => handleSaveBranchTaxes(branch.id)}
                                                        disabled={!isDirty || isSaving}
                                                    >
                                                        {isSaving ? 'Guardando...' : 'Guardar'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {activeBranches.length === 0 && (
                                    <tr>
                                        <td colSpan={taxes.length + 2} className="px-3 py-4 text-center text-xs text-gray-500">
                                            No hay sucursales registradas.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    const renderClientPricingConfiguration = () => (
        <div className="space-y-4">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Edicion de precios por cliente</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Define como se editan las categorias de precios. La regla es global; la sucursal elegida aporta precio y costo de referencia.
                        </p>
                    </div>
                    {clientPricingSettingsLoading && <InlineLoading label="Cargando" />}
                </div>

                <div className="mt-5 grid gap-3 lg:grid-cols-3">
                    {[
                        {
                            mode: CLIENT_PRICING_MODE_COST_PLUS,
                            title: 'Costo + margen y precio final',
                            description: 'Edita el margen sobre el costo como monto o porcentaje, o escribe el precio final. Es el modo predeterminado.',
                        },
                        {
                            mode: CLIENT_PRICING_MODE_DISCOUNT,
                            title: 'Descuento sobre precio base',
                            description: 'Mantiene la edicion anterior por monto o porcentaje de descuento.',
                        },
                        {
                            mode: CLIENT_PRICING_MODE_MARGIN_DISCOUNT,
                            title: 'Descuento sobre el margen',
                            description: 'Reduce solo el margen positivo entre precio y costo. Se recalcula cuando cualquiera de los dos cambia.',
                        },
                    ].map((option) => {
                        const active = clientPricingSettings.mode === option.mode;
                        return (
                            <button
                                key={option.mode}
                                type="button"
                                onClick={() => {
                                    setClientPricingSettings((current) => ({ ...current, mode: option.mode }));
                                    setClientPricingSettingsSaved(false);
                                }}
                                disabled={!canManageClientPricing || clientPricingSettingsLoading || clientPricingSettingsSaving}
                                className={`rounded-xl border p-4 text-left transition ${
                                    active
                                        ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-100'
                                        : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-gray-50'
                                } disabled:opacity-60`}
                            >
                                <span className="flex items-center gap-3">
                                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                                        active ? 'border-blue-600' : 'border-gray-300'
                                    }`}
                                    >
                                        {active && <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />}
                                    </span>
                                    <span className="font-semibold text-gray-900">{option.title}</span>
                                </span>
                                <span className="mt-2 block pl-8 text-sm text-gray-500">{option.description}</span>
                            </button>
                        );
                    })}
                </div>

                {clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT && (
                    <section className="mt-4 rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                        <h3 className="text-sm font-semibold text-blue-950">Cómo ingresar el porcentaje</h3>
                        <p className="mt-1 text-xs text-blue-800">
                            Ambas opciones representan la misma regla. Cambiar esta vista no modifica ningún precio.
                        </p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                            {[
                                {
                                    value: CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
                                    label: 'Reducir el margen en X%',
                                    help: 'Ejemplo: 20% descuenta el 20% del margen positivo.',
                                },
                                {
                                    value: CLIENT_PRICING_MARGIN_DISPLAY_RETAINED,
                                    label: 'Dejar el margen en X%',
                                    help: 'Ejemplo: 80% deja el 80% del margen positivo.',
                                },
                            ].map((option) => {
                                const active = clientPricingSettings.marginPercentDisplay === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        aria-pressed={active}
                                        onClick={() => {
                                            setClientPricingSettings((current) => ({
                                                ...current,
                                                marginPercentDisplay: option.value,
                                            }));
                                            setClientPricingSettingsSaved(false);
                                        }}
                                        disabled={!canManageClientPricing || clientPricingSettingsLoading || clientPricingSettingsSaving}
                                        className={`rounded-lg border bg-white p-3 text-left ${active
                                            ? 'border-blue-500 ring-2 ring-blue-100'
                                            : 'border-blue-100 hover:border-blue-300'} disabled:opacity-60`}
                                    >
                                        <span className="block text-sm font-semibold text-gray-900">{option.label}</span>
                                        <span className="mt-1 block text-xs text-gray-500">{option.help}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT && (
                    <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Conversión de descuentos existentes</h3>
                                <p className="mt-1 text-xs text-gray-500">
                                    Los descuentos que no pudieron convertirse siguen siendo fijos hasta que se corrijan sus datos y se reintente.
                                </p>
                            </div>
                            {canManageClientPricing && (
                                <button
                                    type="button"
                                    onClick={handleRetryClientPricingConversions}
                                    disabled={clientPricingConversionLoading || clientPricingConversionRetrying}
                                    className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                                >
                                    {clientPricingConversionRetrying ? 'Reintentando...' : 'Reintentar pendientes'}
                                </button>
                            )}
                        </div>
                        {clientPricingConversionLoading && <div className="mt-3"><InlineLoading label="Cargando pendientes" /></div>}
                        {clientPricingConversionError && (
                            <p className="mt-3 rounded border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                                {clientPricingConversionError}
                            </p>
                        )}
                        {clientPricingConversionSummary && (
                            <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                {(clientPricingConversionSummary.convertedCount !== undefined
                                    || clientPricingConversionSummary.converted !== undefined) && (
                                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
                                        {Number(clientPricingConversionSummary.convertedCount ?? clientPricingConversionSummary.converted)} convertidos
                                    </span>
                                )}
                                <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700">
                                    {Number(clientPricingConversionSummary.retainedCount
                                        ?? clientPricingConversionSummary.pendingCount
                                        ?? clientPricingConversionSummary.pending
                                        ?? clientPricingConversionIssueTotal)} pendientes
                                </span>
                                {Number(clientPricingConversionSummary.clearedZeroEffectCount
                                    ?? clientPricingConversionSummary.removedCount
                                    ?? clientPricingConversionSummary.removed
                                    ?? 0) > 0 && (
                                    <span className="rounded-full bg-gray-100 px-2.5 py-1 font-semibold text-gray-600">
                                        {Number(clientPricingConversionSummary.clearedZeroEffectCount
                                            ?? clientPricingConversionSummary.removedCount
                                            ?? clientPricingConversionSummary.removed)} sin efecto eliminados
                                    </span>
                                )}
                            </div>
                        )}
                        {!clientPricingConversionLoading && clientPricingConversionIssueTotal === 0 && !clientPricingConversionError && (
                            <p className="mt-3 text-xs font-medium text-emerald-700">No hay descuentos pendientes de conversión.</p>
                        )}
                        {clientPricingConversionIssues.length > 0 && (
                            <div className="mt-3 max-h-56 overflow-auto rounded border border-gray-200">
                                {clientPricingConversionIssues.map((issue, index) => (
                                    <div
                                        key={issue.id || `${issue.varietyPriceId || 'issue'}-${index}`}
                                        className="flex flex-col gap-1 border-b border-gray-100 px-3 py-2 text-xs last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
                                    >
                                        <span className="font-medium text-gray-800">
                                            {[issue.productName, issue.varietyName, issue.productFormatName || issue.clientTypeLabel]
                                                .filter(Boolean).join(' · ') || `Precio ${issue.varietyPriceId || '-'}`}
                                        </span>
                                        <span className="text-amber-700">{getClientPricingConversionReasonLabel(issue)}</span>
                                    </div>
                                ))}
                                {clientPricingConversionIssueTotal > clientPricingConversionIssues.length && (
                                    <div className="flex items-center justify-between gap-3 px-3 py-2 text-xs text-gray-500">
                                        <span>
                                            Mostrando {(clientPricingConversionIssuePage - 1) * 100 + 1}–{Math.min(
                                                (clientPricingConversionIssuePage - 1) * 100 + clientPricingConversionIssues.length,
                                                clientPricingConversionIssueTotal
                                            )} de {clientPricingConversionIssueTotal} pendientes.
                                        </span>
                                        <span className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => loadClientPricingConversionIssues(clientPricingConversionIssuePage - 1)}
                                                disabled={clientPricingConversionLoading || clientPricingConversionIssuePage <= 1}
                                                className="rounded border border-gray-200 bg-white px-2 py-1 font-semibold text-gray-700 disabled:opacity-50"
                                            >
                                                Anterior
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => loadClientPricingConversionIssues(clientPricingConversionIssuePage + 1)}
                                                disabled={clientPricingConversionLoading
                                                    || clientPricingConversionIssuePage * 100 >= clientPricingConversionIssueTotal}
                                                className="rounded border border-gray-200 bg-white px-2 py-1 font-semibold text-gray-700 disabled:opacity-50"
                                            >
                                                Siguiente
                                            </button>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                )}

                {clientPricingSettingsError && (
                    <p className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {clientPricingSettingsError}
                    </p>
                )}
                {clientPricingSettingsSaved && !clientPricingSettingsError && (
                    <p className="mt-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                        Configuracion guardada.
                    </p>
                )}

                {canManageClientPricing && <div className="mt-5 flex justify-end">
                    <button
                        type="button"
                        onClick={handleSaveClientPricingSettings}
                        disabled={clientPricingSettingsLoading || clientPricingSettingsSaving}
                        className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                        {clientPricingSettingsSaving ? 'Guardando...' : 'Guardar configuracion'}
                    </button>
                </div>}
            </section>
        </div>
    );

    const renderClientPricingCategories = () => {
        const selectedCategory = clientPricingCategories.find(
            (category) => String(category.id) === String(selectedClientPricingCategoryId)
        );
        const marginMode = clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT;
        const showMatrix = !marginMode || (selectedCategory?.marginReductionPercent == null
            && String(clientPricingFormatRulesId) === String(selectedClientPricingCategoryId));
        if (!canReadClientPricing) {
            return (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    No tienes permisos para administrar categorias de descuento.
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {(clientPricingCategoriesError
                    || clientPricingApplicabilityError
                    || clientPricingDiscountsError
                    || clientPricingSettingsError) && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {clientPricingCategoriesError
                            || clientPricingApplicabilityError
                            || clientPricingDiscountsError
                            || clientPricingSettingsError}
                    </div>
                )}
                {clientPricingDiscountsSaved && (
                    <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                        {clientPricingSettings.mode === CLIENT_PRICING_MODE_COST_PLUS
                            ? 'Precios actualizados.'
                            : clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT
                                ? 'Reglas de margen actualizadas.'
                                : 'Descuentos actualizados.'}
                    </div>
                )}

                <div className={marginMode ? 'min-w-0 space-y-6' : 'grid min-w-0 gap-4 2xl:grid-cols-[minmax(260px,320px),minmax(0,1fr)]'}>
                    {marginMode ? <ClientPricingMarginGroups
                        groups={clientPricingGroups}
                        categories={clientPricingCategories}
                        selectedId={selectedClientPricingCategoryId}
                        onSelect={selectClientPricingCategory}
                        onCategorySaved={(category) => {
                            setClientPricingCategories((current) => current.some((item) => String(item.id) === String(category.id))
                                ? current.map((item) => String(item.id) === String(category.id) ? { ...item, ...category } : item)
                                : [...current, category]);
                            if (category.marginReductionPercent != null) setClientPricingFormatRulesId(null);
                        }}
                        onDelete={handleDeleteClientPricingCategory}
                        onGroupsChanged={loadClientPricingCategories}
                        onFormatRules={(id) => { selectClientPricingCategory(String(id)); setClientPricingFormatRulesId(String(id)); }}
                        canManage={canManageClientPricing}
                        loading={clientPricingCategoriesLoading || clientPricingSettingsLoading}
                        editingDisabled={clientPricingApplicabilityLoading || Boolean(clientPricingApplicabilityError)}
                        deletingId={clientPricingCategoryDeletingId}
                        marginPercentDisplay={clientPricingSettings.marginPercentDisplay}
                        branches={clientPricingApplicabilityOptions.branches}
                        productCategories={clientPricingApplicabilityOptions.categories}
                        productFormats={clientPricingApplicabilityOptions.productFormats}
                    /> : <ClientPricingCategoryGroups
                        groups={clientPricingGroups}
                        categories={clientPricingCategories}
                        selectedId={selectedClientPricingCategoryId}
                        onSelect={selectClientPricingCategory}
                        onCreate={handleOpenClientPricingCategoryCreate}
                        onEdit={handleEditClientPricingCategory}
                        onDelete={handleDeleteClientPricingCategory}
                        onGroupsChanged={loadClientPricingCategories}
                        canManage={canManageClientPricing}
                        loading={clientPricingCategoriesLoading}
                        editingDisabled={clientPricingApplicabilityLoading || Boolean(clientPricingApplicabilityError)}
                        deletingId={clientPricingCategoryDeletingId}
                        marginPercentDisplay={clientPricingSettings.marginPercentDisplay}
                        productCategories={clientPricingApplicabilityOptions.categories}
                    />}

                    {showMatrix && <div className="min-w-0 space-y-4">
                        {marginMode && <button type="button" className="text-sm font-semibold text-blue-700" onClick={() => setClientPricingFormatRulesId(null)}>Cerrar reglas por formato</button>}
                        <ClientPricingDiscountMatrix
                            title={selectedCategory?.marginReductionPercent != null
                                ? `Vista previa - ${selectedCategory.name}`
                                : clientPricingSettings.mode === CLIENT_PRICING_MODE_COST_PLUS
                                ? selectedCategory ? `Precios - ${selectedCategory.name}` : 'Precios por cliente'
                                : clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT
                                    ? selectedCategory ? `Margen - ${selectedCategory.name}` : 'Descuentos sobre margen'
                                    : selectedCategory ? `Descuentos - ${selectedCategory.name}` : 'Descuentos por precio'}
                            description={selectedCategory?.marginReductionPercent != null
                                ? 'Vista previa del porcentaje general. Edita la categoría para cambiar el descuento de todos los productos aplicables.'
                                : canManageClientPricing
                                ? clientPricingSettings.mode === CLIENT_PRICING_MODE_COST_PLUS
                                    ? 'Edita el margen sobre el costo o el precio final. Siempre veras su equivalente como monto y porcentaje; cada fila se guarda automaticamente.'
                                    : clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT
                                        ? 'La regla se aplica solo al margen positivo y se recalcula con el precio y costo actuales de cada sucursal. Cada fila se guarda automáticamente.'
                                        : 'La sucursal seleccionada se usa como referencia de precio. Cada fila se guarda automaticamente.'
                                : 'Consulta los precios, costos y margenes configurados para esta categoria.'}
                            pricingMode={selectedCategory?.marginReductionPercent != null ? CLIENT_PRICING_MODE_MARGIN_DISCOUNT : clientPricingSettings.mode}
                            marginPercentDisplay={clientPricingSettings.marginPercentDisplay}
                            rows={clientPricingDiscountRows}
                            loading={clientPricingDiscountsLoading}
                            error={clientPricingDiscountsError}
                            saved={clientPricingDiscountsSaved}
                            readOnly={!canManageClientPricing || selectedCategory?.marginReductionPercent != null}
                            disabled={!selectedClientPricingCategoryId || clientPricingBulkSaving}
                            search={clientPricingSearch}
                            onSearchChange={setClientPricingSearch}
                            bulkForm={clientPricingBulkForm}
                            onBulkFormChange={setClientPricingBulkForm}
                            onBulkAction={handleBulkClientPricingDiscounts}
                            onRowChange={handleClientPricingRowCommit}
                            onUnitWeightSave={canUpdateProduct
                                ? handleSaveClientPricingUnitWeight
                                : undefined}
                            rowSavingById={clientPricingRowSaving}
                            rowStatusById={clientPricingRowSaveStatus}
                            savingBulk={clientPricingBulkSaving}
                            emptyMessage={selectedClientPricingCategoryId
                                ? 'No hay precios visibles para este filtro.'
                                : 'Selecciona o crea una categoria.'}
                        />
                        {selectedCategory?.marginReductionPercent == null && selectedClientPricingCategoryId && clientPricingReferenceBranchId && <BulkPricingPendingSection
                            key={`category-bulk:${selectedClientPricingCategoryId}:${clientPricingReferenceBranchId}`}
                            scope={{ scope: 'category', branchId: clientPricingReferenceBranchId, categoryId: selectedClientPricingCategoryId }}
                            refreshKey={clientPricingBulkRefresh} onApplied={loadClientPricingDiscountCatalog}
                            disabled={!canManageClientPricing || clientPricingBulkSaving} canUpdateProduct={canUpdateProduct} canUpdatePrice={canUpdatePrices} userId={user?.id} />}
                        {selectedCategory?.marginReductionPercent == null && <PendingClientPricingCosts
                            rows={clientPricingPendingCosts}
                            costValues={clientPricingPendingCostValues}
                            savingById={clientPricingPendingCostSaving}
                            errorsById={clientPricingPendingCostErrors}
                            rule={clientPricingPendingRule}
                            disabled={!canManageClientPricing || clientPricingBulkSaving}
                            canUpdateWeight={canUpdateProduct}
                            canUpdateCost={canUpdatePrices}
                            onCostChange={handleClientPricingPendingCostChange}
                            onSave={handleSaveClientPricingPendingCost}
                        />}
                    </div>}
                </div>
            </div>
        );
    };

    const renderProductFormats = () => (
        <div className="space-y-6">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Formatos de producto</h2>
                        <p className="text-sm text-gray-500">El código es estable; puedes cambiar nombre, estado y orden.</p>
                    </div>
                    {productFormatsLoading && <InlineLoading label="Cargando" />}
                </div>
                {canManageGlobalSettings && <form onSubmit={handleCreateProductFormat} className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
                    <label className="text-xs font-semibold text-gray-600 uppercase">
                        Nombre
                        <input
                            type="text"
                            value={productFormatForm.name}
                            onChange={(event) => setProductFormatForm((prev) => ({ ...prev, name: event.target.value }))}
                            className="mt-1 h-10 w-full rounded border border-gray-300 px-3 text-sm"
                            placeholder="Nuevo formato"
                        />
                    </label>
                    <label className="flex h-10 items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={productFormatForm.isActive}
                            onChange={(event) => setProductFormatForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                        />
                        Activo
                    </label>
                    <button
                        type="submit"
                        disabled={productFormatsLoading}
                        className="h-10 rounded bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                        Crear
                    </button>
                </form>}
                <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
                    <div className="grid min-w-[620px] grid-cols-[72px_110px_minmax(0,1fr)_112px_90px] bg-gray-50 px-3 py-2 text-xs font-semibold uppercase text-gray-500">
                        <span>Posición</span>
                        <span>Código</span>
                        <span>Nombre</span>
                        <span>Mover</span>
                        <span>Estado</span>
                    </div>
                    {productFormats.map((format, index) => (
                        <div key={format.id} className="grid min-w-[620px] grid-cols-[72px_110px_minmax(0,1fr)_112px_90px] items-center gap-2 border-t border-gray-100 px-3 py-2 text-sm">
                            <span className="text-sm font-semibold text-gray-500">{index + 1}</span>
                            <code className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">{format.code}</code>
                            {canManageGlobalSettings ? (
                                <input
                                    className="h-9 rounded border border-gray-300 px-2 text-sm"
                                    value={format.name || ''}
                                    onChange={(event) => setProductFormats((prev) => prev.map((entry) => (
                                        entry.id === format.id ? { ...entry, name: event.target.value } : entry
                                    )))}
                                    onBlur={(event) => handleUpdateProductFormat(format, { name: event.target.value })}
                                />
                            ) : (
                                <span className="text-gray-800">{format.name || '-'}</span>
                            )}
                            {canManageGlobalSettings ? (
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => handleMoveProductFormat(format, -1)}
                                        disabled={productFormatsLoading || index === 0}
                                        title="Subir formato"
                                        aria-label={`Subir ${format.name || format.code}`}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        <FiChevronUp />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleMoveProductFormat(format, 1)}
                                        disabled={productFormatsLoading || index === productFormats.length - 1}
                                        title="Bajar formato"
                                        aria-label={`Bajar ${format.name || format.code}`}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        <FiChevronDown />
                                    </button>
                                </div>
                            ) : (
                                <span className="text-gray-400">-</span>
                            )}
                            {canManageGlobalSettings ? (
                                <button
                                    type="button"
                                    onClick={() => handleUpdateProductFormat(format, { isActive: format.isActive === false })}
                                    className={`rounded px-2 py-1 text-xs font-semibold ${
                                        format.isActive === false ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                                    }`}
                                >
                                    {format.isActive === false ? 'Inactivo' : 'Activo'}
                                </button>
                            ) : (
                                <span className={`w-fit rounded px-2 py-1 text-xs font-semibold ${
                                    format.isActive === false ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                                }`}>
                                    {format.isActive === false ? 'Inactivo' : 'Activo'}
                                </span>
                            )}
                        </div>
                    ))}
                    {!productFormatsLoading && productFormats.length === 0 && (
                        <div className="border-t border-gray-100 px-3 py-6 text-sm text-gray-500">No hay formatos configurados.</div>
                    )}
                </div>
                {productFormatsError && <p className="mt-3 text-sm text-red-600">{productFormatsError}</p>}
                {productFormatsSaved && !productFormatsError && (
                    <p className="mt-3 text-sm text-green-600">Formatos guardados.</p>
                )}
            </section>
        </div>
    );

    const renderSectionContent = () => {
        switch (activeSectionConfig?.id) {
            case 'companies':
                return <CompaniesSettingsPanel />;
            case 'users-access':
                return renderUsersAccess();
            case 'branch-directory':
                return renderBranchDirectory();
            case 'branch-price-sync':
                return renderPriceSync({
                    showPriceSync: true,
                    showRequestMain: false,
                    showPortalFormats: false,
                    showDefaultCategories: false,
                });
            case 'branch-stock-requests':
                return renderPriceSync({
                    showPriceSync: false,
                    showRequestMain: true,
                    showPortalFormats: false,
                    showDefaultCategories: false,
                });
            case 'purchase-order-reasons':
                return renderPurchaseOrderReasons();
            case 'purchase-order-notifications':
            case 'notifications':
                return renderPurchaseOrderNotifications();
            case 'purchase-order-review':
                return renderPurchaseOrderReviewSettings();
            case 'purchase-order-visible-formats':
                return renderPriceSync({
                    showPriceSync: false,
                    showRequestMain: false,
                    showPortalFormats: true,
                    showDefaultCategories: false,
                });
            case 'purchase-order-default-categories':
                return renderPriceSync({
                    showPriceSync: false,
                    showRequestMain: false,
                    showPortalFormats: false,
                    showDefaultCategories: true,
                });
            case 'client-pricing-categories':
                return renderClientPricingCategories();
            case 'client-pricing-configuration':
                return renderClientPricingConfiguration();
            case 'catalog-product-formats':
                return renderProductFormats();
            case 'catalog-format-equivalences':
                return (
                    <FormatEquivalenceSettingsPanel
                        branchId={activeBranchId}
                        canReadProducts={canReadProducts}
                        canUpdateProduct={canUpdateProduct}
                        confirm={confirm}
                    />
                );
            case 'catalog-product-barcodes':
                return renderProductBarcodeSettings();
            case 'catalog-category-barcodes':
                return renderCategoryBarcodeSettings();
            case 'price-auto-rule':
                return renderAutoPriceRule();
            case 'registers-cash':
                return renderBranchDirectory();
            case 'registers-print-defaults':
                return renderPrintRedirectDefaults();
            case 'registers-barcode':
                return renderBarcodes();
            case 'registers-printing':
                return renderPrintSettings();
            case 'registers-pos-favs':
                return renderPosFavs();
            case 'taxes-manage':
                return renderTaxSettings();
            case 'catalog-products':
                return renderCatalogProducts();
            case 'catalog-categories':
            case 'catalog-subcategories':
                return renderCatalogCategories();
            case 'catalog-product-search':
                return renderCatalogProductSearch();
            case 'catalog-pos-sales':
                return renderCatalogPosSales();
            case 'catalog-price-recommendations':
                return renderCatalogPriceRecommendations();
            case 'sale-rounding':
                return (
                    <SaleRoundingSettingsPanel
                        key={activeBranchId || 'company'}
                        activeBranchId={activeBranchId}
                        canManage={!isBusinessOwner && hasPermission(user, 'settings.manage')}
                    />
                );
            case 'catalog-price-list':
                return (
                    <AdvancedPricingSettingsPanel
                        activeBranchId={activeBranchId}
                        canReadPrices={canReadPrices}
                        canUpdatePrices={canUpdatePrices}
                        onOpenOperationalList={() =>
                            navigate(
                                activeBranchId
                                    ? `/branches/${activeBranchId}/price-list`
                                    : '/branches'
                            )
                        }
                    />
                );
            case 'complex-price-rules':
                return (
                    <ComplexPriceRulesPanel
                        canRead={canReadPrices}
                        canUpdate={canUpdatePrices}
                    />
                );
            default:
                return null;
        }
    };

    const isMarginCategoryPage = activeSectionConfig?.id === 'client-pricing-categories'
        && clientPricingSettings.mode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT;
    return (
        <div className={isMarginCategoryPage ? 'cpm-settings-page' : 'min-h-[90vh] overflow-x-hidden bg-gray-100 pt-16 px-2 pb-6 sm:px-4 lg:px-6'}>
            <div className="mx-auto w-full max-w-screen-2xl">
                <div className={isMarginCategoryPage ? 'min-w-0' : 'min-w-0 bg-white shadow rounded-lg overflow-hidden'}>
                    {isMarginCategoryPage ? <nav aria-label="Ruta de configuración" className="cpm-settings-breadcrumb"><button type="button" onClick={() => navigate('/settings')}>Configuración</button><span>/</span><span>Precios</span></nav> : <div className="border-b px-5 py-3">
                        <h1 className="text-xl font-bold">Configuracion</h1>
                        <p className="text-sm text-gray-600">
                            Ajusta tu entorno personal, reglas globales y configuracion por sucursal.
                        </p>
                    </div>}
                    <div className="min-w-0 p-3 sm:p-4 lg:p-5">
                        <div className="min-w-0 flex-1 space-y-6">
                            {isBusinessOwner && (
                                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                                    Modo solo lectura: puedes consultar toda la información de la empresa, pero no crear, editar ni eliminar datos.
                                </div>
                            )}
                            {!isMarginCategoryPage && renderSectionScope()}
                            {(!isMarginCategoryPage || clientPricingFormatRulesId) && renderBranchTabs()}
                            {renderSectionContent()}
                        </div>
                    </div>
                </div>
            </div>
            <ClientPricingCategoryModal
                groups={clientPricingGroups}
                pricingMode={clientPricingSettings.mode}
                marginPercentDisplay={clientPricingSettings.marginPercentDisplay}
                isOpen={clientPricingCategoryModalOpen}
                initialData={clientPricingCategories.find(
                    (category) => String(category.id) === String(clientPricingEditingId)
                ) || null}
                branches={clientPricingApplicabilityOptions.branches}
                productCategories={clientPricingApplicabilityOptions.categories}
                productFormats={clientPricingApplicabilityOptions.productFormats}
                loading={clientPricingApplicabilityLoading}
                saving={clientPricingCategorySaving}
                error={clientPricingCategoriesError}
                onClose={closeClientPricingCategoryModal}
                onSave={handleSubmitClientPricingCategory}
            />

        </div>
    );
};

export default SettingsPage;
